import {API} from 'aws-amplify';
import dayjs from '../dayjs';
import util from 'util';
import {createStop, deleteRoute, getRoutesWithOpts, loadRoute, updateRoute, updateStop,} from './routeService';
import config from '../config';
import {BusRoute, LinkedStop, Stop} from '../model/busRoute';
import {DATE_STRING, Schedule} from '../model/schedule';
import {Vehicle} from '../model/vehicle';
import {ulid} from 'ulid';
import {Driver} from '../model/driver';
import {Shift} from '../model/shift';
import log from 'loglevel';
import {find, keyBy} from 'lodash/collection';
import {abbreviate, ShiftBat, ShiftBatRowType} from '../model/shiftBat';
import {CharterRouteRun, Deadrun} from '../model/deadrun';
import {cloneDeep} from 'lodash/lang';
import Dexie from 'dexie';
import {camelCase} from 'lodash/string';
import Features from '../model/features';
import {debounce} from 'lodash/function';
import {toHrsMinsSecs} from '../libs/formatLib';
import {chunk, findIndex} from 'lodash/array';
import {Transfer} from '../model/transfer';
import {pickBy, values} from 'lodash/object';
import {noop} from 'lodash/util';
import sleep from 'sleep-promise';
import {Employee} from '../model/hrm/employee';
import {Employment} from '../model/hrm/employment';
import {Entitlement} from '../model/hrm/entitlement';
import {Leave} from '../model/hrm/leave';
import {License} from '../model/hrm/license';
import {Qualification} from '../model/hrm/qualifications';
import {Training} from '../model/hrm/training';
import {Superannuation} from '../model/hrm/superannuation';
import {Tax} from '../model/hrm/tax';
import {Banking} from '../model/hrm/banking';
import {isEqual, omit, pick, uniq, uniqBy, uniqWith} from 'lodash';
import {Address, Student} from '../model/student';
import {getEditor} from '../hooks/getEditor';
import {calculateGeoHash, reverseGeocode} from '../libs/mapLib';
import {checkOnline} from '../libs/errorLib';
import {APP_VERSION} from '../App';
import {Charter, Itinerary, ItineraryShift} from '../model/charter';
import {Customer} from '../model/customer';
import {Job} from '../model/job';
import {VehicleType} from '../model/vehicleType';
import {WeeklyScenario} from '../model/roster';
import {Report} from '../model/reports';
import {Booking} from '../model/booking';
import {createOptibusIntegration} from '../optibus/optibusIntegration';
import restrictedPoints from '../model/restrictedPoints';
import { normaliseDate } from '../libs/routes-lib';
import { generateAndStoreCharterPDFs } from '../libs/pdfGenerator';
import packageJson from '../../package.json';
import ModelLoadingPerformanceTracker from './ModelLoadingPerformanceTracker';
import { decodeOperator, decodeRoute, decodeSchedule, decodeShiftBat, decodeStop } from "../lib/proto-helper.js";
const logger = log.getLogger('ModelService');

const protoHelperMap = {
    Operator: decodeOperator,
    Route: decodeRoute,
    Stop: decodeStop,
    Schedule: decodeSchedule,
    Deadrun: decodeRoute,
    ShiftBat: decodeShiftBat,
}

// const UPDATE_INTERVAL = 60000;
const DEFAULT_MODEL_FILTER = ['Operator', 'PublicSchedule', 'Schedule', 'Stop', 'Route', 'Transfer', 'Deadrun', 'Ref',
    'DriverShift', 'VehicleShift', 'ShiftBat', 'Employee', 'Sms', 'Booking', 'Charter', 'Customer', 'Student', 'RouteComment', 'StopComment',
    'StudentConfig', 'Roster', 'Vehicle', 'VehicleType', 'Report', 'Block'];

class ModelExpiryService {
    constructor(wss) {
        this.wss = wss;
        this.wsConnection = null;
        this.wsConnectionId = null;
        this.wsConnectionInterval = null;
        this.initialised = false;
        this.updatingObjects = {};
        this.models = {};
        this.messageApi = null;
        this.loadedListeners = [];
        this.loadingState = 'idle'; // idle, loading, loaded, error
        this.loadingProgress = {};
        this.loadingErrors = {};
        this._isFallbackActive = false; // Track when fallback loading is active
        this.performanceTracker = new ModelLoadingPerformanceTracker(); // Track loading performance

        // HACK:
        this.vehicleListener = null;

        // Define explicit model dependencies to ensure proper loading order
        this.modelDependencies = {
            'Operator': [],
            'Stop': [],
            'VehicleType': [],
            'Schedule': ['PublicSchedule'],
            'PublicSchedule': [],
            'Route': ['Stop'],
            'Transfer': ['Stop', 'Schedule'],
            'Deadrun': ['Stop'],
            'Vehicle': ['VehicleType'],
            'Employee': [],
            'Customer': [],
            'ShiftBat': ['Stop', 'Route', 'Transfer', 'Deadrun'],
            'Charter': ['Stop', 'Route', 'Vehicle', 'Customer', 'VehicleType', 'Deadrun', 'ShiftBat'],
            'Job': ['ShiftBat', 'Charter', 'Employee', 'Vehicle'],
            'Roster': ['Vehicle', 'Employee', 'ShiftBat'],
            'Student': ['Stop'],
            'RouteComment': ['Route', 'Stop'],
            'StopComment': ['Stop'],
            'StudentConfig': [],
            'Sms': [],
            'Booking': [],
            'Ref': [],
            'DriverShift': [],
            'VehicleShift': [],
            'Report': [],
            'Block': []
        };

        // Fallback configuration for when WebSocket fails
        this.fallbackConfig = {
            websocketTimeout: 300000, // 5 minutes
            modelTimeout: 120000, // 2 minutes
            enabled: true,
            maxWsMessageInterval: 60000, // 60 seconds
            maxRetries: 3,
            retryDelay: 5000 // 5 seconds
        };

        // WebSocket simulation configuration for testing
        this.simulationConfig = {
            enabled: false, // Set to true to simulate WebSocket failures
            failureType: 'timeout', // 'timeout', 'connection_error', 'data_error', 'none'
            timeoutMs: 5000, // How long to wait before simulating timeout
            failureChance: 0.8 // 80% chance of failure when enabled
        };

        // Track last message time for any model - resets timeout for all models when any message arrives
        this.lastMessageTime = null;
    }

    closeWSConnection() {
        this.wsConnection && this.wsConnection.close();
    }

    /**
     * Get the loading order for models based on dependencies
     * @returns {string[]} Array of model names in dependency order
     */
    getLoadingOrder() {
        const order = [];
        const visited = new Set();
        const visiting = new Set();

        const visit = (modelName) => {
            if (visiting.has(modelName)) {
                throw new Error(`Circular dependency detected: ${modelName}`);
            }
            if (visited.has(modelName)) {
                return;
            }

            visiting.add(modelName);
            
            const dependencies = this.modelDependencies[modelName] || [];
            for (const dep of dependencies) {
                if (this.models[dep]) {
                    visit(dep);
                }
            }
            
            visiting.delete(modelName);
            visited.add(modelName);
            order.push(modelName);
        };

        // Only process models that are in the filter and exist
        const modelsToLoad = Object.keys(this.models).filter(
            name => !this.modelFilter || this.modelFilter.includes(name)
        );

        for (const modelName of modelsToLoad) {
            if (!visited.has(modelName)) {
                visit(modelName);
            }
        }

        return order;
    }

    /**
     * Check if all dependencies for a model are loaded
     * @param {string} modelName - Name of the model to check
     * @returns {boolean} True if all dependencies are loaded
     */
    areDependenciesLoaded(modelName) {
        const dependencies = this.modelDependencies[modelName] || [];
        
        // Only check dependencies that are actually in the filter
        const filteredDependencies = dependencies.filter(dep => 
            !this.modelFilter || this.modelFilter.includes(dep)
        );
        
        return filteredDependencies.every(dep => 
            this.models[dep] && this.models[dep].loaded
        );
    }

    /**
     * Update loading progress for a model
     * @param {string} modelName - Name of the model
     * @param {string} stage - Current stage (fetching, initializing, loaded)
     * @param {number} progress - Progress percentage (0-100)
     */
    updateLoadingProgress(modelName, stage, progress) {
        this.loadingProgress[modelName] = { stage, progress, timestamp: Date.now() };
        
        // Notify listeners of progress update
        this.loadedListeners.forEach(listener => {
            if (listener.onProgressUpdate) {
                listener.onProgressUpdate(modelName, stage, progress);
            }
            if (this.loadingState === 'loading' && listener.onProgressUpdate) {
                const progress = this.getOverallProgress();
                listener.onProgressUpdate('overall', 'loading', progress.percentage);
            }
        });
    }

    /**
     * Record an error for a specific model
     * @param {string} modelName - Name of the model
     * @param {Error} error - The error that occurred
     */
    recordLoadingError(modelName, error) {
        this.loadingErrors[modelName] = {
            error: error.message,
            stack: error.stack,
            timestamp: Date.now()
        };
        
        logger.error(`❌ Error loading model ${modelName}:`, error);
        
        // Notify listeners of error
        this.loadedListeners.forEach(listener => {
            if (listener.onError) {
                listener.onError(modelName, error);
            }
        });
    }

    /**
     * Get overall loading progress
     * @returns {Object} Loading progress information
     */
    getOverallProgress() {
        const modelNames = Object.keys(this.models).filter(
            name => !this.modelFilter || this.modelFilter.includes(name)
        );
        
        const totalModels = modelNames.length;
        const loadedModels = modelNames.filter(name => 
            this.models[name] && this.models[name].loaded
        ).length;
        
        return {
            total: totalModels,
            loaded: loadedModels,
            percentage: totalModels > 0 ? Math.round((loadedModels / totalModels) * 100) : 0,
            errors: Object.keys(this.loadingErrors),
            progress: this.loadingProgress
        };
    }

    async requestData() {

        try {
            // Start performance tracking
            this.performanceTracker.startLoading();
            const connectionTime = Date.now();
            const syncs = await this.db.syncs.toArray();
            let syncByName = keyBy(syncs, 'name');
            this.performanceTracker.setLoadType(syncByName?.Operator?.lastUpdated > 0 ? 'delta' : 'full');

            // Filter models based on modelFilter
            Object.keys(syncByName).forEach((name) => {
                if (this.modelFilter && !this.modelFilter.includes(name)) {
                logger.debug('⏭️ Skipping model (not in filter):', name);
                    delete syncByName[name];
                }
            });
            
            if (!this.wsChannel) {
                logger.warn('⚠️ WebSocket not available, using fallback polling');
                await this.loadModelsViaAPI(null, syncByName);
                this.performanceTracker.endLoading();
                return;
            }

            // Reset flags at the start of each request
            this._isFallbackActive = false;
            this._websocketError = false;
            this._websocketErrorMessage = null;

            this.loadingState = 'loading';
            logger.info('📡 Starting WebSocket-based data loading...');
            
            
            // Get loading order based on dependencies
            const loadingOrder = this.getLoadingOrder();
            logger.info('📋 Loading order:', loadingOrder);

            // Separate models into WebSocket and API loading groups
            const websocketModels = loadingOrder.filter(modelName => this.models[modelName]?.ws !== false);
            const apiModels = loadingOrder.filter(modelName => this.models[modelName]?.ws === false);
            
            logger.info(`📡 WebSocket models (${websocketModels.length}):`, websocketModels);
            logger.info(`🔌 API-only models (${apiModels.length}):`, apiModels);
            
            // Debug: show which models have ws property set to false
            const wsFalseModels = loadingOrder.filter(modelName => this.models[modelName]?.ws === false);
            if (wsFalseModels.length > 0) {
                logger.info(`🔍 Models with ws=false:`, wsFalseModels);
            }
            
            // Debug: show models without ws property
            const noWsPropertyModels = loadingOrder.filter(modelName => !this.models[modelName]?.hasOwnProperty('ws'));
            if (noWsPropertyModels.length > 0) {
                logger.info(`🔍 Models without ws property (defaulting to WebSocket):`, noWsPropertyModels);
            }
            
            // Check for WebSocket models that depend on API models
            const websocketModelsWithApiDeps = websocketModels.filter(modelName => {
                const dependencies = this.modelDependencies[modelName] || [];
                return dependencies.some(dep => this.models[dep]?.ws === false);
            });
            
            if (websocketModelsWithApiDeps.length > 0) {
                logger.warn(`⚠️ WebSocket models that depend on API models:`, websocketModelsWithApiDeps);
                logger.warn(`🔄 These will be loaded after API models to ensure dependencies are satisfied`);
            }

            // First, load API models that WebSocket models depend on
            const apiModelsNeededFirst = websocketModels.filter(modelName => {
                const dependencies = this.modelDependencies[modelName] || [];
                return dependencies.some(dep => this.models[dep]?.ws === false);
            });
            
            if (apiModelsNeededFirst.length > 0) {
                logger.info('🔌 Loading API models that WebSocket models depend on first...');
                const apiDepsToLoad = [...new Set(apiModelsNeededFirst.flatMap(modelName => 
                    this.modelDependencies[modelName] || []
                ).filter(dep => this.models[dep]?.ws === false))];
                
                logger.info(`📋 API dependencies to load first:`, apiDepsToLoad);
                await this.loadModelsViaAPI(apiDepsToLoad, syncByName, connectionTime, false);
                logger.info('✅ API dependencies loaded successfully!');
            }
            
            // Then try to load WebSocket models through WebSocket
            if (websocketModels.length > 0) {
                logger.info('📡 Attempting WebSocket-based loading for WebSocket models...');
                
                try {
                    // Send WebSocket subscription for WebSocket models sequentially based on dependencies
                    await this.sendWebSocketSubscription(syncByName, websocketModels);
                    
                    // Wait for WebSocket models to be loaded through WebSocket with overall timeout
                    const websocketTimeout = setTimeout(() => {
                        logger.warn('⚠️ WebSocket loading timeout - forcing fallback to API');
                        this._websocketError = true;
                        this._websocketErrorMessage = 'WebSocket loading timeout';
                    }, this.fallbackConfig.websocketTimeout); // 3 minute overall timeout
                    
                    try {
                        await this.waitForWebSocketModels(websocketModels, connectionTime);
                        clearTimeout(websocketTimeout);
                        logger.info('🎉 All WebSocket models loaded successfully via WebSocket!');
                    } catch (websocketError) {
                        clearTimeout(websocketTimeout);
                        throw websocketError;
                    }
                    
                } catch (websocketError) {
                    logger.warn('⚠️ WebSocket loading failed, falling back to API for WebSocket models:', websocketError);
                    
                    // Update loading method for models that failed via websocket
                    websocketModels.forEach(modelName => {
                        if (this.performanceTracker.trackingData[modelName] && !this.performanceTracker.trackingData[modelName].success) {
                            this.performanceTracker.updateLoadingMethod(modelName, 'fallback');
                        }
                    });
                    
                    // Reset progress for fallback
                    this.loadingProgress = {};
                    this.loadingErrors = {};
                    
                    // Record the WebSocket error
                    this.recordLoadingError('system', websocketError);
                    
                    // Fall back to API-based loading for WebSocket models
                    await this.loadModelsViaAPI(websocketModels, syncByName, connectionTime, true);
                }
            } else {
                logger.info('ℹ️ No WebSocket models to load');
            }

            // Ensure fallback flag is reset after WebSocket phase (whether successful or fallback)
            if (this._isFallbackActive) {
                logger.debug('🔄 Resetting fallback flag after WebSocket phase completion');
                this._isFallbackActive = false;
            }

            // Check if any WebSocket models are still not loaded (this shouldn't happen but let's be safe)
            const unloadedWebSocketModels = websocketModels.filter(modelName => !this.models[modelName]?.loaded);
            if (unloadedWebSocketModels.length > 0) {
                logger.warn(`⚠️ Some WebSocket models are still not loaded:`, unloadedWebSocketModels);
                logger.warn(`🔄 Falling back to API for unloaded WebSocket models...`);
                await this.loadModelsViaAPI(unloadedWebSocketModels, syncByName, connectionTime, true);
            }

            // Log summary of WebSocket phase
            const loadedWebSocketModels = websocketModels.filter(modelName => this.models[modelName]?.loaded);
            logger.info(`📊 WebSocket phase summary: ${loadedWebSocketModels.length}/${websocketModels.length} models loaded successfully`);
            if (loadedWebSocketModels.length < websocketModels.length) {
                logger.warn(`⚠️ Missing WebSocket models:`, websocketModels.filter(modelName => !this.models[modelName]?.loaded));
            }

            // Then load remaining API-only models via API (only after WebSocket models are complete)
            const remainingApiModels = apiModels.filter(modelName => {
                // Exclude API models that were already loaded as dependencies
                const apiModelsNeededFirst = websocketModels.filter(modelName => {
                    const dependencies = this.modelDependencies[modelName] || [];
                    return dependencies.some(dep => this.models[dep]?.ws === false);
                });
                const apiDepsToLoad = [...new Set(apiModelsNeededFirst.flatMap(modelName => 
                    this.modelDependencies[modelName] || []
                ).filter(dep => this.models[dep]?.ws === false))];
                return !apiDepsToLoad.includes(modelName);
            });
            
            if (remainingApiModels.length > 0) {
                logger.info('🔌 Loading remaining API-only models after WebSocket models completed...');
                await this.loadModelsViaAPI(remainingApiModels, syncByName, connectionTime, false);
                logger.info('✅ Remaining API-only models loaded successfully!');
            } else {
                logger.info('ℹ️ No remaining API-only models to load');
            }

            // All models loaded successfully
            logger.info('🎯 All models loaded successfully, finalizing...');
            this.loadingState = 'loaded';
            
            // Ensure fallback flag is reset
            this._isFallbackActive = false;
            
            // Set up cache dependencies for runtime synchronization
            logger.info('🔗 Setting up cache dependencies...');
            this.setupCacheDependencies();
            
            // Notify all listeners that loading is complete
            logger.info('📢 Notifying all listeners that loading is complete...');
            await this.notifyAllModelsLoaded();
            
            // End performance tracking and log snapshot
            this.performanceTracker.endLoading();
            logger.info('📊 Model Loading Performance:', this.performanceTracker.getFormattedSnapshot());
            
            // Save performance snapshot to S3 for Athena analysis
            await this.performanceTracker.save({
                orgId: this.orgId,
                regionId: this.regionId,
                username: this.user?.username,
                url: window?.location?.href,
                logger: logger.info.bind(logger),
                errorLogger: logger.error.bind(logger)
            });
            
            logger.info('🎉 ModelService initialization completed successfully!');

        } catch (error) {
            logger.error('💥 Critical error in requestData:', error);
            this.loadingState = 'error';
            this._isFallbackActive = false;
            this.recordLoadingError('system', error);
            
            // Try fallback method
            if (this.fallbackConfig.enabled) {
                logger.info('🔄 Attempting fallback data loading...');
                await this.loadModelsViaAPI(null, null, null, true);
            }
            
            // End performance tracking even on error
            this.performanceTracker.endLoading();
            logger.info('📊 Model Loading Performance (with errors):', this.performanceTracker.getFormattedSnapshot());
            
            // Save performance snapshot to S3 for Athena analysis (even on error)
            await this.performanceTracker.save({
                orgId: this.orgId,
                regionId: this.regionId,
                username: this.user?.username,
                url: window?.location?.href,
                logger: logger.info.bind(logger),
                errorLogger: logger.error.bind(logger)
            });
        }
    }

    /**
     * Wait for models to be loaded through WebSocket
     */
    async waitForWebSocketModels(loadingOrder, connectionTime) {
        
        // Check if WebSocket simulation is enabled
        if (this.simulationConfig.enabled) {
            logger.info('🧪 WebSocket simulation enabled - simulating failure...');
            
            // Simulate different types of failures
            if (this.simulationConfig.failureType === 'timeout') {
                logger.info(`⏰ Simulating WebSocket timeout after ${this.simulationConfig.timeoutMs}ms...`);
                await sleep(this.simulationConfig.timeoutMs);
                throw new Error('🧪 Simulated WebSocket timeout failure');
            } else if (this.simulationConfig.failureType === 'connection_error') {
                logger.info('🔌 Simulating WebSocket connection error...');
                throw new Error('🧪 Simulated WebSocket connection error');
            } else if (this.simulationConfig.failureType === 'data_error') {
                logger.info('📊 Simulating WebSocket data error after partial load...');
                // Simulate partial loading then failure
                for (let i = 0; i < Math.min(3, loadingOrder.length); i++) {
                    const modelName = loadingOrder[i];
                    if (this.models[modelName]) {
                        logger.debug(`⏳ Simulating partial load of ${modelName}...`);
                        this.updateLoadingProgress(modelName, 'waiting_for_websocket', 25);
                        await sleep(500);
                    }
                }
                throw new Error('🧪 Simulated WebSocket data error after partial load');
            }
        }
        
        // Wait for each model to be loaded
        logger.info(`📋 Processing ${loadingOrder.length} models in WebSocket phase...`);
        
        // Initialize last message time tracking - resets when ANY model receives a message
        this.lastMessageTime = Date.now();
        
        // Track which models we've processed to avoid infinite loops
        const processedModels = new Set();
        let attempts = 0;
        const maxAttempts = loadingOrder.length * 2; // Allow each model to be attempted twice
        
        while (processedModels.size < loadingOrder.length && attempts < maxAttempts) {
            attempts++;
            logger.debug(`🔄 WebSocket loading attempt ${attempts}/${maxAttempts}, processed: ${processedModels.size}/${loadingOrder.length}`);
            
            for (const modelName of loadingOrder) {
                // Skip if already processed
                if (processedModels.has(modelName)) {
                    continue;
                }
                
                const startTime = Date.now();
                
                if (!this.models[modelName]) {
                    logger.warn(`⚠️ Model ${modelName} not found, skipping`);
                    processedModels.add(modelName);
                    continue;
                }

                // Skip models that don't support WebSocket
                if (this.models[modelName].ws === false) {
                    logger.debug(`⏭️ Skipping ${modelName} - API-only model`);
                    processedModels.add(modelName);
                    continue;
                }

                // Check if model dependencies are loaded
                const dependencies = this.modelDependencies[modelName] || [];
                const unloadedDependencies = dependencies.filter(dep => !this.models[dep]?.loaded);
                if (unloadedDependencies.length > 0) {
                    logger.warn(`⚠️ Model ${modelName} has unloaded dependencies:`, unloadedDependencies);
                    
                    // Check if any dependencies are API-only models that should be loaded first
                    const apiDependencies = unloadedDependencies.filter(dep => this.models[dep]?.ws === false);
                    if (apiDependencies.length > 0) {
                        logger.warn(`⚠️ Model ${modelName} depends on API-only models:`, apiDependencies);
                        logger.warn(`🔄 Skipping ${modelName} until API dependencies are loaded`);
                        logger.debug(`🔍 ${modelName} will be retried in next iteration after dependencies are loaded`);
                        continue; // Skip this model for now
                    }
                    
                    // For WebSocket dependencies, we can proceed as they should load in parallel
                    logger.warn(`🔄 Proceeding with ${modelName} - WebSocket dependencies should load in parallel`);
                }

                logger.info(`⏳ Processing model ${modelName} (${loadingOrder.indexOf(modelName) + 1}/${loadingOrder.length})...`);
                this.updateLoadingProgress(modelName, 'waiting_for_websocket', 25);
                
                // Start tracking this model's load
                this.performanceTracker.startModelLoad(modelName, 'websocket');
                
                // Wait for the model to be loaded
                let waitStartTime = Date.now();
                let lastProgressTime = Date.now();
                
                while (!this.models[modelName].loaded) {
                    const now = Date.now();

                    // Check for overall timeout
                    if (now - startTime > this.fallbackConfig.modelTimeout) {
                        const error = new Error(`Timeout waiting for ${modelName} to load via WebSocket`);
                        this.performanceTracker.markModelError(modelName, error);
                        throw error;
                    }
                    
                    // Get the last message time for any model (resets when any model receives a message)
                    const lastMessageTime = this.lastMessageTime || waitStartTime;
                    const timeSinceLastMessage = now - lastMessageTime;
                    
                    // Check for timeout - resets every time ANY model receives a message
                    const maxWsMessageInterval = this.fallbackConfig.maxWsMessageInterval;
                    if (timeSinceLastMessage > maxWsMessageInterval) {
                        logger.warn(`⚠️ Model ${modelName} taking too long to load via WebSocket (${Math.round(timeSinceLastMessage / 1000)}s since last message for any model), checking if data exists...`);
                        
                        // Check if the model actually has data but isn't marked as loaded
                        if (this.models[modelName].data && Object.keys(this.models[modelName].data).length > 0) {
                            logger.info(`✅ Model ${modelName} has data but wasn't marked as loaded, fixing...`);
                            this.models[modelName].setLoaded(connectionTime);
                            this.performanceTracker.markModelLoaded(modelName);
                            break;
                        }
                        
                        // If no data, throw error to trigger fallback
                        const error = new Error(`Model ${modelName} stuck waiting for WebSocket data after ${Math.round(timeSinceLastMessage / 1000)} seconds without messages for any model`);
                        this.performanceTracker.markModelError(modelName, error);
                        throw error;
                    }
                    
                    // Check for WebSocket errors that should trigger fallback
                    if (this._websocketError) {
                        const error = new Error(`WebSocket error detected: ${this._websocketErrorMessage}`);
                        this.performanceTracker.markModelError(modelName, error);
                        throw error;
                    }
                    
                    // Check if WebSocket connection is still healthy
                    if (this.wsConnection && this.wsConnection.readyState !== WebSocket.OPEN) {
                        const error = new Error(`WebSocket connection lost (state: ${this.wsConnection.readyState})`);
                        this.performanceTracker.markModelError(modelName, error);
                        throw error;
                    }
                    
                    // Log progress every 5 seconds to help debug hanging
                    if (now - lastProgressTime > 1000) {
                        const timeSinceLastMessage = now - (this.lastMessageTime || waitStartTime);
                        logger.debug(`⏳ Still waiting for ${modelName} to load via WebSocket... (${Math.round(timeSinceLastMessage / 1000)}s since last message for any model, ${Math.round((now - waitStartTime) / 1000)}s total)`);
                        lastProgressTime = now;
                    }
                    
                    await sleep(100);
                }
                
                // Initialize the model
                logger.debug(`⚙️ Initializing ${modelName}...`);
                this.updateLoadingProgress(modelName, 'initializing', 75);
                
                await this.models[modelName].init();
                this.models[modelName].setLoaded(connectionTime);
                this.models[modelName].updateSyncsNow();
                
                // Mark model as successfully loaded in performance tracker
                this.performanceTracker.markModelLoaded(modelName);
                
                // Resolve load promise if exists
                if (this.models[modelName]._loadResolver) {
                    this.models[modelName]._loadResolver();
                }
                
                this.updateLoadingProgress(modelName, 'loaded', 100);
                
                // Update overall progress for WebSocket loading
                const currentIndex = loadingOrder.indexOf(modelName);
                const overallProgress = Math.round(((currentIndex + 1) / loadingOrder.length) * 100);
                this.updateLoadingProgress('overall', 'websocket_loading', overallProgress);
                
                logger.info(`✅ ${modelName} loaded and initialized via WebSocket (${currentIndex + 1}/${loadingOrder.length}) - Overall Progress: ${overallProgress}%`);
                
                // Mark as processed
                processedModels.add(modelName);
            }
            
            // If we've processed all models, break out of the loop
            if (processedModels.size >= loadingOrder.length) {
                break;
            }
            
            // If we're still waiting for some models, wait a bit before the next attempt
            if (attempts < maxAttempts) {
                const remainingModels = loadingOrder.filter(modelName => !processedModels.has(modelName));
                logger.debug(`⏳ Waiting for remaining models to load... (${remainingModels.length} remaining):`, remainingModels);
                await sleep(1000);
            }
        }
        
        // Final check - ensure all models are loaded
        const finalLoadedCount = loadingOrder.filter(modelName => this.models[modelName]?.loaded).length;
        logger.info(`🎯 WebSocket phase complete: ${finalLoadedCount}/${loadingOrder.length} models loaded`);
        
        if (finalLoadedCount < loadingOrder.length) {
            const unloadedModels = loadingOrder.filter(modelName => !this.models[modelName]?.loaded);
            logger.warn(`⚠️ Some models were not loaded via WebSocket:`, unloadedModels);
        }
        
        // Check if we hit the max attempts limit
        if (attempts >= maxAttempts) {
            logger.warn(`⚠️ Hit maximum attempts limit (${maxAttempts}) for WebSocket loading`);
        }
    }

    /**
     * Load models via API as fallback when WebSocket fails
     */
    async loadModelsViaAPI(loadingOrder = null, syncByName = null, connectionTime = null, isFallback = false) {
        if (isFallback) {
            logger.info('🔄 Loading models via API fallback...');
            this._isFallbackActive = true;
        } else {
            logger.info('🔌 Loading models via API...');
        }

        try {
            // If parameters not provided, look them up internally
            if (!loadingOrder) {
                loadingOrder = this.getLoadingOrder();
            }
            if (!syncByName) {
                const syncs = await this.db.syncs.toArray();
                syncByName = keyBy(syncs, 'name');

                // Filter models based on modelFilter
                Object.keys(syncByName).forEach((name) => {
                    if (this.modelFilter && !this.modelFilter.includes(name)) {
                        logger.debug('⏭️ Skipping model (not in filter):', name);
                        delete syncByName[name];
                    }
                });
            }
            if (!connectionTime) {
                connectionTime = Date.now();
            }

            const totalModels = loadingOrder.length;
            let loadedModels = 0;

            // Reset progress state for fallback
            this.loadingProgress = {};

            for (const modelName of loadingOrder) {
                if (!this.models[modelName]) {
                    logger.warn(`⚠️ Model ${modelName} not found, skipping`);
                    continue;
                }

                try {
                    // Start tracking this model's load (or update method if already tracking)
                    // Do this BEFORE checking if already loaded, so we track all models
                    const loadingMethod = isFallback ? 'fallback' : 'api';
                    if (!this.performanceTracker.trackingData[modelName]) {
                        this.performanceTracker.startModelLoad(modelName, loadingMethod);
                        logger.debug(`🔄 Loading model via ${loadingMethod.toUpperCase()}: ${modelName} (tracking started)`);
                    } else {
                        this.performanceTracker.updateLoadingMethod(modelName, loadingMethod);
                        logger.debug(`🔄 Loading model via ${loadingMethod.toUpperCase()}: ${modelName} (method updated)`);
                    }

                    // Check if model is already loaded - if so, mark it as loaded in tracker and skip
                    if (this.models[modelName].loaded) {
                        logger.debug(`⏭️ Model ${modelName} already loaded, marking in tracker and skipping`);
                        this.performanceTracker.markModelLoaded(modelName);
                        if (this.models[modelName]._loadResolver) {
                            this.models[modelName]._loadResolver();
                        }
                        loadedModels++;
                        continue;
                    }

                    // Check if dependencies are loaded
                    if (!this.areDependenciesLoaded(modelName)) {
                        logger.debug(`⏳ Waiting for dependencies of ${modelName} to load...`);
                        await this.waitForDependencies(modelName);
                    }

                    // Set special fetch options for Route model
                    if (modelName === 'Route') {
                        this.models[modelName]
                            .setFetchOption('updatedAfter', syncByName.Route?.lastUpdated || 0)
                                    .setFetchOption('includePath', true)
                                    .setFetchOption('includeStopTimes', true);
                            }

                    // Fetch data with retry logic (only when WebSocket fails)
                    logger.debug(`📥 Fetching data for ${modelName}...`);
                    await this.fetchModelWithRetry(modelName);

                    // Initialize the model
                    logger.debug(`⚙️ Initializing model: ${modelName}`);

                    await this.models[modelName].init();

                    // Mark as loaded
                    this.models[modelName].setLoaded(connectionTime);
                    this.models[modelName].updateSyncsNow();

                    // Mark model as successfully loaded in performance tracker
                    this.performanceTracker.markModelLoaded(modelName);

                    // Resolve load promise if exists
                    if (this.models[modelName]._loadResolver) {
                        this.models[modelName]._loadResolver();
                    }

                    loadedModels++;

                    // Send progress update
                    const progress = Math.round((loadedModels / totalModels) * 100);
                    const stage = isFallback ? 'fallback_progress' : 'api_loading';
                    this.loadedListeners.forEach(listener => {
                        if (listener.onProgressUpdate) {
                            listener.onProgressUpdate('overall', stage, progress);
                        }
                    });

                    logger.info(`✅ Model ${modelName} loaded successfully via API (${loadedModels}/${totalModels}) - ${isFallback ? 'Fallback' : 'API'} Progress: ${progress}%`);
                    logger.debug(`Waiting for ${loadingOrder.filter(modelName => !this.models[modelName]?.loaded).join(', ')} to load...`);

                    // Add a small delay to make progress visible
                    await sleep(100);

                } catch (error) {
                    this.recordLoadingError(modelName, error);
                    this.performanceTracker.markModelError(modelName, error);
                    logger.error(`❌ Failed to load model ${modelName}:`, error);

                    // Continue with other models instead of failing completely
                    continue;
                }
            }

            const stage = isFallback ? 'API fallback' : 'API';
            logger.info(`🎉 All models loaded successfully via ${stage}`);

            // Ensure all models are actually marked as loaded
            for (const modelName of loadingOrder) {
                if (this.models[modelName] && !this.models[modelName].loaded) {
                    logger.warn(`⚠️ Model ${modelName} was not properly marked as loaded, fixing...`);
                    this.models[modelName].setLoaded(connectionTime);
                }
                if (this.models[modelName]?._loadResolver) {
                    this.models[modelName]._loadResolver();
                }
            }

            logger.info(`🎯 ${stage} completed: ${loadedModels}/${totalModels} models loaded`);

            // Only finalize loading state when this is a top-level call
            // (not a sub-step of requestData, which manages its own loadingState)
            if (this.loadingState !== 'loading') {
                this.loadingState = 'loaded';
                this.setupCacheDependencies();
                await this.notifyAllModelsLoaded();
            }
        } finally {
            if (isFallback) {
                this._isFallbackActive = false;
                logger.info('🔄 Fallback loading completed - WebSocket reconnection now allowed');
            }
        }
    }
    
    /**
     * Check if WebSocket reconnection is currently allowed
     */
    isWebSocketReconnectionAllowed() {
        // Don't reconnect if fallback is active
        if (this._isFallbackActive) {
            return false;
        }
        
        // Don't reconnect if we already have a working connection
        if (this.wsConnection && (this.wsConnection.readyState === WebSocket.OPEN || this.wsConnection.readyState === WebSocket.CONNECTING)) {
            return false;
        }
        
        // Allow reconnection if app is loaded (normal operation)
        // or if we're not actively loading (idle state)
        return this.loadingState === 'loaded' || this.loadingState === 'idle';
    }
    
    /**
     * Wait for all dependencies of a model to be loaded
     * @param {string} modelName - Name of the model to wait for
     */
    async waitForDependencies(modelName) {
        const dependencies = this.modelDependencies[modelName] || [];
        const maxWaitTime = 60000; // 1 minute timeout
        const startTime = Date.now();

        while (!this.areDependenciesLoaded(modelName)) {
            if (Date.now() - startTime > maxWaitTime) {
                throw new Error(`Timeout waiting for dependencies of ${modelName}`);
            }
            await sleep(100); // Wait 100ms before checking again
        }
    }

    /**
     * Fetch a model with retry logic and exponential backoff
     * @param {string} modelName - Name of the model to fetch
     */
    async fetchModelWithRetry(modelName) {
        let attempts = 0;
        let sleepMs = 100;
        const maxAttempts = 5;

        while (attempts < maxAttempts) {
            try {
                await this.models[modelName].fetchAll();
                return; // Success, exit retry loop
            } catch (error) {
                attempts++;
                                logger.warn(
                    `⚠️ Attempt ${attempts}/${maxAttempts} failed for ${modelName}. Error: ${error.message}`
                );

                if (attempts >= maxAttempts) {
                    throw new Error(`Failed to fetch ${modelName} after ${maxAttempts} attempts: ${error.message}`);
                }

                // Exponential backoff
                                await sleep(sleepMs);
                sleepMs = Math.min(sleepMs * 2, 5000); // Cap at 5 seconds
            }
        }
    }

    /**
     * Send WebSocket subscription message
     * @param {Object} syncByName - Sync data by model name
     */
    async sendWebSocketSubscription(syncByName, loadingOrder) {
        if (this.wsChannel && this.apiKey) {
            logger.info('🔌 Sending WebSocket subscriptions sequentially based on dependencies...');
            
            let orderdSync = {}
            // Send subscription messages sequentially in dependency order
            for (const modelName of loadingOrder) {
                // Skip models that don't support WebSocket
                if (this.models[modelName]?.ws === false) {
                    logger.debug(`⏭️ Skipping WebSocket subscription for ${modelName} (API-only model)`);
                    continue;
                }
                orderdSync[modelName] = syncByName[modelName] || { lastUpdated: 0, name: modelName };
            }

            const subscriptionMessage = {
                ...this.operator,
                syncByName: orderdSync,
                eventType: 'subscribe',
                username: this.user?.username
            };
            
            try {
                this.wsChannel.send(JSON.stringify(subscriptionMessage));
                logger.debug(`🔌 Subscribed: ${JSON.stringify(subscriptionMessage)}`);
                
                // Small delay to ensure messages are processed in order
                await sleep(50);
            } catch (error) {
                logger.error(`❌ Failed to subscribe:`, error);
                // Continue with other models instead of failing completely
            }
        }
    }

    /**
     * Notify all models that loading is complete
     */
    async notifyAllModelsLoaded() {
             const modelNames = Object.keys(this.models).filter(
            (name) => !this.modelFilter || this.modelFilter.includes(name)
        );

        // Check if all models are loaded
        if (modelNames.every((name) => this.models[name]?.loaded)) {
            logger.info('📢 Notifying all models that loading is complete...');
            
            try {
                await Promise.all(
                            modelNames.map(async (name) => {
                        if (this.models[name]?.notifyLoaded) {
                                return await this.models[name].notifyLoaded();
                        }
                    })
                );
                
                logger.info('✅ All models notified successfully');
                this.loaded = true;
                
                // Notify all listeners
                this.loadedListeners.forEach((listener) => {
                    if (listener.loaded) {
                        listener.loaded();
                    }
                });
            
                // Don't set up cache dependencies during initial load
                // They will be set up when the first real-time update arrives
                logger.debug('⏭️ Cache dependencies deferred until first real-time update');
                
            } catch (error) {
                logger.error('❌ Error notifying models:', error);
                throw error;
            }
        } else {
            logger.warn('⚠️ Not all models are loaded, skipping notification');
        }
    }



    addLoadedListener(listener) {
        this.loadedListeners.push(listener);
        
        // If already loaded, notify immediately
        if (this.loaded) {
            listener.loaded();
        }
        
        // If currently loading, provide current progress
        if (this.loadingState === 'loading' && listener.onProgressUpdate) {
            const progress = this.getOverallProgress();
            listener.onProgressUpdate('overall', 'loading', progress.percentage);
        }
        
        // If there are errors, notify about them
        if (Object.keys(this.loadingErrors).length > 0 && listener.onError) {
            Object.entries(this.loadingErrors).forEach(([modelName, errorInfo]) => {
                listener.onError(modelName, new Error(errorInfo.error));
            });
        }
    }

    removeLoadedListener(listener) {
        if (typeof listener === 'string') {
            // Remove by id: find listener with .id === listener
            const idx = this.loadedListeners.findIndex((l) => l && l.id === listener);
            if (idx !== -1) {
                this.loadedListeners.splice(idx, 1);
            }
            return;
        }
        this.loadedListeners.splice(
            this.loadedListeners.findIndex((l) => l === listener),
            1
        );
    }

    /**
     * Get current loading status and progress
     * @returns {Object} Current loading information
     */
    getLoadingStatus() {
        return {
            state: this.loadingState,
            progress: this.getOverallProgress(),
            errors: this.loadingErrors,
            timestamp: Date.now()
        };
    }

    /**
     * Check if the service is in a healthy state
     * @returns {boolean} True if healthy
     */
    isHealthy() {
        return this.loadingState === 'loaded' && Object.keys(this.loadingErrors).length === 0;
    }

    /**
     * Reset loading state (useful for retry scenarios)
     */
    resetLoadingState() {
        this.loadingState = 'idle';
        this.loadingProgress = {};
        this.loadingErrors = {};
        this.loaded = false;
        // Clear message time tracking when resetting loading state
        this.lastMessageTime = null;
    }

    /**
     * Retry loading a specific model
     * @param {string} modelName - Name of the model to retry
     */
    async retryModel(modelName) {
        if (!this.models[modelName]) {
            throw new Error(`Model ${modelName} not found`);
        }

        try {
            logger.info(`🔄 Retrying model: ${modelName}`);
            
            // Clear previous error
            delete this.loadingErrors[modelName];
            
            // Reset model state
            this.models[modelName].loaded = false;
            this.models[modelName].fetchedAll = false;
            
            // Reload the model
            await this.fetchModelWithRetry(modelName);
            await this.models[modelName].init();
            
            this.models[modelName].setLoaded(Date.now());
            this.updateLoadingProgress(modelName, 'loaded', 100);
            
            logger.info(`✅ Model ${modelName} retried successfully`);
            
        } catch (error) {
            this.recordLoadingError(modelName, error);
            throw error;
        }
    }

    /**
     * Enable WebSocket failure simulation for testing
     * @param {Object} config - Simulation configuration
     */
    enableWebSocketSimulation(config = {}) {
        this.simulationConfig = {
            ...this.simulationConfig,
            ...config,
            enabled: true
        };
        logger.info('🧪 WebSocket failure simulation enabled:', this.simulationConfig);
    }

    /**
     * Disable WebSocket failure simulation
     */
    disableWebSocketSimulation() {
        this.simulationConfig.enabled = false;
        logger.info('🧪 WebSocket failure simulation disabled');
    }

    /**
     * Get current simulation configuration
     */
    getSimulationConfig() {
        return { ...this.simulationConfig };
    }

    /**
     * Get WebSocket connection health and statistics
     */
    getWebSocketHealth() {
        if (!this.wsConnection) {
            return { status: 'disconnected', readyState: 'N/A' };
        }
        
        const readyStateMap = {
            0: 'CONNECTING',
            1: 'OPEN',
            2: 'CLOSING',
            3: 'CLOSED'
        };
        
        return {
            status: readyStateMap[this.wsConnection.readyState] || 'UNKNOWN',
            readyState: this.wsConnection.readyState,
            url: this.wsConnection.url,
            bufferedAmount: this.wsConnection.bufferedAmount,
            messageCount: this._messageCount || 0,
            lastUpdate: this._lastWebSocketUpdate || 'Never',
            connectionTime: this._connectionStartTime || 'Unknown',
            throttledCount: this._throttledCount || 0,
            lastThrottle: this._lastThrottle || 'Never'
        };
    }

    /**
     * Get connection quality information including bitrate and status
     */
    getConnectionQuality() {
        if (!this.wsConnection || this.wsConnection.readyState !== WebSocket.OPEN) {
            return {
                status: 'disconnected',
                bitrate: 0,
                bitrateMbps: '0.00',
                messageRate: 0,
                quality: 'disconnected',
                isIdle: false
            };
        }

        // Calculate current bitrate if we have recent data
        let currentBitrate = 0;
        let currentMessageRate = 0;
        let isIdle = false;
        
        if (this._lastWebSocketUpdate && this._lastByteCount !== undefined) {
            const now = Date.now();
            const timeSinceLastUpdate = (now - this._lastWebSocketUpdate) / 1000;
            
            if (timeSinceLastUpdate > 0) {
                const bytesSinceLast = (this._byteCount || 0) - this._lastByteCount;
                const messagesSinceLast = (this._messageCount || 0) - (this._lastMessageCount || 0);
                
                currentBitrate = (bytesSinceLast * 8) / timeSinceLastUpdate; // bits per second
                currentMessageRate = messagesSinceLast / timeSinceLastUpdate;
                
                // Consider connection idle if no messages in the last 5 seconds
                isIdle = timeSinceLastUpdate > 5 && messagesSinceLast === 0;
            }
        }

        // Determine connection quality based on bitrate and idle state
        let quality = 'unknown';
        
        if (isIdle) {
            // If connection is idle (no recent messages), show as "stable" instead of poor
            quality = 'stable';
        } else if (currentBitrate > 0) {
            if (currentBitrate >= 1000000) { // 1 Mbps
                quality = 'excellent';
            } else if (currentBitrate >= 500000) { // 500 Kbps
                quality = 'good';
            } else if (currentBitrate >= 100000) { // 100 Kbps
                quality = 'fair';
            } else {
                quality = 'poor';
            }
        } else if (this.loadingState === 'loaded') {
            // If app is fully loaded and no recent activity, show as stable
            quality = 'stable';
        }

        return {
            status: 'connected',
            bitrate: currentBitrate,
            bitrateMbps: (currentBitrate / 1000000).toFixed(2),
            messageRate: currentMessageRate.toFixed(2),
            quality: quality,
            totalMessages: this._messageCount || 0,
            totalBytes: this._byteCount || 0,
            isIdle: isIdle
        };
    }

    /**
     * Get comprehensive debugging information for WebSocket issues
     */
    getDebugInfo() {
        const health = this.getWebSocketHealth();
        const loadingState = this.getLoadingStatus();
        
        return {
            websocket: health,
            loading: loadingState,
            models: {
                total: Object.keys(this.models).length,
                loaded: Object.keys(this.models).filter(name => this.models[name]?.loaded).length,
                loading: Object.keys(this.models).filter(name => this.models[name]?.loadingData?.length > 0).length
            },
            throttling: {
                messageCount: this._messageCount || 0,
                throttledCount: this._throttledCount || 0,
                lastThrottle: this._lastThrottle || 'Never',
                throttleRate: this._messageCount ? Math.round((this._throttledCount / this._messageCount) * 100) : 0
            },
            memory: {
                bufferedAmount: this.wsConnection?.bufferedAmount || 0,
                updatingObjects: Object.keys(this.updatingObjects || {}).length
            }
        };
    }

    /**
     * Reset WebSocket statistics (useful for debugging)
     */
    resetWebSocketStats() {
        this._messageCount = 0;
        this._throttledCount = 0;
        this._lastWebSocketUpdate = null;
        this._lastThrottle = null;
        logger.debug('🔄 WebSocket statistics reset');
    }

    /**
     * Set up cache dependencies for all models after loading is complete
     * This ensures models stay synchronized with their dependencies
     */
    setupCacheDependencies() {
        logger.info('🔗 Setting up cache dependencies...');
        
        // Only set up cache dependencies for models in the filter
        const modelsToProcess = Object.keys(this.models).filter(
            name => !this.modelFilter || this.modelFilter.includes(name)
        );
        
        // Track which dependencies are already set up to prevent duplicates
        const setupDependencies = new Set();
        
        modelsToProcess.forEach(modelName => {
            const dependencies = this.modelDependencies[modelName] || [];
            
            dependencies.forEach(depName => {
                // Only set up dependencies if the dependent model is also in the filter
                if (this.models[depName] && (!this.modelFilter || this.modelFilter.includes(depName))) {
                    const dependencyKey = `${modelName}:${depName}`;
                    
                    // Prevent duplicate dependency setup
                    if (setupDependencies.has(dependencyKey)) {
                        logger.debug(`⏭️ Dependency ${dependencyKey} already set up, skipping...`);
                        return;
                    }
                    
                    logger.debug(`🔗 ${modelName} will refresh when ${depName} updates`);
                    setupDependencies.add(dependencyKey);
                    
                    // Add a cooldown to prevent excessive updates
                    let lastUpdateTime = 0;
                    const cooldownMs = 5000; // 5 second cooldown to prevent loops
                    
                    this.models[depName].addListener({
                        notifyUpdate: () => {
                            const now = Date.now();
                            if (now - lastUpdateTime < cooldownMs) {
                                logger.debug(`⏭️ ${modelName} update skipped due to cooldown (${now - lastUpdateTime}ms since last update)`);
                                return;
                            }
                            
                            lastUpdateTime = now;
                            if (this.models[modelName]?.refreshLocalCacheFromDexieDebounced) {
                                logger.debug(`🔄 Triggering ${modelName} refresh due to ${depName} update`);
                                logger.debug(`🔍 DEBUG: ${depName} update triggered refresh of ${modelName}`);
                                this.models[modelName].refreshLocalCacheFromDexieDebounced();
                            }
                        }
                    });
                }
            });
        });
        
        logger.info('✅ Cache dependencies configured for filtered models');
    }

    initConnection() {
        if (!this.operator && !this.apiKey) {
            logger.warn('No operator or apiKey.');
            return;
        }
        // if (!this.user?.username) {
            // logger.warn('No user.');
            // return;
        // }
        if (!this.isWebSocketReconnectionAllowed()) {
            logger.debug(`🔍 Debug: _isFallbackActive=${this._isFallbackActive}, loadingState=${this.loadingState}, wsConnection exists=${!!this.wsConnection}`);
            logger.info('⏸️ WebSocket reconnection deferred - fallback loading in progress');
            return;
        }

        if (this.wsConnection?.readyState === WebSocket.CONNECTING) {
            return;
        }
        if (this.wsConnection?.readyState === WebSocket.OPEN) {
            logger.debug('WebSocket already open.');
            checkOnline().then((online) => {
                if (!online) {
                    this.closeWSConnection();
                }
            });
            return;
        }
        logger.debug(`Attempting to connect to websocket: ${this.wss}`);
        logger.info(`🔌 Connecting to WebSocket: ${this.wss}?_u=${this.user?.username?.slice(0, 8)}...`);

        try {
            this.wsConnection = new WebSocket(`${this.wss}?_u=${this.user?.username}&_v=${this.version || 1}&_o=${this.operator?.orgId || this.operator?.companyId || 'UNKNOWN'}&_r=${this.operator?.regionId || this.operator?.operatorId || this.apiKey || 'UNKNOWN'}&_k=${this.apiKey}`);
            this.connectionTime = Date.now();
        } catch (error) {
            logger.error(`Failed to create WebSocket connection: ${error.message}`);
            logger.error('WebSocket creation failed:', error);
            return;
        }
        this.wsConnection.onopen = (event) => {
            logger.info('✅ WebSocket connection established');
            logger.info('WebSocket is now open.');
            this.wsChannel = event.currentTarget;
            this._connectionStartTime = new Date().toISOString();
            logger.info(`🕐 WebSocket connection started at: ${this._connectionStartTime}`);
            
            // Always request data and subscribe for real-time updates when connection opens
            if (!this.noRequest) {
                logger.info('📡 Requesting initial data and setting up real-time subscriptions...');
                this.requestData();
            }
        };

        this.wsConnection.onclose = (event) => {
            logger.info(`WebSocket is now closed. Code: ${event.code}, Reason: ${event.reason}`);
            logger.info(`WebSocket closed - Code: ${event.code}, Reason: ${event.reason || 'No reason given'}`);
            
            // Don't attempt reconnection if fallback is active
            if (!this.isWebSocketReconnectionAllowed()) {
                logger.warn('⏸️ WebSocket reconnection deferred - fallback loading in progress');
                return;
            }
            
            // Attempt automatic reconnection after a delay (unless it was a deliberate close)
            if (event.code === 1000) {
                logger.info('✅ WebSocket closed normally - no reconnection needed');
                clearInterval(this.wsConnectionInterval);
            }
        };

        this.wsConnection.onerror = (event) => {
            logger.error(
                `WebSocket error observed: ${util.inspect(event)}`,
                event
            );
            
            // Enhanced error details
            const errorDetails = {
                type: event.type,
                target: event.target?.url,
                readyState: event.target?.readyState,
                timestamp: new Date().toISOString(),
                messageCount: this._messageCount || 0,
                bufferedAmount: event.target?.bufferedAmount || 0,
                lastUpdate: this._lastWebSocketUpdate || 'Never',
                connectionTime: this._connectionStartTime || 'Unknown'
            };
            
            logger.error('🔍 WebSocket error details:', errorDetails);
            
            // Track throttling statistics
            if (!this._throttledCount) this._throttledCount = 0;
            this._throttledCount++;
            this._lastThrottle = new Date().toISOString();
            
            // Log to your error tracking system if available
            if (window.gtag) {
                window.gtag('event', 'websocket_error', {
                    error_type: 'websocket_error',
                    ready_state: errorDetails.readyState,
                    message_count: errorDetails.messageCount,
                    buffered_amount: errorDetails.bufferedAmount
                });
            }
            
            // Set WebSocket error flag to trigger fallback in waitForWebSocketModels
            this._websocketError = true;
            this._websocketErrorMessage = 'WebSocket connection error';
            
            // If we're currently waiting for WebSocket models, trigger fallback
            if (this.loadingState === 'loading' && !this._isFallbackActive) {
                logger.warn('⚠️ WebSocket error detected during loading - will trigger fallback on next check');
            }
        };

        this.wsConnection.onmessage = async (event) => {
            // Enhanced debugging for large datasets, now also showing bitrate
            if (!this._messageCount) this._messageCount = 0;
            if (!this._byteCount) this._byteCount = 0;
            this._messageCount++;
            this._byteCount += event.data ? event.data.length : 0;

            // Calculate and log message rate per second and bitrate
            if (!this._lastWebSocketUpdate || Date.now() - this._lastWebSocketUpdate > 1000) {
                const now = Date.now();
                if (!this._lastMessageCount) this._lastMessageCount = 0;
                if (!this._lastByteCount) this._lastByteCount = 0;
                
                // Calculate rates before updating the timestamp
                const messagesSinceLast = this._messageCount - this._lastMessageCount;
                const bytesSinceLast = this._byteCount - this._lastByteCount;
                const secondsElapsed = this._lastWebSocketUpdate ? (now - this._lastWebSocketUpdate) / 1000 : 0;
                
                const ratePerSecond = secondsElapsed > 0 ? (messagesSinceLast / secondsElapsed).toFixed(2) : 'N/A';
                const bitrate = secondsElapsed > 0 ? (bytesSinceLast * 8 / secondsElapsed) : 0; // bits per second
                const bitrateMbps = bitrate > 0 ? (bitrate / 1000000).toFixed(2) : 'N/A';

                logger.info(`📊 Total messages received: ${this._messageCount}`);
                logger.info(`⏱️ Message rate: ${ratePerSecond} messages per second`);
                logger.info(`📶 Bitrate: ${bitrateMbps} Mbps`);

                // Update timestamps after calculation
                this._lastWebSocketUpdate = now;
                this._lastMessageCount = this._messageCount;
                this._lastByteCount = this._byteCount;

                // Notify listeners of connection quality update
                const connectionQuality = this.getConnectionQuality();
                this.loadedListeners.forEach(listener => {
                    if (listener.onConnectionQualityUpdate) {
                        listener.onConnectionQualityUpdate(connectionQuality);
                    }
                });
            }

            // console.log(event)
            const data = JSON.parse(event.data);
            if (!data) {
                return;
            }
            const {action} = data;

            if (action === 'navigate' && this.vehicleListener) {
                this.vehicleListener(data);
                return;
            }

            if ((action === 'error' || data.message) && this.messageApi) {
                this.setFaultState?.({
                    type: 'serverError',
                    error: data.message || 'Unknown error',
                    data,
                });
                return;
            }

            let model = this.models[data.model];
            if (!model) {
                // if (action === 'loaded') {
                //     Object.keys(this.models).forEach(name => this.models[name].init())
                // }
                return;
            }
            
            // Skip WebSocket messages for API-only models
            if (model.ws === false) {
                logger.debug(`⏭️ Skipping WebSocket message for ${data.model} - API-only model`);
                return;
            }

            // Reset timeout for all models when any message arrives
            // This allows the timeout to be extended as long as messages are still coming for any model
            if (this.lastMessageTime !== null) {
                this.lastMessageTime = Date.now();
                logger.debug(`🔄 Reset timeout for all models - message received for ${data.model}`);
            }
            if (action === 'update') {
                const obj = await model.fetch(data.id);
                delete obj._status;

                if (model.getSubModelData) {
                    model = model.getSubModelData(obj);
                }
                
                await model.set(obj);
            } else if (action === 'delete') {
                logger.debug(`DELETE WS message: `, data);
                if (model.getSubModelData) {
                    model = model.getSubModelData(data);
                }
                await model.delete(data.id);
            } else if (action === 'pb') {
                const {model: modelName, data: base64Data} = data;
                const protobuf = Buffer.from(base64Data, 'base64')
                const obj = protoHelperMap[modelName](protobuf);

                if (model.getSubModelData) {
                    model = model.getSubModelData(obj);
                }
                if (model.loaded) {

                    // Check if model is already processing to prevent loops
                    if (!model._isProcessing && !model._isNotifyingUpdate && !model._isRefreshing) {
                        await model.set(obj);
                    } else {
                        logger.debug(`🔄 ${model.name}: Skipping WebSocket update - model already processing data (processing: ${!!model._isProcessing}, notifying: ${!!model._isNotifyingUpdate}, refreshing: ${!!model._isRefreshing})`);
                    }
                } else {
                    if (Array.isArray(obj)) {
                        model.loadingData = model.loadingData.concat(obj);
                    } else {
                        model.loadingData.push(obj);
                    }
                }
            } else if (action === 'chunk') {
                const {objectId, idx, chunk, length} = data;
                if (!this.updatingObjects[objectId]) {
                    this.updatingObjects[objectId] =
                        this.updatingObjects[objectId] || [];
                }
                this.updatingObjects[objectId].push({
                    objectId,
                    idx,
                    chunk,
                    length,
                });
                if (this.updatingObjects[objectId].length === length) {
                    const str = this.updatingObjects[objectId]
                        .filter((ch) => ch !== undefined)
                        .sort((a, b) => a.idx - b.idx)
                        .map((ch) => ch.chunk)
                        .join('');
                    try {
                        let objs = JSON.parse(str);
                        objs = Array.isArray(objs) ? objs : [objs];
                        for(let obj of objs) {
                            delete obj._status;

                            if (model.getSubModelData) {
                                model = model.getSubModelData(obj);
                            }
                            if (model.loaded) {

                                // Check if model is already processing to prevent loops
                                if (!model._isProcessing && !model._isNotifyingUpdate && !model._isRefreshing) {
                                    await model.set(obj);
                                } else {
                                    logger.debug(`🔄 ${model.name}: Skipping WebSocket update - model already processing data (processing: ${!!model._isProcessing}, notifying: ${!!model._isNotifyingUpdate}, refreshing: ${!!model._isRefreshing})`);
                                }
                            } else {
                                if (Array.isArray(obj)) {
                                    model.loadingData = model.loadingData.concat(obj);
                                } else {
                                    model.loadingData.push(obj);
                                }
                            }
                        }
                    } catch (e) {
                        logger.error(
                            'Could not update %s from WS for the following string. Error: %s',
                            model.name,
                            e.toString(),
                            e
                        );
                        logger.error(str);
                    }
                }
            } else if (action === 'loaded') {
                logger.info(`📨 Received 'loaded' action for ${data.model}.`);
                logger.debug(`🔍 Model ${data.model} state before: loaded=${model.loaded}, hasLoadingData=${!!model.loadingData?.length}`);

                if (model.loadingData?.length) {
                    logger.debug(`📥 Processing ${model.loadingData.length} items for ${data.model}`);

                    if (model.getSubModelData) {
                        model = model.getSubModelData(model.loadingData[0]);
                    }
                    await model.db.bulkPut(model.loadingData);
                    if (model.summaryDb) {
                        await model.summaryDb.bulkPut(model.loadingData.map(model.toSummary));
                    }
                    delete model.initData;
                }
                
                logger.debug(`✅ Marking ${data.model} as loaded at ${this.connectionTime}`);
                model.setLoaded(this.connectionTime);
                if (model._loadResolver) {
                    model._loadResolver();
                }
                model.updateSyncsNow();
                
                // Mark model as successfully loaded in performance tracker (if not already tracked, start tracking)
                if (!this.performanceTracker.trackingData[data.model]) {
                    this.performanceTracker.startModelLoad(data.model, 'websocket');
                }
                this.performanceTracker.markModelLoaded(data.model);
                
                logger.debug(`✅ Model ${data.model} marked as loaded at ${this.connectionTime}`);

                // const modelNames = Object.keys(this.models).filter(
                //     (name) =>
                //         !this.modelFilter || this.modelFilter.includes(name)
                // );
                // if (modelNames.every((name) => this.models[name].loaded)) {
                //     await Promise.all(
                //         modelNames.map(async (name) => {
                //             return await this.models[name].notifyLoaded();
                //         })
                //     );
                // }
            } else if (action === 'refresh') {
                logger.debug('Received refresh message.');
                // model.refreshCache()
            }

            // append received message from the server to the DOM element
            // const data = event.data.split('::');
            // const username = data[0];
            // const message = data.slice(1).join('::'); // in case the message contains the separator '::'
            //
            // const newMessage = {
            //     timestamp: Date.now(),
            //     username,
            //     message
            // }
            //
            // setMessages((prevState) => {
            //     return [
            //         ...prevState,
            //         newMessage
            //     ];
            // });
        };
        logger.debug('WSS: ', this.wsConnection);
        this.initialised = true;
        window.addEventListener('offline', this.closeWSConnection.bind(this));
    }

    setApiKey(apiKey) {
        if (!apiKey) {
            throw new Error('No API KEY!');
        }
        this.apiKey = apiKey;

        Object.keys(this.models).forEach((modelName) => {
            this.models[modelName].apiKey = this.apiKey;
        });
    }

    setOperator(operator) {
        this.operator = operator;
        operator.regionId = operator.regionId || operator.operatorId;
        operator.orgId = operator.orgId || operator.companyId;
        this.regionId = operator.regionId;
        this.orgId = operator.orgId;
        this.setApiKey(operator.regionId);
        this.closeWSConnection();
    }

    async init({
                   apiKey = this.apiKey,
                   user,
                   modelFilter = DEFAULT_MODEL_FILTER,
                   messageApi,
                   superFast = true,
                   setFaultState,
                   noRequest = false
               }) {

        if(!modelFilter?.length) {
            modelFilter = DEFAULT_MODEL_FILTER;
        }

        if (this.modelFilter?.length && modelFilter.every(val => this.modelFilter.includes(val))) {
            console.log('ModelExpiryService already initialised with modelFilter: ', this.modelFilter);
            return;
        }

        this.setApiKey(apiKey);
        this.noRequest = noRequest;
        this.user = user;
        const {editor} = getEditor({user});
        this.modelFilter = modelFilter;
        this.messageApi = messageApi;
        this.setFaultState = setFaultState;

        Object.keys(this.models).forEach((modelName) => {
            this.models[modelName].apiKey = apiKey;
            this.models[modelName].messageApi = messageApi;
            this.models[modelName].public = !user;
            this.models[modelName].user = user;
            this.models[modelName].editor = editor;
            this.models[modelName].localCache = superFast;
            this.models[modelName].setFaultState = setFaultState;
        });

        await this.openDb();
        if (!config.local) {
            // Initial connection attempt
            this.initConnection();
            
            // Set up connection monitoring interval - only reconnect if needed
            this.wsConnectionInterval = setInterval(() => {
                // Only try to reconnect if connection is closed or doesn't exist
                if (!this.wsConnection ||
                    this.wsConnection.readyState === WebSocket.CLOSED ||
                    this.wsConnection.readyState === WebSocket.CLOSING) {
                    if (!this.isWebSocketReconnectionAllowed()) {
                        return;
                    }
                    logger.info('🔄 WebSocket connection lost, attempting to reconnect...');
                    this.initConnection();
                } else if (this.wsConnection.readyState === WebSocket.CONNECTING) {
                    logger.debug('⏳ WebSocket is connecting...');
                } else if (this.wsConnection.readyState === WebSocket.OPEN) {
                    // Connection is healthy, just log occasionally
                    if (Math.random() < 0.1) { // Only log 10% of the time to reduce spam
                        logger.debug('✅ WebSocket connection healthy');
                    }
                    
                    // Update connection quality for listeners even when no messages
                    const connectionQuality = this.getConnectionQuality();
                    this.loadedListeners.forEach(listener => {
                        if (listener.onConnectionQualityUpdate) {
                            listener.onConnectionQualityUpdate(connectionQuality);
                        }
                    });
                }
            }, 5000);

            this.saveAtSource();
        }
        window.removeEventListener(
            'offline',
            this.closeWSConnection.bind(this)
        );

        return this;
    }

    deinit() {
        clearInterval(this.wsConnectionInterval);
        this.wsConnection?.close();
        this.loadedListeners = [];
        Object.values(this.models).forEach(m => m.clear())
    }

    optOutSuperFast() {
        Object.keys(this.models).forEach((modelName) => {
            this.models[modelName].localCache = false;
            delete this.models[modelName].data;
        });
    }

    saveAtSource() {
        Object.keys(this.models).forEach((modelName) => {
            this.models[modelName].saveAtSource();
        });
    }

    async sync() {
        logger.debug('Checking for updates...');
        try {
            // const models = await API.get("routes", `/modelUpdates`);
            // models.forEach(model => {
            //     const localModel = this[model.name];
            // Expired is true if the lastUpdated is greater than local
            // localModel.setLastUpdated(model.lastUpdated);// && dayjs().diff(dayjs(model.lastUpdated), 's') > 0;
            // });
            const body = {
                source: 'busable.syncClient',
                detailType: 'SyncAll',
                detail: {
                    connectionId: this.wsConnectionId,
                    sync: await this.db.syncs.toArray(),
                },
            };
            await API.post('events', `/events`, {body});
            return body;
        } catch (e) {
            logger.debug(`Error loading model update data: ${e}`, e);
        }
    }

    addData(modelData) {
        this.models[modelData.name] = modelData;
    }

    setDeserialiseData(dataKey, data) {
        Object.keys(this.models).forEach((key) => {
            this.models[key].deserialiseData[dataKey] = data;
        });
    }

    //
    // async checkDb(cacheUpdate = 0) {
    //     const syncs = await Promise.all(Object.keys(this.models).map(async name => {
    //         return await this.db.syncs.get(name)
    //     }))
    //     if (syncs.some(sync => !sync || sync.lastUpdated < cacheUpdate)) {
    //         await this.refreshCache();
    //     }
    // }

    getDbName(operatorId) {
        return `${process.env.REACT_APP_STAGE}_${operatorId || this.apiKey}_db`;
    }

    dbOpenAttempts = 0;

    async doOpenDb(dbs) {
        try {
            this.dbOpenAttempts++;
            const [major] = APP_VERSION.split('.');
            this.db.version(major).stores(dbs);
            await this.db.open();
        } catch (err) {
            if (this.dbOpenAttempts > 3) {
                logger.error('Catastrophic failure opening DB:', err);
                this.dbOpenAttempts = 0;
                return;
            }
            logger.error('Failed to open DB:', err);
            logger.warn('Resetting database...');
            await this.db.delete();
            await this.doOpenDb(dbs);
        }
    }

    async openDb() {
        if (this.db?.isOpen()) {
            return;
        }

        try {
            this.db = new Dexie(this.getDbName());
            const dbs = {syncs: 'name', tpt: 'id, reportId, DATE_ID'};
            Object.keys(this.models).forEach((name) => {
                dbs[this.models[name].getDbName()] =
                    this.models[name].getDbIndexes();
                if (this.models[name].toSummary) {
                    dbs[this.models[name].getDbName() + '_summary'] =
                        this.models[name].getDbIndexes();
                }
            });

            await this.doOpenDb(dbs);

            await Promise.all(Object.keys(this.models).map(async name => {
                try {
                    this.models[name].db = this.db[name];
                    this.models[name].summaryDb = this.db[name + '_summary'];
                    this.models[name].syncDb = this.db.syncs;
                    let sync = await this.db.syncs.get(name);
                    if (sync) {
                        this.models[name].lastUpdated = sync.lastUpdated;
                    } else {
                        await this.db.syncs.add({name, lastUpdated: 0});
                    }
                } catch (e) {
                    logger.error(`Error opening ${name} db: `, e);
                }
            }));
        } catch (err) {
            logger.error(err.stack || err);
        }
    }

    async clearDb(operatorId) {
        if (this.db) {
            if (operatorId) {
                const dbName = this.getDbName(operatorId);
                await Dexie.getDatabaseNames((names, cb) => {
                    names.filter(name => name === dbName).forEach(name => {
                        var db = new Dexie(name);
                        db.delete().then(() => {
                            logger.info('Database successfully deleted: ', name);
                        }).catch((err) => {
                            logger.error('Could not delete database: ', name, err);
                        }).finally(() => {
                            logger.debug('Done. Now executing callback if passed.');
                            if (typeof cb === 'function') {
                                cb();
                            }
                        });
                    });
                });
            } else {
                await this.db.delete();
                delete this.db;
            }
        }
    }

    async deleteAllDbs() {
        await Dexie.getDatabaseNames((names, cb) => {
            logger.debug('database names: ', names);
            names.forEach((name) => {
                var db = new Dexie(name);
                db.delete().then(() => {
                    logger.info('Database successfully deleted: ', name);
                }).catch((err) => {
                    logger.error('Could not delete database: ', name, err);
                }).finally(() => {
                    logger.debug('Done. Now executing callback if passed.');
                    if (typeof cb === 'function') {
                        cb();
                    }
                });
            });
        });
    }
}

const routesModelExpiryService = new ModelExpiryService(config.apiGateway.WSS);

// const dutyModelExpiryService = new ModelExpiryService(config.apiGateway.duty.WSS);

// Static cache for promises
const _promiseCache = new Map();

export const getAllPromise = (instance) => {
    if (!_promiseCache.has(instance.name)) {
        _promiseCache.set(instance.name, instance.getAll());
    }
    return _promiseCache.get(instance.name);
}

/**
 * Invalidate the promise cache for a specific model
 * This forces the next getAllPromise call to fetch fresh data
 */
export const invalidatePromiseCache = (instance) => {
    if (_promiseCache.has(instance.name)) {
        _promiseCache.delete(instance.name);
        logger.debug(`🗑️ Promise cache invalidated for ${instance.name}`);
    }
}

/**
 * Clear all promise caches
 * Use this when you need to force all models to fetch fresh data
 */
export const clearAllPromiseCaches = () => {
    const clearedCount = _promiseCache.size;
    _promiseCache.clear();
    logger.debug(`🗑️ Cleared ${clearedCount} promise caches`);
}

class ModelData {

    constructor({
                    name,
                    path,
                    api,
                    expiryMins = 5,
                    fetchOptions,
                    idName,
                    saveDebounce = 1000,
                    deleteAge = 0,
                    maxSave = 100,
                    baseIndexName, baseKeyExpression, baseKeyExpressionValue,
                    ws = true
                }) {
        this.name = name;
        this.api = api || 'routes';
        this.path =
            path || (api ? '/' + api + 's' : '/' + camelCase(name) + 's');
        this.fetched = {};
        this.data = null;
        this.fetchedAll = false;
        this.expiryMins = expiryMins;
        this.apiKey = null;
        this.expired = false;
        this.getPromise = null;
        this.listeners = [];
        this.fetchOptions = fetchOptions;
        this.idName = idName || camelCase(name) + 'Id';
        this.lastUpdated = 0;
        this.loaded = false;
        this.loadingData = [];
        this.db = null;
        this.syncDb = null;
        this.saveDebounce = saveDebounce;
        this.localCache = true;
        this.localCacheInitialised = false;
        this.deleteAge = 0;
        this.maxSave = maxSave;
        this.messageApi = null;
        this.public = false;
        this.baseIndexName = baseIndexName;
        this.baseKeyExpression = baseKeyExpression;
        this.baseKeyExpressionValue = baseKeyExpressionValue;
        this.ws = ws;

        // Cache dependencies will be set up after all models are loaded
        // to avoid circular dependency issues during initialization

        this._loadResolver = null;
        this._loadPromise = new Promise((resolve) => {
            this._loadResolver = resolve;
        });
    }

    async getOptibusIntegration() {
        if (!this.optibusIntegration) {
            const operator = await operatorModelData.get(this.apiKey);
            this.optibusIntegration = createOptibusIntegration(operator);
        }
        return this.optibusIntegration;
    }

    setLoaded(connectionTIme) {
        this.lastUpdated = connectionTIme;
        this.loaded = true;
    }

    updateSyncsNow = () => {
        try {
            logger.debug('Updating syncs: ', this.name);
            if (this.loaded) {
                const {name, lastUpdated} = this;
                this.syncDb.put({name, lastUpdated}).then(noop);
            }
        } catch (e) {
            logger.error(e, e);
        }
    };

    async init() {
        const start = Date.now();

        if (this.localCache) {
            let dataAsArray = await this.db.toArray();
            dataAsArray = await this.deserialise(dataAsArray);
            this.data = this.keyDataBy(dataAsArray);
        }
        this.localCacheInitialised = true;

        logger.warn(
            `Elapsed time for getting init ${this.name}: ${toHrsMinsSecs(
                (Date.now() - start) / 1000
            )}`
        );
    }

    updateSyncs = debounce(this.updateSyncsNow, 5000);

    refreshLocalCache() {
        delete this.data;
        
        // Clear the promise cache to force fresh data on next getAllPromise call
        this.invalidatePromiseCache();
        

    }

    /**
     * Refresh local cache from Dexie database without triggering network requests
     * This is used for dependency-based cache invalidation
     */
    async refreshLocalCacheFromDexie() {
        // Prevent infinite loops by checking if we're already refreshing
        if (this._isRefreshing) {
            logger.debug(`⏭️ ${this.name} already refreshing, skipping...`);
            return;
        }
        
        // Prevent excessive refreshes by tracking last refresh time
        const now = Date.now();
        if (this._lastRefreshTime && (now - this._lastRefreshTime) < 1000) {
            logger.debug(`⏭️ ${this.name} refreshed too recently (${now - this._lastRefreshTime}ms ago), skipping...`);
            return;
        }
        
        this._isRefreshing = true;
        this._lastRefreshTime = now;
        
        logger.debug(`🔄 Refreshing ${this.name} cache from local database...`);
        
        try {
            // Clear the in-memory cache
            delete this.data;
            
            // Clear the promise cache to force fresh data on next getAllPromise call
            this.invalidatePromiseCache();
            
                            // Reload data from local Dexie database
                if (this.localCache && this.db) {
                    const time = Date.now();
                    let dataAsArray = await this.db.toArray();
                    dataAsArray = await this.deserialise(dataAsArray);
                    this.data = this.keyDataBy(dataAsArray);
                    logger.debug(`✅ ${this.name} cache refreshed from local database. Time taken: ${(Date.now() - time)}ms`);
                }
            } catch (error) {
                logger.error(`❌ Failed to refresh ${this.name} cache from local database:`, error);
            } finally {
                this._isRefreshing = false;
            }
    }

    refreshLocalCacheDebounced = debounce(this.refreshLocalCache, 2000);
    
    // New debounced method for dependency-based refresh - increased debounce time to prevent loops
    refreshLocalCacheFromDexieDebounced = debounce(this.refreshLocalCacheFromDexie, 5000);
    
    /**
     * Invalidate the promise cache for this model
     * This forces the next getAllPromise call to fetch fresh data
     */
    invalidatePromiseCache() {
        if (_promiseCache.has(this.name)) {
            _promiseCache.delete(this.name);
            logger.debug(`🗑️ Promise cache invalidated for ${this.name}`);
        }
    }

    async bulkUpdate(objs) {
        try {
            const _this = this;
            if (this.db) {
                await this.db?.bulkDelete(objs.map(obj => _this.getId(obj)));
                await this.db?.bulkPut(objs);
            }
            if (this.summaryDb) {
                await this.summaryDb.bulkDelete(
                    objs.map((obj) => _this.getId(obj))
                );
                await this.summaryDb.bulkPut(objs);
            }
        } catch (e) {
            logger.error(e, e);
        }
    }

    saveAtSourceNow = async () => {
        // await Promise.all(Object.keys(this.data).filter(id => this.data[id]._status).map(async id => {
        //     const obj = this.data[id]
        //     if (obj._status === 'dirty') {
        //         await this._save(obj)
        //     } else if (obj._status === 'new') {
        //         await this._create(obj)
        //     } else if (obj._status === 'delete') {
        //         await this._delete(this.getId(obj))
        //     }
        //     delete obj._status
        //     // await this.db.update(this.getId(obj), {_status: undefined})
        // }))
        if (this.public) {
            return;
        }
        const objs = await this.db
            .filter((obj) => obj._dirty || obj._new || obj._delete)
            .toArray();

        const _this = this;
        const newObjs = objs
            .filter((obj) => obj._new > 0)
            .map((obj) => {
                delete obj._new;
                return obj;
            });
        if (newObjs?.length) {
            try {
                await this._create(newObjs);
                await this.bulkUpdate(newObjs);
            } catch (e) {
                logger.error(e, e);
                logger.error('Error saving: ', newObjs);
                this.setFaultState?.({
                    type: 'saveRouteError',
                    error: `Error creating ${newObjs.length} ${this.name}${newObjs.length > 1 ? 's' : ''}: ` + e,
                    data: {response: e.response, exception: e, objs: newObjs}

                });
            }
        }

        const dirtyObjs = objs
            .filter((obj) => obj._dirty > 0)
            .map((obj) => {
                delete obj._dirty;
                return obj;
            });
        if (dirtyObjs?.length) {
            try {
                await this._create(dirtyObjs);
                await this.bulkUpdate(dirtyObjs);
            } catch (e) {
                logger.error(e, e);
                logger.error('Error updating: ', dirtyObjs);
                this.setFaultState?.({
                    type: 'saveRouteError',
                    error: `Error updating ${dirtyObjs.length} ${this.name}${dirtyObjs.length > 1 ? 's' : ''}: ` + e,
                    data: {response: e.response, exception: e, objs: dirtyObjs}
                });
            }
        }
        const deleteObjs = objs
            .filter((obj) => obj._delete > 0)
            .map((obj) => {
                delete obj._delete;
                return obj;
            });
        if (deleteObjs?.length) {
            try {
                await this._delete(deleteObjs.map(o => _this.getId(o)));
                await this.db.bulkDelete(deleteObjs.map(o => _this.getId(o)));
                if (this.summaryDb) {
                    await this.summaryDb.bulkDelete(
                        deleteObjs.map(o => _this.getId(o))
                    );
                }
                this.notifyDelete(deleteObjs.map(o => _this.getId(o)));

            } catch (e) {
                logger.error(e, e);
                logger.error('Error deleting: ', deleteObjs);
                // this.messageApi?.error(
                //     `Error deleting ${deleteObjs.length} ${this.name}s: ` +
                //     e,
                //     10
                // );
                this.setFaultState?.({
                    type: 'saveRouteError',
                    error: `Error deleting ${deleteObjs.length} ${this.name}${deleteObjs.length > 1 ? 's' : ''}: ` + e,
                    data: {response: e.response, exception: e, objs: deleteObjs}
                });
            }
        }
    };

    saveAtSource = debounce(this.saveAtSourceNow, this.saveDebounce);

    getDbName() {
        return this.name;
    }

    getDbIndexes() {
        return this.idName;
    }

    clear() {
        this.fetched = {};
        this.fetchedAll = false;
        delete this.apiKey;
        delete this.getPromise;
        this.listeners.length = 0;
        delete this.data;
    }

    create(obj) {
        return obj;
    }

    /**
     * Recursively removes React elements, functions, and other non-serializable values from an object
     * @param {*} value - The value to clean
     * @param {Set} seen - Set to track circular references
     * @returns {*} Cleaned value
     */
    removeUnwantedElements(value, seen = new Set()) {
        // Handle null and undefined
        if (value === null || value === undefined) {
            return value;
        }

        // Remove functions - they cannot be serialized
        if (typeof value === 'function') {
            return undefined;
        }

        // Handle primitives (string, number, boolean, symbol, bigint)
        if (typeof value !== 'object') {
            // Symbols cannot be serialized
            if (typeof value === 'symbol') {
                return undefined;
            }
            return value;
        }

        // Handle circular references
        if (seen.has(value)) {
            return undefined;
        }

        // Check if it's a React element
        // React elements have $$typeof property that's a Symbol
        if (value.$$typeof && typeof value.$$typeof === 'symbol') {
            return undefined; // Remove React elements
        }

        // Check for other React-related symbols
        const reactElementSymbol = Symbol.for('react.element');
        const reactFragmentSymbol = Symbol.for('react.fragment');
        if (value.$$typeof === reactElementSymbol || value.$$typeof === reactFragmentSymbol) {
            return undefined;
        }

        // Handle arrays
        if (Array.isArray(value)) {
            seen.add(value);
            const cleaned = value
                .map(item => this.removeUnwantedElements(item, seen))
                .filter(item => item !== undefined);
            seen.delete(value);
            return cleaned;
        }

        // Handle Date objects - these are serializable
        if (value instanceof Date) {
            return value;
        }

        // Handle RegExp - not serializable
        if (value instanceof RegExp) {
            return undefined;
        }

        // Handle Error objects - not fully serializable
        if (value instanceof Error) {
            return undefined;
        }

        // Handle plain objects
        seen.add(value);
        const cleaned = {};
        for (const key in value) {
            if (value.hasOwnProperty(key)) {
                // Skip Symbol keys
                if (typeof key === 'symbol') {
                    continue;
                }
                const cleanedValue = this.removeUnwantedElements(value[key], seen);
                // Only include the key if the value is not undefined
                if (cleanedValue !== undefined) {
                    cleaned[key] = cleanedValue;
                }
            }
        }
        seen.delete(value);
        return cleaned;
    }

    serialise(obj) {
        return obj;
    }

    async deserialise(objs) {
        objs = Array.isArray(objs) ? objs : [objs];
        objs = cloneDeep(objs);
        const result = objs.map(this.create);
        return result;
    }

    async duplicate(id) {
        let obj = await this.db.get(id);
        if (obj.clone) {
            obj = obj.clone();
        } else {
            obj = cloneDeep(obj);
        }
        this.setId(obj);
        return await this.save(obj);
    }

    async set(objs) {
        // Prevent infinite loops by checking if we're already processing this exact data
        const dataKey = JSON.stringify(objs);
        if (!this._lastSetData) this._lastSetData = new Map();
        
        if (this._lastSetData.has(dataKey)) {
            const lastSetTime = this._lastSetData.get(dataKey);
            const now = Date.now();
            if (now - lastSetTime < 2000) { // 2 second cooldown for identical data (increased)
                logger.debug(`🔄 ${this.name}: Identical data set() called too recently, skipping to prevent loop`);
                return;
            }
        }
        
        // Also check if we're already processing any data for this model
        if (this._isProcessing) {
            logger.debug(`🔄 ${this.name}: Already processing data, skipping to prevent loop`);
            return;
        }
        
        // Check if we're in the middle of a notifyUpdate cycle
        if (this._isNotifyingUpdate) {
            logger.debug(`🔄 ${this.name}: notifyUpdate in progress, skipping set() to prevent loop`);
            return;
        }
        
        // Check if we're in the middle of a refresh cycle
        if (this._isRefreshing) {
            logger.debug(`🔄 ${this.name}: refresh in progress, skipping set() to prevent loop`);
            return;
        }
        
        
        this._isProcessing = true;
        this._lastSetData.set(dataKey, Date.now());
        
        try {
            logger.debug(`Setting ${this.name}...`);

            // obj = await this.deserialise(obj)
            if (!Array.isArray(objs)) {
                objs = [objs];
            }
            
            if (this.db) {
                await this.db.bulkPut(objs);
            }
            if (this.summaryDb) {
                const _this = this;
                await this.summaryDb.bulkPut(objs.map(_this.toSummary));
            }
            logger.debug(`Added ${this.name}. Notifying listeners...`);
            logger.debug(`${this.name} - about to deserialise in set method`);
            objs = await this.deserialise(objs);
            logger.debug(`${this.name} - deserialise complete in set method`);
            if (this.localCache) {
                this.invalidatePromiseCache();
                this.data = this.data || {};
                this.updateKeyDataBy(objs);
            }
            if (this.loaded) {
                // Prevent infinite loops by checking if we've been called too recently
                const now = Date.now();
                if (!this._lastNotifyUpdateTime) this._lastNotifyUpdateTime = 0;
                
                if (now - this._lastNotifyUpdateTime < 500) { // 500ms cooldown
                    logger.debug(`🔄 ${this.name}: notifyUpdate called too recently, skipping to prevent loop`);
                    return;
                }
                
                // Also check if we're in any processing state
                if (this._isNotifyingUpdate || this._isRefreshing) {
                    logger.debug(`🔄 ${this.name}: Model in processing state, skipping notifyUpdate to prevent loop`);
                    return;
                }
                
                this._lastNotifyUpdateTime = now;
                logger.debug(`${this.name} - calling notifyUpdate for ${objs.length} objects`);
                this.notifyUpdate(objs);
            }
        } finally {
            // Reset processing flag when set operation completes
            this._isProcessing = false;
        }
    }

    async delete(ids, deleteAtSource, immediate) {
        if (!Array.isArray(ids)) {
            ids = [ids];
        }
        const time = Date.now();

        // TODO: use this instead once Dexie v4.0.1 has been released
        // if (deleteAtSource) {
        //     await this.db.bulkUpdate(ids.map(id => ({key: id, changes: {_delete: time}})))
        //     if (this.summaryDb) {
        //         await this.summaryDb.bulkUpdate(ids.map(id => ({key: id, changes: {_delete: time}})))
        //     }
        // } else {
        //     if (this.data) {
        //         this.data = omit(this.data, ids)
        //     }
        //     await this.db.bulkDelete(ids)
        //     if (this.summaryDb) {
        //         await this.summaryDb.bulkDelete(ids)
        //     }
        // }
        //
        // this.lastUpdated = Date.now()
        // ids.forEach(id => {
        //     this.notifyDelete(id);
        // });

        await Promise.all(
            ids.map(async (id) => {
                if (deleteAtSource) {
                    // this.data[id]._status = 'delete'
                    if (immediate) {
                        await this._delete(id);
                        await this.db.delete(id);
                        this.summaryDb && (await this.summaryDb.delete(id));
                        this.notifyDelete(id);
                    } else {
                        await this.db.update(id, {_delete: time});
                        if (this.summaryDb) {
                            await this.summaryDb.update(id, {_delete: time});
                        }
                    }
                } else {
                    if (this.data) {
                        delete this.data[id];
                    }
                    await this.db.delete(id);
                    if (this.summaryDb) {
                        await this.summaryDb.delete(id);
                    }
                }
                this.lastUpdated = Date.now();
            })
        );
        if (deleteAtSource) {
            this.saveAtSource();
        }

        ids.forEach(id => {
            this.notifyDelete(id);
        });
    }

    async fetch(id) {
        if (!this.path) {
            throw new Error('Please initialise fetch. No path.');
        }

        logger.debug(`Fetching Vehicle for ${this.apiKey}...`);

        let fetched;
        if (config.local) {
            fetched = await fetch(
                `/LOCAL_DATA/${this.apiKey}-${this.api}.json`,
                {
                    method: 'GET',
                }
            )
                .then((response) => response.json())
                .then((data) => {
                    return find(data, (obj) => this.getId(obj) === id);
                });
        } else {
            fetched = await API.get(
                this.api,
                `${this.path}/${id}${this.apiKey ? `?_k=${this.apiKey}` : ''}`
            );
        }
        try {
            [fetched] = await this.deserialise(fetched);
        } catch (e) {
            console.log(e, e);
            this.messageApi?.error(
                'Error fetching ' + this.name + ': ' + e,
                10
            );
        }
        logger.debug(this.name, fetched);
        await this.db.put(fetched);

        if (this.summaryDb) {
            await this.summaryDb.put(this.toSummary(fetched));
        }
        return fetched;
    }

    async fetchAll(force) {
        if (!this.path) {
            throw new Error('Please initialise fetchAll. No path.');
        }

        logger.debug(`Fetching for ${this.apiKey}...`);
        this.expired = false;
        // this.lastUpdated = Date.now()

        let fetched = [];
        if (config.local) {
            const filename = `/LOCAL_DATA/${this.apiKey}-${this.api}.json`;
            fetched = await fetch(filename, {
                method: 'GET',
            }).then((response) => response.json());
        } else {
            // check the last updated for this model in the local db
            const ttl = await this.syncDb.get(this.name);
            this.syncDb.put({name: this.name, lastUpdated: Date.now()});
            let url = `/page${this.path}${this.apiKey ? `?_k=${this.apiKey}` : ''}`;

            let indexName = this.baseIndexName, keyExpression, keyExpressionValue;
            if (this.baseKeyExpression) {
                keyExpression = this.baseKeyExpression;
                if (this.baseKeyExpressionValue) {
                    keyExpressionValue = this.baseKeyExpressionValue;
                }
            }
            if (!force && ttl?.lastUpdated > 0) {
                keyExpression = 'updatedAt > :lastUpdated';
                keyExpressionValue = `lastUpdated|${ttl.lastUpdated}|i`;
                indexName = 'UpdatedAtIdx';
                // url += `indexName=UpdatedAtIdx&${encodeURIComponent(keyExpression)}&keyExpressionValue=${encodeURIComponent(keyExpressionValue)}`;
                // fetched = await API.post(this.api, `/page${this.path}${this.apiKey ? `?_k=${this.apiKey}` : ''}&indexName=UpdatedAtIdx&keyExpression=${encodeURIComponent('updatedAt > :lastUpdated')}&keyExpressionValue=lastUpdated|${ttl.lastUpdated}|i`)
                // } else {
                //     fetched = await API.post(this.api, `/page${this.path}${this.apiKey ? `?_k=${this.apiKey}` : ''}`);
            }

            if (indexName) {
                url += `&indexName=${indexName}`;
            }
            if (keyExpression && keyExpressionValue) {
                url += `&keyExpression=${encodeURIComponent(keyExpression)}&keyExpressionValue=${encodeURIComponent(keyExpressionValue)}`;
            }
            let response = await API.post(this.api, url, {headers: {'x-accept-encoding': 'gzip'}});
            while (response.LastEvaluatedKey) {
                fetched = fetched.concat(response.items || []);
                response = await API.post(this.api, url, {
                    body: {LastEvaluatedKey: response.LastEvaluatedKey},
                    headers: {'x-accept-encoding': 'gzip'}
                });
            }
            fetched = fetched.concat(response.items || []);
        }

        console.log(`Fetched ${fetched.length} ${this.name}'s.`);
        await this.db.bulkPut(fetched);
        if (this.summaryDb) {
            const fetchedSummary = fetched.map(this.toSummary);
            await this.summaryDb.bulkPut(fetchedSummary);
        }
        // if (this.localCache && fetched?.length) {
        //     fetched = await this.deserialise(fetched);
        //     this.data = this.data || {};
        //     const fetchedById = keyBy(
        //         fetched,
        //         this.idName
        //     );
        //     this.data = {...this.data, ...fetchedById};
        // }
        this.fetchedAll = true;
        this.loaded = true;
    }

    async fetchCurrentIdBeginningWith(id) {
        if (!this.path) {
            throw new Error('Please initialise fetchAll. No path.');
        }

        logger.debug(
            `Fetching ${this.name} for ${this.apiKey}... current with id: ${id}`
        );
        this.expired = false;
        // this.lastUpdated = Date.now()

        let fetched = await API.get(
            this.api,
            `${this.path}/${encodeURIComponent(id)}`
        );
        logger.debug(`Fetched ${fetched} ${this.name}'s.`);
        if (fetched) {
            [fetched] = await this.deserialise(fetched);
        }
        return this.create(fetched);
    }

    async fetchAllIdBeginningWith(id, apiKey, force) {
        if (!this.path) {
            throw new Error('Please initialise fetchAll. No path.');
        }

        logger.debug(
            `Fetching ${this.name} for ${this.apiKey}... beginning with with id: ${id}`
        );
        this.expired = false;
        // this.lastUpdated = Date.now()
        if (!force && this.data && Object.keys(this.data)?.length) {
            return values(
                pickBy(this.data, (value, key) => key.startsWith(id))
            );
        }
        if(!force) {
            try {
                const fetched = await this.db.where(this.idName).startsWith(id).toArray();
                if (fetched?.length) {
                    return await this.deserialise(fetched);
                }
            } catch (e) {
                console.log(e, e);
            }
        }

        let fetched;
        if (config.local) {
            const filename = `/LOCAL_DATA/${this.apiKey}-${this.api}.json`;
            fetched = await fetch(filename, {
                method: 'GET',
            })
                .then((response) => response.json())
                .then((data) => {
                    return data
                        .filter((obj) => this.getId(obj).startsWith(id))
                        .map((r) => this.create(r));
                });
        } else {
            fetched = (
                await API.get(
                    this.api,
                    `${this.path}${
                        (apiKey || this.apiKey) ? `?_k=${apiKey || this.apiKey}` : ''
                    }&keyExpression=begins_with(${
                        this.idName
                    }, :id)&keyExpressionValue=id|${encodeURIComponent(id)}`
                )
            ).map(this.create);

            await Promise.all(
                fetched.map(async (s) => {
                    await this.db?.put(s);
                    if (this.summaryDb) {
                        await this.summaryDb.put(this.toSummary(s));
                    }
                    this.fetched[id] = true;
                })
            );
        }

        // if(this._loadResolver) {
        //     this._loadResolver();
        // }

        logger.debug(`Fetched ${fetched.length} ${this.name}'s.`);
        if (fetched) {
            fetched = await this.deserialise(fetched);
        }
        return fetched;
    }

    async fetchAllIdBetween(id1, id2, apiKey) {
        if (!this.path) {
            throw new Error('Please initialise fetchAll. No path.');
        }

        logger.debug(
            `Fetching ${this.name} for ${this.apiKey}... between with id: ${id1} and ${id2}`
        );
        this.expired = false;
        let fetched = (
            await API.get(
                this.api,
                `${this.path}${
                    (apiKey || this.apiKey) ? `?_k=${apiKey || this.apiKey}` : ''
                }&keyExpression=${this.idName} BETWEEN :id1 AND :id2&keyExpressionValue=id1|${encodeURIComponent(id1)},id2|${encodeURIComponent(id2)}`
            )
        ).map(this.create);

        await Promise.all(
            fetched.map(async (s) => {
                await this.db.put(s);
                if (this.summaryDb) {
                    await this.summaryDb.put(this.toSummary(s));
                }
                this.fetched[this.getId(s)] = true;
            })
        );

        // if(this._loadResolver) {
        //     this._loadResolver();
        // }

        logger.debug(`Fetched ${fetched.length} ${this.name}'s.`);
        if (fetched) {
            fetched = await this.deserialise(fetched);
        }
        return fetched;
    }

    notifyAll() {
        this.db.toArray().then((data) => {
            this.listeners.forEach((listener) => {
                if (listener.notifyAll) {
                    listener.notifyAll(data);
                }
            });
        });
    }

    async notifyLoaded(objs) {
        logger.debug(`Notifying loaded for all listeners of ${this.name}...`);

        if (objs) {
            objs = await this.deserialise(objs);
            this.listeners
                .filter((listener) => listener.loaded)
                .forEach((listener) => {
                    listener.loaded(keyBy(objs, this.idName));
                    logger.debug(
                        `Listener ${listener.id} of ${this.name} loaded.`
                    );
                });
        } else {
            let objs = await getAllPromise(this);
            await Promise.all(
                this.listeners
                    .filter((listener) => listener.loaded)
                    .map(async (listener) => {
                        listener.loaded(Array.isArray(objs) ? keyBy(objs, this.idName) : objs);
                        logger.debug(
                            `Listener ${listener.id} of ${this.name} loaded.`
                        );
                        return Promise.resolve();
                    })
            );
        }
        logger.debug(`All listeners of ${this.name} loaded.`);
    }

    notifyUpdates() {
        if (!this.updates?.length) {
            return
        }
        // Prevent recursive notifyUpdate calls
        if (this._isNotifyingUpdate) {
            logger.debug(`🔄 ${this.name}: notifyUpdate already in progress, skipping to prevent loop`);
            return;
        }        

        // Also check if we're in the middle of a set operation
        if (this._isProcessing) {
            logger.debug(`🔄 ${this.name}: set() operation in progress, skipping notifyUpdate to prevent loop`);
            return;
        }
        
        // Also check if we're in the middle of a refresh operation
        if (this._isRefreshing) {
            logger.debug(`🔄 ${this.name}: refresh operation in progress, skipping notifyUpdate to prevent loop`);
            return;
        }
        
        this._isNotifyingUpdate = true;
        
        try {
            this.updates.forEach(({obj, local}) => {
                this.listeners.forEach((listener) => {
                    if (listener.notifyUpdate) {
                        listener.notifyUpdate(obj, local);
                    } else if (listener.setterFn) {
                        let objs = obj;
                        if (!Array.isArray(objs)) {
                            objs = [objs];
                        }
                        logger.debug(`${objs.length} ${this.name}s  updated.`);
                        listener.setterFn((existingObjs) => {
                            if (!existingObjs) {
                                return;
                            }
                            if (Array.isArray(existingObjs)) {
                                objs.forEach((obj) => {
                                    const idx = existingObjs.findIndex(
                                        (existing) =>
                                            this.getId(obj) === this.getId(existing)
                                    );
                                    if (idx > -1) {
                                        logger.debug(`${this.name} - notifyUpdate calling create for existing object at idx ${idx}`);
                                        existingObjs[idx] = this.create({
                                            ...existingObjs[idx],
                                            ...obj,
                                        });
                                        // logger.debug(`Found existing ${this.name} idx@${idx}`)
                                        // existingObjs.splice(idx, 1, obj);
                                    } else {
                                        // logger.debug(`New ${this.name}...`)
                                        logger.debug(`${this.name} - notifyUpdate pushing new object`);
                                        existingObjs.push(obj);
                                    }
                                });
                                if (listener.sortFn) {
                                    existingObjs.sort(listener.sortFn);
                                }
                                return [...existingObjs];
                            } else if (typeof existingObjs === 'object') {
                                objs.forEach((obj) => {
                                    const id = this.getId(obj);
                                    logger.debug(`${this.name} - notifyUpdate calling create for object with id ${id}`);
                                    existingObjs[id] = this.create({
                                        ...existingObjs[id],
                                        ...obj,
                                    });
                                });
                                return {...existingObjs};
                            }
                        });
                    }
                    if (listener.refresh) {
                        listener.refresh(obj);
                    }
                });
            });
        } finally {
            this._isNotifyingUpdate = false;
            this.updates.length = 0;
        }
    }

    notifyDebounced =  debounce(() => this.notifyUpdates(), 500);

    notifyUpdate(obj, local) {
        this.updates = this.updates || [];
        this.updates.push({obj, local});
        this.notifyDebounced();
    }

    notifyDelete(id) {
        this.listeners.forEach((listener) => {
            if (listener.notifyDelete) {
                listener.notifyDelete(id);
            } else if (listener.setterFn) {
                let ids = Array.isArray(id) ? id : [id];
                listener.setterFn((existingObjs) => {
                    if (Array.isArray(existingObjs)) {
                        return existingObjs.filter((obj) => !ids.includes(this.getId(obj)));
                    } else if (existingObjs) {
                        logger.debug('Deleting ', ids);
                        delete existingObjs[ids];
                        return omit(existingObjs, ids);
                    }
                });
            } else if (listener.refresh) {
                listener.refresh();
            }
        });
    }

    addListener(listener) {
        if (listener.id) {
            const existingIdx = findIndex(this.listeners, ['id', listener.id]);
            if (existingIdx > -1) {
                this.listeners[existingIdx] = listener;
            } else {
                this.listeners.push(listener);
            }
        } else {
            this.listeners.push(listener);
        }
        if (this.loaded) {
            logger.debug(this.name + ' already loaded...');
            this.notifyLoaded().then(noop);
        }
        return listener;
    }

    removeListener(listener) {
        if (!listener) {
            return;
        }
        if (typeof listener === 'string') {
            this.listeners = this.listeners.filter((l) => l.id !== listener);
        } else {
            this.listeners = this.listeners.filter((l) => listener !== l);
        }
    }

    async copy(obj) {
        const copy = this.create(obj);
        this.setId(copy, '_');
        return await this.save(copy);
    }

    async save(objs, opts = {indirect: false, silent: false, lazy: false}) {
        if (!this.apiKey) {
            throw new Error('SETUP incomplete. No apiKey!')
        }
        if (!Array.isArray(objs)) {
            objs = [objs];
        }
        try {
            let create = false;
            objs = objs.map((obj) => {
                obj.createdAt = obj.createdAt || Date.now();
                if (!opts.indirect) {
                    obj.updatedAt = Date.now();
                    obj.author = !obj.author?.length || obj.author.includes('@') ? this.editor : obj.author;
                    obj.lastEditor = this.editor;
                }
                const id = this.getId(obj);
                if (!id || id === '_') {
                    create = true;
                    logger.debug('Creating new', obj);
                    this.setId(obj, ulid());
                } else {
                    // Object already has a valid ID, don't regenerate it
                    logger.debug('Updating existing', obj);
                }
                return obj;
            });

            const serialised = objs.map((obj) => {
                let serialised = obj.toJson
                    ? obj.toJson()
                    : this.serialise(obj);
                // Remove React elements from serialized data (in case toJson() included them)
                serialised = this.removeUnwantedElements(serialised);
                serialised.userId = this.apiKey;
                if (create) {
                    serialised._new = Date.now();
                } else {
                    serialised._dirty = Date.now();
                }
                serialised.editor = this.editor;
                serialised.source = 'web.client.' + packageJson.version;
                serialised.username = this.user?.username;
                return serialised;
            });
            let summaries;
            if (this.summaryDb) {
                summaries = objs.map(this.toSummary);
            }
            // const id = this.getId(obj)
            if (create) {
                // logger.debug('Creating new', obj)
                // this.setId(obj, ulid())
                // obj = await this._create(obj);
                // const serialised = obj.toJson ? obj.toJson() : this.serialise(obj)
                // serialised._new = Date.now()
                // obj = await this.deserialise(obj)
                await this.db.bulkPut(serialised);
                if (summaries) {
                    await this.summaryDb.bulkPut(summaries);
                }
                // this.saveAtSource()
            } else {
                // obj = await this._save(obj);
                // const serialised = obj.toJson ? obj.toJson() : this.serialise(obj);
                // serialised._dirty = Date.now();
                // obj = await this.deserialise(obj)
                await Promise.all(
                    serialised.map(async (serialised) => {
                        const id = this.getId(serialised);
                        if (!id) {
                            this.setFaultState?.({
                                type: 'saveRouteError',
                                error: 'No ID found for object',
                                data: {response: null, exception: null, objs: serialised}
                            });
                            return;
                        }
                        try {
                            const result = await this.db.update(id, serialised);
                            if (result === 0) {
                                await this.db.put(serialised);
                            }
                        } catch(e) {
                            this.setFaultState?.({
                                type: 'saveRouteError',
                                error: 'Failed to save object',
                                data: {response: e.response, exception: e, objs: serialised}
                            });
                            return;
                        }
                    })
                );

                if (summaries) {
                    await Promise.all(
                        summaries.map(async (summary) => {
                            const id = this.getId(summary);
                            if (!id) {
                                this.setFaultState?.({
                                    type: 'saveRouteError',
                                    error: 'No ID found for summary',
                                    data: {response: null, exception: null, objs: summary}
                                });
                                return;
                            }
                            try {
                                const result = await this.summaryDb.update(id,
                                    summary
                                );
                                if (result === 0) {
                                    await this.summaryDb.put(summary);
                                }
                            } catch(e) {
                                this.setFaultState?.({
                                    type: 'saveRouteError',
                                    error: 'Failed to save summary',
                                    data: {response: e.response, exception: e, objs: summary}
                                });
                                return;
                            }
                        })
                    );
                }
                // this.saveAtSource()
            }
            // this.lastUpdated = Date.now();

            const deserialised = objs.map(obj => this.create(obj));
            if (this.localCache) {
                this.invalidatePromiseCache();
                this.data = this.data || {};
                this.updateKeyDataBy(deserialised)
            }
            if(opts.lazy) {
                setTimeout(() => {
                    this.saveAtSourceNow();
                }, 1000);
            } else {
                this.saveAtSource()
            }
            !opts.silent && this.notifyUpdate(deserialised, true);
            return deserialised?.length === 1 ? deserialised[0] : deserialised;
        } catch (e) {
            logger.error(`Couldn't save ${this.name}: ${e}`, e);
            this.messageApi?.error('Error saving ' + this.name + '. Please try again.', 10);
            throw e;
        }
    }

    async _create(obj) {
        if (!this.path) {
            throw new Error('Please initialise. No path.');
        }
        if (Array.isArray(obj)) {
            const serialised = obj.map((o) => {
                o.userId = this.apiKey;
                o = o.toJson ? o.toJson() : this.serialise(o);
                o.editor = this.lastEditor;
                o.source = 'web.client.' + packageJson.version;
                o.username = this.user?.username;
                return o;
            });
            await Promise.all(
                chunk(serialised, this.maxSave).map(async (chunk) => {
                    return await API.post(this.api, this.path, {
                        body: chunk,
                    });
                })
            );
            return obj.map(this.create);
        } else {
            console.log(
                'Saving',
                obj.toJson ? obj.toJson() : this.serialise(obj)
            );
            obj.userId = this.apiKey;
            obj = obj.toJson ? obj.toJson() : this.serialise(obj);
            obj.editor = this.lastEditor;
            obj.source = 'web.client.' + packageJson.version;
            obj.username = this.user?.username;
            if (config.local) {
                console.log('Creating...');
                console.log('POST: ', this.path);
                console.log({
                    body: obj,
                });
            } else {
                await API.post(this.api, this.path, {
                    body: obj,
                });
                return this.create(obj);
            }
        }
    }

    async _save(obj) {
        if (!this.path) {
            throw new Error('Please initialise. No path.');
        }
        if (Array.isArray(obj)) {
            return await this._create(obj);
        } else {
            obj.userId = this.apiKey;
            obj = obj.toJson ? obj.toJson() : this.serialise(obj);
            obj.editor = this.lastEditor;
            obj.source = 'web.client.' + packageJson.version;
            obj.username = this.user?.username;
            if (config.local) {
                console.log('Saving...');
                console.log(`PUT: ${this.path}/${this.getId(obj)}`);
                console.log({
                    body: obj,
                });
            } else {
                let id = this.getId(obj);
                id = encodeURIComponent(id)
                    .replace('(', '%28')
                    .replace(')', '%29');
                await API.put(this.api, `${this.path}/${id}`, {
                    body: obj,
                });
                return this.create(obj);
            }
        }
    }

    async _delete(ids) {
        if (!this.path || !this.apiKey) {
            throw new Error('Please initialise. No path.');
        }
        if (!config.local) {
            if (Array.isArray(ids)) {
                ids = ids.map((id) => ({[this.idName]: id, editor: this.lastEditor, source: 'web.client.' + packageJson.version, username: this.user?.username}));

                await API.del(this.api, `${this.path}?_k=${this.apiKey}`, {
                    body: ids,
                });
            } else {
                await API.del(this.api, `${this.path}?_k=${this.apiKey}`, {
                    body: {[this.idName]: ids, editor: this.lastEditor, source: 'web.client.' + packageJson.version, username: this.user?.username},
                });
            }
        }
    }

    setLastUpdated(lastUpdated) {
        logger.debug(
            `${this.name}'s last updated: ${lastUpdated}. Local: ${this.lastUpdated}`
        );
        if (lastUpdated > this.lastUpdated) {
            this.lastUpdated = lastUpdated;
            this.expired = true;
        }
        logger.debug(`${this.name}s expired: ${this.expired}`);
    }

    getId(obj) {
        if (this.idName) {
            return obj[this.idName];
        }
        throw new Error('Please implement getId(obj)');
    }

    setId(obj, id = ulid()) {
        if (this.idName) {
            obj[this.idName] = id;
            return;
        }
        throw new Error('Please implement setId(obj, id)');
    }

    getStoreId(id) {
        return `${this.name}:${id}`;
    }

    getIdFromKey(key) {
        logger.debug(`Splitting key: ${key}`);
        return key.split(':')[1];
    }

    _getAll(apiKey, batchFn) {
        // modelExpiryService.initConnection(apiKey).then(()=>{});
        if (this.getPromise) {
            if (batchFn && !this.getPromise.batchFns.contains(batchFn)) {
                this.getPromise.batchFns.push(batchFn);
            }
            return this.getPromise;
        }
        this.getPromise = {
            promise: new Promise(async (resolve) => {
                const result = await this._getAll(apiKey, batchFn);
                resolve(result);
                delete this.getPromise;
            }),
            batchFns: [],
        };

        if (batchFn) {
            this.getPromise.batchFns.push(batchFn);
        }
        return this.getPromise;
    }

    keyDataBy(objs) {
        objs = Array.isArray(objs) ? objs : [objs];
        return keyBy(objs, this.idName)
    }

    updateKeyDataBy(objs) {
        objs = Array.isArray(objs) ? objs : [objs];
        objs.forEach((obj) => {
            // update existing object in local cache
            this.data[this.getId(obj)] = this.create({...this.data[this.getId(obj)], ...obj});
        });
    }

    async getAll(opts = {summary: false}) {

        // Wait for the model to be initialized
        if (this._loadPromise) {
            await this._loadPromise;
        }

        const start = Date.now();
        const {apiKey = null, asArray = false, sortFn = null} = opts;
        if (apiKey) {
            this.apiKey = apiKey;
        }
        // if (force) {
        //     logger.debug(`${this.name} data expired. Refreshing...`);
        //     // try {
        //     await this.fetchAll(force);
        //     // if (force) {
        //     //     this.db.each(obj => this.fetched[this.getId(obj)] = true)
        //     // }
        //     this.fetchedAll = true;
        //     logger.debug(`${this.name}s refreshed. Total of ${await this.db.count()} ${this.name}s fetched.`);
        //     // } catch (e) {
        //     //     logger.warn(`Couldn't fetch data. Error: ${e}`, e);
        //     // }
        // }
        // const dependents = await Promise.all(this.dependents.map(async dependent => dependent.getAll()))

        try {
            let all;
            if (opts.summary && this.summaryDb) {
                all = await this.summaryDb.toArray();
            } else {
                if (this.data && Object.keys(this.data).length) {
                    if (asArray) {
                        return [...values(this.data)];
                    }
                    return {...this.data};
                }
                all = await this.db?.toArray();
                if (all?.length) {
                    // Only call deserialise when there is data
                    all = await this.deserialise(all);
                }
            }

            if (!all?.length) return asArray ? [] : {};
            
            logger.debug(this.name, all);
            if (asArray) {
                return sortFn ? all.sort(sortFn) : all;
            }
            const result = this.keyDataBy(all, this.idName);

            if (!opts.summary && this.localCache) {
                this.data = result;
            }
            logger.warn(
                `Elapsed time for getting all ${this.name}: ${toHrsMinsSecs(
                    (Date.now() - start) / 1000
                )}`
            );
            return result;
        } catch (e) {
            console.log('Couldn\'t get all data. Error: ' + e, e);
            return asArray ? [] : {};
        }
    }

    async get(apiKey, id) {
        // Wait for the model to be initialized
        if (this._loadPromise) {
            await this._loadPromise;
        }

        const start = Date.now();
        this.apiKey = apiKey || this.apiKey;
        let obj;
        try {
            obj = this.data?.[id] && this.create(this.data[id]);
            if (obj) return obj;
            obj = (await this.db.get(id)) || null;
        } catch (e) {
            console.log(e, e);
        }
        if (!obj) return;
        [obj] = await this.deserialise(obj);
        logger.warn(
            `Elapsed time for get ${this.name}: ${toHrsMinsSecs(
                (Date.now() - start) / 1000
            )}`
        );
        return this.create(obj);
    }

    setApiKey(apiKey) {
        this.apiKey = apiKey;
    }

    setFetchOption(name, val) {
        this.fetchOptions = this.fetchOptions || {};
        this.fetchOptions[name] = val;
        return this;
    }

    /**
     * Check if the model is ready (initialized and loaded)
     * @returns {boolean} True if the model is ready
     */
    isReady() {
        return this.localCacheInitialised && this.loaded;
    }

    /**
     * Get the loading promise for this model
     * @returns {Promise} Promise that resolves when the model is ready
     */
    getLoadPromise() {
        return this._loadPromise;
    }
}

class StopModelData extends ModelData {
    constructor() {
        super({name: 'Stop', baseIndexName: 'DuplicateIdx', baseKeyExpression: 'duplicate <= :duplicate', baseKeyExpressionValue: 'duplicate|0|i'});
    }

    async save(objs, opts = {indirect: false}) {
        if (!Array.isArray(objs)) {
            objs = [objs];
        }
        objs.forEach(obj => {
            if (obj.master) {
                delete obj.master;
                obj.stopId = ulid();
                obj.verified = 1;
                obj.duplicate = -1;
            }
            if (obj.stopType === 'restricted') {
                obj.verified = 1;
                obj.duplicate = -1;
            }
            if (obj.duplicate === undefined || obj.duplicate === null || !isFinite(obj.duplicate)) {
                obj.duplicate = -1;
            }
            // Retain author if shared stop
            // if(obj.authorityId) {
            //     obj.author = 'TFNSW'
            // }

        });
        await Promise.all(objs.map(async stop => {
            if (!stop.postcode?.length && !stop.suburb?.length) {
                const {postcode, suburb, town, city} = await reverseGeocode(stop) || {};
                stop.postcode = postcode;
                stop.suburb = suburb || town || city;
            }
        }));
        return await super.save(objs, opts);
    }

    async init() {
        const start = Date.now();
        let stops = await this.db.toArray();

        const duplicates = stops.filter(stop => stop.duplicate === 1);
        if (duplicates?.length) {
            this.db?.bulkDelete(duplicates.map(obj => obj.stopId))
                .then(() => console.log(duplicates.length, ' duplicate stops deleted.'));
        }

        if (this.localCache) {
            stops = stops.filter(stop => !stop.duplicate || stop.duplicate < 1);
            stops = (await this.deserialise(stops))
                .map((stop) => {
                    stop.setLinkedStops(stops);
                    return stop;
                });
            stops = await this.deserialise(stops);

            // if (!this.data) throw new Error('Stops must be fetched before ' + this.name)

            this.data = keyBy(
                stops,
                this.idName
            );
        }

        this.localCacheInitialised = true;

        logger.warn(
            `Elapsed time for getting init ${this.name}: ${toHrsMinsSecs(
                (Date.now() - start) / 1000
            )}`
        );
    }

    async getAll(opts = {summary: false}) {
        const allStops = await super.getAll(opts);
        const nonDuplicates = Object.keys(allStops).filter(stopId => !allStops[stopId].duplicate || allStops[stopId].duplicate < 1);
        return pick(allStops, nonDuplicates);
    }

    // async set(stops) {
    //     if (!Array.isArray(stops)) {
    //         stops = [stops]
    //     }
    //     // const duplicates = stops.filter(stop => stop.duplicate === 1)
    //     await super.set(stops);
    //     // if (duplicates.length) {
    //     //     await Promise.all(duplicates.map(async duplicateStop => {
    //     //         if (this.data) {
    //     //             delete this.data[duplicateStop.stopId];
    //     //         }
    //     //         await this.db.delete(duplicateStop.stopId);
    //     //         this.notifyDelete(duplicateStop.stopId);
    //     //
    //     //     }))
    //     // }
    // }

    create(obj) {
        return new Stop(obj);
    }

    async _create(obj) {
        if (Array.isArray(obj)) {
            obj.forEach(o => {
                o.userId = this.apiKey;
            });
        } else {
            obj.userId = this.apiKey;
        }
        await createStop(obj);
        return new Stop(obj);
    }

    async _save(objs) {
        if (!Array.isArray(objs)) {
            objs = [objs];
        }
        return await Promise.all(
            objs.map(async (obj) => {
                obj.userId = this.apiKey;
                delete obj._status;
                if (config.local) {
                    console.log('Saving...');
                    console.log(`PUT: stops/${this.getId(obj)}`);
                    console.log({
                        body: obj.toJson ? obj.toJson() : this.serialise(obj),
                    });
                } else {
                    await updateStop(obj);
                }
                return new Stop(obj);
            })
        );
    }

    // async _delete(ids) {
    //     await deleteStop(ids, this.apiKey);
    // }

    async fetch(id) {
        const stop = await API.get('routes', `/stops/${id}?_k=${this.apiKey}`);
        stop.center = [stop.lon, stop.lat];
        logger.debug(this.name, stop);
        // if (this.localCache) {
        //     this.data = this.data || {};
        //     this.data[stop.stopId] = this.create(stop);
        // }
        try {
            await this.db.put(stop);
        } catch (e) {
            console.log(e, e);
        }
        const obj = new Stop(stop);
        console.log('Setting linked stops for ', this.name, obj.stopId)
        obj.setLinkedStops(await getAllPromise(this));
        return obj;
    }

// async fetchAll() {
//     const startTime = Date.now();
//     logger.debug(`Fetching Stops for ${this.apiKey}...`);
//     this.expired = false;
//     this.lastUpdated = Date.now()
//
//     let fetched;
//
//     if (config.local) {
//         fetched = await fetch(`/LOCAL_DATA/${this.apiKey}-stops.json`, {
//             method: 'GET'
//         }).then(response => response.json())
//     } else {
//         fetched = await API.get("routes", `/stops${this.apiKey ? `?_k=${this.apiKey}` : ''}`);
//     }
//     logger.debug(`Fetched ${fetched.length} ${this.name}'s.`)
//     fetched = fetched.map(stop => {
//         stop.center = [stop.lon, stop.lat];
//         return new Stop(stop)
//     });
//     if (this.localCache) {
//         this.data = keyBy(fetched.map(obj => this.create(obj)), 'stopId')
//     }
//     await this.db.bulkPut(fetched);
//
//     // Set linkedStops
//     // fetched.forEach(stop => stop.setLinkedStops(keyBy(fetched, 'stopId'), false))
//
//     const elapsed = Date.now() - startTime
//     const msg = `StopModelService - fetchAll elapsed: ${elapsed}`
//     elapsed > 5000 ? logger.warn(msg) : logger.info(msg)
// }

    async sendAll() {
        logger.debug(`Sending Stops for ${this.apiKey}...`);
        await API.get('routes', `/ws/stops?_k=${this.apiKey}`);
    }

    getId(obj) {
        if (obj) {
            return obj.stopId;
        }
    }

    setId(obj, id = ulid()) {
        obj.stopId = id;
    }

    serialise(stop) {
        const {
            userId,
            stopId,
            lat,
            lon,
            stopName,
            stopDesc,
            stopType,
            suburb,
            postcode,
            stopCode,
            routes,
            transfers,
            duplicate,
            verified,
            imported,
            linkedStops,
            startBell,
            startBellWindow,
            endBell,
            endBellWindow,
            radius,
            authorityId,
            outOfSync,
            aliases,
            wheelchair,
            author,
            lastEditor,
            bearing,
            website,
            email,
            phone,
            street,
            state,
            type,
            transferWindow,
            place_id
        } = stop;
        let stopToUpdate = {
            userId,
            stopId,
            lat,
            lon,
            stopName,
            stopDesc,
            stopType,
            suburb,
            postcode,
            stopCode,
            routes,
            transfers,
            duplicate,
            verified,
            imported,
            linkedStops,
            startBell,
            startBellWindow,
            endBell,
            endBellWindow,
            radius,
            authorityId,
            outOfSync,
            aliases,
            wheelchair,
            author,
            lastEditor,
            bearing,
            website,
            email,
            phone,
            street,
            state,
            type,
            transferWindow,
            place_id
        };
        if (!['venue', 'school'].includes(stopToUpdate.stopType)) {
            delete stopToUpdate.startBell;
            delete stopToUpdate.startBellWindow;
            delete stopToUpdate.endBell;
            delete stopToUpdate.endBellWindow;
        }
        if (stopToUpdate.routes) {
            stopToUpdate.routes.forEach((r) => {
                delete r.merged;
            });
        }

        stopToUpdate.linkedStops?.forEach(linkedStop => {
            Object.keys(linkedStop)
                .filter(key => !LinkedStop.SERIALISED_KEYS().includes(key))
                .forEach(key => {
                    delete linkedStop[key];
                });
        });

        if (stopToUpdate.stopType === 'restricted') {
            stopToUpdate.verified = 1;
            stopToUpdate.duplicate = -1;
        }
        Object.keys(stopToUpdate).forEach((key) => {
            if (stopToUpdate[key] === null) {
                delete stopToUpdate[key];
            }
        });
        stopToUpdate.suburb = stopToUpdate.suburb?.toUpperCase();
        return stopToUpdate;
    }

    async deserialise(objs) {
        console.log('Deserialising Stops...');
        const time = Date.now();
        objs = await super.deserialise(objs);

        if (this.loaded && this.data) {
            objs = objs.map((stop) => {
                stop.setLinkedStops(this.data);
                return stop;
            });
        }
        objs.forEach(s => {
            calculateGeoHash(s);
            if (s.stopType === 'restricted') {
                s.verified = 1;
                s.duplicate = -1;
            }
        });
        console.log(`STops deserialised. Elapsed: ${Date.now() - time}ms`);
        return objs;
    }
}

class RouteModelData extends ModelData {
    constructor(fetchOptions) {
        super({
            name: 'Route',
            fetchOptions: fetchOptions,
            deleteAge: 1000 * 60 * 60 * 24 * 30,
            maxSave: 5,
        });
    }

    toSummary(route) {
        let {
            routeId,
            routeNumber,
            routeName,
            routeDetails,
            colour,
            routeLogo,
            services = [],
            routeType,
            direction,
            published,
            driverShift,
            stopTimes,
            warnings,
        } = route;
        // services = services.map(trip => ({scheduleIds: trip.scheduleIds, stopTimes: [trip.stopTimes[0]]}))
        return {
            routeId,
            routeNumber,
            routeName,
            routeDetails,
            colour,
            routeLogo,
            services,
            routeType,
            direction,
            published,
            driverShift,
            stopTimes,
            warnings,
        };
    }

    async _create(obj) {
        return await this._save(obj);
    }

    async _save(objs) {
        if (!Array.isArray(objs)) {
            objs = [objs];
        }
        return await Promise.all(
            objs.map(async (obj) => {
                delete obj._status;
                obj.editor = this.editor;
                obj.source = 'web.client.' + packageJson.version;
                obj.username = this.user?.username;
                obj.userId = this.apiKey;
                if (config.local) {
                    console.log('Saving...');
                    console.log(`PUT: routes/${this.getId(obj)}`);
                    console.log({
                        body: obj.toJson ? obj.toJson() : this.serialise(obj),
                    });
                } else {
                    await updateRoute(obj);
                }
                return new BusRoute(obj);
            })
        );
    }

    async _delete(ids) {
        await deleteRoute(ids, this.apiKey);
    }

    async fetch(id) {
        logger.debug('Fetching route: ', id);
        const obj = await loadRoute(id, true, this.apiKey, true);
        // const obj = await this.db.get(id);
        // if (this.localCache) {
        //     this.data = this.data || {};
        //     this.data[id] = this.create(obj);
        // }
        await this.db.put(obj);
        return obj;
    }

    // async fetchAll() {
    //     logger.debug(`Fetching ${this.name}s for ${this.apiKey}...`);
    //     this.expired = false;
    //     this.lastUpdated = Date.now()
    //     // await API.get("routes", `/ws/routes?_k=${this.apiKey}`);
    //     return new Promise((resolve) => {
    //         getRoutes(true, true, true, false, true, this.apiKey, fetchedRoutes => {
    //             logger.debug(`Fetched ${fetchedRoutes.length} ${this.name}'s.`)
    //             let batch = {};
    //             fetchedRoutes.forEach(o => batch[this.getId(o)] = o);
    //             if (this.getPromise.batchFns) {
    //                 logger.debug('We have batch fn.')
    //                 this.getPromise.batchFns.forEach(batchFn => batchFn(batch));
    //             }
    //             this.data = this.data || {};
    //             Object.assign(this.data, batch);
    //             this.write();
    //         }, resolve);
    //     })
    // }

    async fetchAll() {
        const startTime = Date.now();
        logger.debug(`Fetching ${this.name}s for ${this.apiKey}...`);
        this.expired = false;
        // this.lastUpdated = Date.now()
        const _this = this;
        // await API.get("routes", `/ws/routes?_k=${this.apiKey}`);
        return new Promise((resolve) => {
            return getRoutesWithOpts(
                this.apiKey,
                async (fetchedRoutes) => {
                    logger.debug(
                        `Fetched ${fetchedRoutes.length} ${
                            _this.name
                        }'s.  ${fetchedRoutes.map((r) => r.routeNumber)}`
                    );
                    fetchedRoutes.forEach(r => {
                        delete r.warning;
                        delete r.warnings;
                        delete r.patternChecked;
                    });

                    // await this.db.bulkPut(fetchedRoutes);
                    // fetchedRoutes.forEach((r) => {
                    //     // if (!_this.fetched[r.routeId]) {
                    //     _this.set(r);
                    //     // }
                    // });
                    // let batch = {};
                    // fetchedRoutes.forEach(o => batch[this.getId(o)] = o);
                    // if (this.getPromise.batchFns) {
                    //     logger.debug('We have batch fn.')
                    //     this.getPromise.batchFns.forEach(batchFn => batchFn(batch));
                    // }
                    // this.data = this.data || {};
                    // Object.assign(this.data, batch);
                    // this.write();
                },
                resolve,
                this.fetchOptions
            );
        })
            .then(routes => {
                this.db.bulkPut(routes);
                return routes;
            })
            .then(routes => {
                // if(this._loadResolver) {
                //     this._loadResolver();
                // }
                const elapsed = Date.now() - startTime;
                const msg = `RouteModelService - fetched: ${
                    routes?.length
                } routes with ${routes?.reduce(
                    (prev, route) => route.tripCount + prev,
                    0
                )} services, elapsed time: ${elapsed}`;
                elapsed > 5000 ? logger.warn(msg) : logger.info(msg);
                // if (this.localCache) {
                //     this.data = this.data || {};
                //     this.deserialise(routes).then(routes => {
                //         const fetchedById = keyBy(
                //             routes,
                //             this.idName
                //         );
                //         this.data = {...this.data, ...fetchedById};
                //     });
                // }
                this.loaded = true;
                this.fetchedAll = true;
            });
    }

    async sendAll() {
        logger.debug(`Sending ${this.name}s for ${this.apiKey}...`);
        await API.get('routes', `/ws/routes?_k=${this.apiKey}`);
    }

    getId(obj) {
        return obj.routeId;
    }

    setId(obj, id = ulid()) {
        obj.routeId = id;
    }

    create(obj) {
        const route = new BusRoute(obj);
        return route;
    }

    async deserialise(routes) {
        console.log('Deserialising ROutes...');
        const time = Date.now();
        routes = await super.deserialise(routes);
        const stopsById = await getAllPromise(stopModelData);
        if (!stopsById)
            throw new Error('Stops must be fetched before ' + this.name);
        const result = routes.map((route) => {
            route.setBaseStops(stopsById);
            delete route.warnings;
            delete route.patternChecked;
            route.waypoints.forEach(wp => {
                delete wp.x;
                delete wp.y;
            });
            return route;
        });

        console.log(`Routes deserialised. Elapsed: ${Date.now() - time}ms`);
        return result;
    }

    serialise(route) {
        const {
            userId,
            charter,
            routeId,
            published,
            scheduleId,
            colour,
            direction,
            routeLogo,
            routeType,
            routeNumber,
            driverShift,
            vehicleShift,
            routeName,
            routeLabel,
            routeDescription,
            routeDetails,
            routeHeadsign,
            waypoints,
            stopTimes,
            startTime,
            services,
            author,
            createdAt,
            lastEditor,
            approvedBy,
            approvedAt,
            publishedBy,
            publishedAt,
            unpublishedBy,
            unpublishedAt,
            contractId,
            updatedAt,
            warnings,
            distance,
            duration,
            returnDistance,
            returnDuration,
            startDepot,
            startDepotDistance,
            startDepotDuration,
            endDepot,
            endDepotDistance,
            endDepotDuration,
            vehicleTypes,
            vehicleTypeQuantities,
            passengerCount,
            returnEnabled,
            dates
        } = route;

        let routeToUpdate = {
            userId,
            charter,
            routeId,
            published,
            scheduleId,
            colour,
            direction,
            routeLogo,
            routeType,
            routeNumber,
            driverShift,
            vehicleShift,
            routeName,
            routeLabel,
            routeDescription,
            routeDetails,
            routeHeadsign,
            waypoints,
            stopTimes,
            startTime,
            services,
            author,
            createdAt,
            lastEditor,
            approvedBy,
            approvedAt,
            publishedBy,
            publishedAt,
            unpublishedBy,
            unpublishedAt,
            contractId,
            updatedAt,
            warnings,
            distance,
            duration,
            returnDistance,
            returnDuration,
            startDepot,
            startDepotDistance,
            startDepotDuration,
            endDepot,
            endDepotDistance,
            endDepotDuration,
            vehicleTypes,
            vehicleTypeQuantities,
            passengerCount,
            returnEnabled,
            dates: dates?.map(d => typeof d === 'string' ? d :  normaliseDate(d)?.format(DATE_STRING))
        };
        routeToUpdate.routeId =
            routeToUpdate.routeId === '_' ? ulid() : routeToUpdate.routeId;

        routeToUpdate.services?.forEach(trip => {
            delete trip.transfersFrom;
            delete trip.transfersTo;
            delete trip.stops;
            delete trip.route;
            trip.dates = trip.dates?.map(d => typeof d === 'string' ? d : d?.format ? d.format(DATE_STRING) : d);
            trip.stopTimes = trip.stopTimes?.map(st => ({
                ...st,
                date: typeof st.date === 'string' ? st.date : st.date?.format ? st.date.format(DATE_STRING) : st.date
            }));
        });

        routeToUpdate.createdAt = routeToUpdate.createdAt || Date.now();
        routeToUpdate.stopTimes?.forEach((s) => {
            delete s.avgSpd;
            delete s.distance;
            delete s.linkTime;
            delete s.transfersFrom;
            delete s.transfersTo;
            s.date = typeof s.date === 'string' ? s.date : s.date?.format ? s.date?.format(DATE_STRING) : s.date;
            Object.keys(s).forEach((key) => {
                if (s[key] === null) {
                    delete s[key];
                }
            });
        });

        Object.keys(routeToUpdate).forEach((key) => {
            if (routeToUpdate[key] === null) {
                delete routeToUpdate[key];
            }
        });
        routeToUpdate.waypoints.forEach(wp => {
            delete wp.x;
            delete wp.y;
        });
        return routeToUpdate;
    }
}

class ScheduleModelData extends ModelData {
    constructor() {
        super({
            name: 'Schedule',
            path: '/schedules',
            api: 'schedules'
        });
    }

    getId(obj) {
        if (obj) {
            return obj.scheduleId;
        }
    }

    setId(obj, id = ulid()) {
        obj.scheduleId = id;
    }

    create(obj, all) {
        return new Schedule(obj);
    }

    async copy(obj) {
        const copy = this.create(obj);
        this.setId(copy);
        return await this.save(copy);
    }

    async deserialise(objs) {
        console.log('Deserialising Schedules...');
        objs = await super.deserialise(objs);
        const allPublicSchedules = await getAllPromise(publicScheduleModelData);
        if (!allPublicSchedules)
            throw new Error('Public Schedules must be fetched before ' + this.name);
        let schedules = this.db ? await this.db.toArray() : values(this.data) || objs;
        schedules = {
            ...allPublicSchedules,
            ...keyBy(schedules.map(s => new Schedule(s)), 'scheduleId')
        };
        return objs.map((obj) => {
            obj.setSubSchedules(schedules);
            return obj;
        });
    }

    serialise(obj) {
        return Schedule.clone(obj).toJson();
    }
}

class PublicScheduleModelData extends ModelData {
    constructor() {
        super({
            name: 'PublicSchedule',
            path: '/public-schedules',
            api: 'public_schedules',
            idName: 'scheduleId',
            ws: false
        });
    }

    getId(obj) {
        if (obj) {
            return obj.scheduleId;
        }
    }

    setId(obj, id = ulid()) {
        obj.scheduleId = id;
    }

    create(obj, all) {
        return new Schedule(obj);
    }

    async deserialise(objs) {
        console.log('Deserialising PublicSchedules...');
        objs = await super.deserialise(objs);
        let schedules = this.db ? await this.db.toArray() : values(this.data) || objs;
        schedules = keyBy(schedules.map(s => new Schedule(s)), 'scheduleId');
        return objs.map((obj) => {
            obj.setSubSchedules(schedules);
            return obj;
        });
    }

    serialise(obj) {
        return Schedule.clone(obj).toJson();
    }
}

class VehicleModelData extends ModelData {
    constructor() {
        super({name: 'Vehicle', api: 'vehicle'});
    }

    getId(obj) {
        if (obj) {
            return obj.vehicleId;
        }
    }

    setId(obj, id = ulid()) {
        obj.vehicleId = id;
    }

    create(obj) {
        return new Vehicle(obj);
    }

    async deserialise(objs) {
        objs = await super.deserialise(objs);
        const allVehicleTypes = await getAllPromise(vehicleTypeModelData);
        if (!allVehicleTypes)
            throw new Error('Vehicel Types must be fetched before ' + this.name);

        objs.forEach(vehicle => vehicle.vehicleType = allVehicleTypes[vehicle.vehicleTypeId]);
        return objs;
    }

    serialise(vehicle) {
        const {
            userId,
            vehicleId,
            author,
            createdAt,
            lastEditor,
            updatedAt,
            vehicleName,
            vehicleTypeId,
            vehicleNumber,
            vehicleRego,
            vehicleStatus,
            fuelType,
            capacity,
            maxDistance,
            rate,
            tankage
        } = vehicle;
        return {
            userId,
            vehicleId,
            author,
            createdAt,
            lastEditor,
            updatedAt,
            vehicleName,
            vehicleTypeId,
            vehicleNumber,
            vehicleRego,
            vehicleStatus,
            fuelType,
            capacity,
            maxDistance,
            rate,
            tankage
        };
    }

}

class VehicleTypeModelData extends ModelData {
    constructor() {
        super({name: 'VehicleType', api: 'vehicleType'});
    }

    getId(obj) {
        if (obj) {
            return obj.vehicleTypeId;
        }
    }

    setId(obj, id = ulid()) {
        obj.vehicleTypeId = id;
    }

    create(obj) {
        return new VehicleType(obj);
    }

}

class DriverModelData extends ModelData {
    constructor() {
        super({name: 'Driver', api: 'driver'});
    }

    getId(obj) {
        if (obj) {
            return obj.driverId;
        }
    }

    setId(obj, id = ulid()) {
        obj.driverId = id;
    }

    create(obj) {
        return new Driver(obj);
    }
}

class DriverShiftModelData extends ModelData {
    constructor() {
        super({
            name: 'DriverShift',
            idName: 'shiftId',
            api: 'shift',
            path: '/shifts/driver',
        });
    }

    create(obj) {
        return new Shift(obj);
    }
}

class VehicleShiftModelData extends ModelData {
    constructor() {
        super({
            name: 'VehicleShift',
            idName: 'shiftId',
            api: 'shift',
            path: '/shifts/vehicle',
        });
    }

    create(obj) {
        return new Shift(obj);
    }
}

class ShiftBatModelData extends ModelData {
    constructor() {
        super({
            name: 'ShiftBat',
            api: 'shift_bat',
            path: '/shiftBats',
        });
    }

    getId(obj) {
        if (obj) {
            return obj.shiftBatId;
        }
    }

    setId(obj, id = ulid()) {
        obj.shiftBatId = id;
    }

    create(obj) {
        return new ShiftBat(obj);
    }

    validateShiftBatNumber(shiftBatNumber, shiftBat) {
        return values(this.data)?.find(sb => sb.shiftBatId !== shiftBat?.shiftBatId && sb.charter === false && sb.shiftBatNumber === shiftBatNumber);
    }


    async load(apiKey, id) {
        scheduleModelData.localCache = true;
        transferModelData.localCache = true;
        stopModelData.localCache = true;
        routeModelData.localCache = true;
        deadrunModelData.localCache = true;
        shiftBatModelData.localCache = true;
        charterModelData.localCache = true;
        customerModelData.localCache = true;


        const data = await API.get('routes', `/viewShiftBat/${apiKey}/${id}`);
        await publicScheduleModelData.set(data.publicSchedules);
        publicScheduleModelData._loadResolver();
        await scheduleModelData.set(data.schedules);
        scheduleModelData._loadResolver();
        await stopModelData.set(data.stops);
        stopModelData._loadResolver();
        await routeModelData.set(data.routes);
        routeModelData._loadResolver();
        await transferModelData.set(data.transfers);
        transferModelData._loadResolver();
        await deadrunModelData.set(data.deadruns);
        deadrunModelData._loadResolver?.();
        await shiftBatModelData.set(data.shiftBat);
        shiftBatModelData._loadResolver();
        await vehicleTypeModelData.set([]);
        vehicleTypeModelData._loadResolver();
        await vehicleModelData.set([]);
        vehicleModelData._loadResolver();

        await customerModelData.set(data.customers);
        customerModelData._loadResolver();
        await charterModelData.set(data.charters);
        charterModelData._loadResolver();

        // shiftBatModelData.data = {[id]: (await shiftBatModelData.deserialise([new ShiftBat(data.shiftBat)]))[0]}
        return shiftBatModelData.data[id];

        //         const sb = await this.fetch(id)
        //
        //         const scheduleIds = sb.scheduleIds
        //         await scheduleModelData.fetchAll();
        //         await transferModelData.fetchAll();
        //         const stopIds = sb.rows.filter(r => r.type === ShiftBatRowType.stop).map(r => r.stopId)
        //         const stops = keyBy(await Promise.all(stopIds.map(async sId => await stopModelData.fetch(sId))), 'stopId')
        // // const schedules = keyBy(await Promise.all(scheduleIds.map(async sId => await scheduleModelData.fetch(sId))), 'scheduleId')
        //         const routeIds = sb.rows.filter(r => r.type === ShiftBatRowType.service).map(r => r.routeId)
        //         const routes = keyBy(await Promise.all(routeIds.map(async sId => await routeModelData.fetch(sId))), 'routeId')
        //         routes.forEach(r => r.stops.forEach(s => stops[s.stopId] = s))
        //         const deadrunIds = sb.rows.filter(r => r.type === ShiftBatRowType.dead).map(r => r.routeId)
        //         const deadruns = keyBy(await Promise.all(deadrunIds.map(async dId => await deadrunModelData.fetchAllIdBeginningWith(dId))), 'deadrunId')
        //
        //
        //
    }

    async deserialise(objs) {
        console.log('Deserialising ShiftBats...');
        const time = Date.now();
        objs = await super.deserialise(objs);

        const allStops = await getAllPromise(stopModelData);
        if (!allStops)
            throw new Error('Stops must be fetched before ' + this.name);

        const allTransfers = await getAllPromise(transferModelData);
        if (!allTransfers)
            throw new Error('Transfers must be fetched before ' + this.name);

        const allRoutes = await getAllPromise(routeModelData);
        if (!allRoutes)
            throw new Error('ROutes must be fetched before ' + this.name);

        const allDeadruns = await getAllPromise(deadrunModelData);
        if (!allDeadruns)
            throw new Error('Deadruns must be fetched before ' + this.name);

        try {
            const result = await Promise.all(objs.map(async obj => {
                obj.published = Number.isFinite(obj.published) ? obj.published : -1;
                obj.effectiveDate = dayjs(obj.effectiveDate);
                try {
                    await obj.updateRows({
                        apiKey: this.apiKey,
                        allStops,
                        allRoutes,
                        allTransfers,
                        allDeadruns,
                        opts: {directions: false, createNew: false}
                    });
                    // Find any newly created deadruns
                    // const dirtyDeadrunRows = obj.rows.filter(r => r.type === ShiftBatRowType.dead && r.route?.routeId === '_')
                    // // // Save those deadruns
                    // await Promise.all(dirtyDeadrunRows.map(async r => {
                    //     if(!r.description && !r.route?.routeDetails) {
                    //         r.route.routeDetails = await r.buildDirections();
                    //         r.description = abbreviate(r.route.routeDetails);
                    //     }
                    //     const deadrun = await deadrunModelData.save(r.route);
                    //     r.routeId = deadrun.routeId
                    // }));
                    // // Save the shift bat with the new deadrun routeId
                    // if(dirtyDeadrunRows.length) {
                    //     obj = await shiftBatModelData.save(obj);
                    // }
                } catch(e) {
                    console.log('Failed to update shift bat: ', e);
                    return null;
                }
                return obj;
            }));
            console.log(`ShiftBats deserialised. Elapsed: ${Date.now() - time}ms`);
            return result.filter(Boolean);
        } catch (e) {
            console.log('Failed to deserialise ShiftBats: ', e)
            return [];
        }
    }
}

class OperatorModelData extends ModelData {
    constructor() {
        super({
            name: 'Operator',
            api: 'operators',
            path: '/operators',
        });
    }

    getId(obj) {
        if (obj) {
            return obj.operatorId;
        }
    }

    setId(obj, id) {
    }

    create(obj) {
        if (!obj?.features) {
            obj.features = {};
        }
        obj.features = new Features(obj.features);
        return obj;
    }

    async deserialise(objs) {
        objs = await super.deserialise(objs);
        return objs.map((obj) => {
            if (!obj?.features) {
                throw new Error('No Operator ' + this.apiKey);
            }
            obj.features = new Features(obj.features);
            if (obj.operatorAddress) {
                obj.operatorAddress = new Address(obj.operatorAddress);
            }
            if (obj.operatorPostalAddress) {
                obj.operatorPostalAddress = new Address(obj.operatorPostalAddress);
            }
            return obj;
        });
    }

    async get(apiKey) {

        if (!this.localCacheInitialised) {
            await this.init();
        }
        const start = Date.now();
        let obj = await this.db.get(apiKey);
        if (!obj) {
            obj = await this.fetch(apiKey);
        }
        [obj] = await this.deserialise(obj);
        logger.warn(
            `Elapsed time for get ${this.name}: ${toHrsMinsSecs(
                (Date.now() - start) / 1000
            )}`
        );
        return this.create(obj);
    }

    async _create(obj) {
        obj.regionId = obj.regionId || obj.operatorId;
        obj.orgId = obj.orgId || obj.companyId;
        return await this._save(obj);
    }

    async _save(obj) {
        let operator = obj;
        if (Array.isArray(operator)) {
            operator = operator[0];
        }
        operator.regionId = operator.regionId || operator.operatorId;
        operator.orgId = operator.orgId || operator.companyId;
        config.operator = operator;
        await API.put('operators', `/operators/${operator.operatorId}`, {
            body: operator
        });
        return this.create(operator);
    }

    async deserialise(objs) {
        objs = await super.deserialise(objs);
        return objs.map((obj) => {
            obj.regionId = obj.regionId || obj.operatorId;
            obj.orgId = obj.orgId || obj.companyId;
            return this.create(obj);
        });
    }
}

class TransferModelData extends ModelData {
    constructor() {
        super({
            name: 'Transfer',
            api: 'transfer',
        });
    }

    // async setLoaded() {
    //     await super.setLoaded();
    //     const transfers = await this.db.toArray()
    //     transfers.forEach(transfer => {
    //         transfer._trx = []
    //         transfers.filter(t => !t.invalid && t !== transfer).forEach(leg2 => {
    //             if (leg2.fromStopId !== transfer.fromStopId &&
    //                 leg2.fromRouteNumber === transfer.toRouteNumber &&
    //                 leg2.time >= transfer.time &&
    //                 intersection(leg2.scheduleIds, transfer.scheduleIds).length
    //             ) {
    //                 leg2 = cloneDeep(leg2);
    //                 delete leg2._trx;
    //                 transfer._trx.push(leg2);
    //             }
    //         })
    //     })
    // }

    // loaded() {
    //     super.loaded()
    //
    //     transfers.forEach(transfer => {
    //         transfer.schedule = schedules[transfer.scheduleId]
    //         if (typeof transfer.waypoints === 'string') {
    //             transfer.waypoints = JSON.parse(transfer.waypoints);
    //             transfer.distance = parseInt(transfer.distance);
    //             transfer.duration = parseInt(transfer.duration);
    //             transfer.time = parseInt(transfer.time);
    //             transfer.window = parseInt(transfer.window);
    //         }
    //         delete transfer.fromTripId
    //         delete transfer.warning;
    //         //distance
    //         //duration
    //         //time
    //         //window
    //         const fromLog = transfer.fromRouteNumber === '571'
    //
    //         fromLog && logger.info('The TRANSFER: ', transfer)
    //
    //         const fromStop = allStops[transfer.fromStopId];
    //         fromLog && logger.info("From STOP: ", fromStop);
    //         if (fromStop) {
    //             fromLog && logger.info("From stop: ", fromStop.stopName);
    //             const fromStopPassingTimes = fromStop.getPassingTimes(r => r.routeNumber === transfer.fromRouteNumber, transfer)
    //                 .filter(fromStopPassingTime => fromStopPassingTime.scheduleIds.some(sId => schedules[sId] && (!schedules[sId].isFuture() && !schedules[sId].isObsolete())));
    //             fromLog && logger.info('FROM PASSING TIME: ', fromStopPassingTimes)
    //
    //             let fromStopPassingTime = null
    //             if (!fromStopPassingTimes.length) {
    //                 logger.warn('NO TRIPS FOR Transfer fromRoute %s @ %s.', transfer.fromRouteNumber, fromStop.stopName)
    //             } else {
    //                 fromStopPassingTime = fromStopPassingTimes[0]
    //             }
    //             if (fromStopPassingTimes.length > 1) {
    //                 transfer.warning = `MULTIPLE TRIPS FOR Transfer from route ${transfer.fromRouteNumber} @ ${fromStop.stopName}.`
    //                 logger.warn('MULTIPLE TRIPS FOR Transfer fromRoute %s @ %s. Using first one.', transfer.fromRouteNumber, fromStop.stopName)
    //             }
    //             transfer.fromTripId = fromStopPassingTime?.tripId;
    //             transfer.fromScheduleIds = fromStopPassingTime?.scheduleIds;
    //             transfer.fromRouteId = fromStopPassingTime?.routeId
    //             transfer.fromPassingTime = fromStopPassingTime?.passingTime
    //             transfer.fromStopPassingTimes = fromStopPassingTimes
    //         } else {
    //             logger.warn('Could not find from stop with id ', transfer.fromStopId)
    //             transfer.warning = `From stop does not exist.`
    //         }
    //
    //         const toLog = false;//transfer.toRouteNumber === 'S266'
    //         const toStop = allStops[transfer.toStopId]
    //         toLog && logger.info("To STOP: ", toStop);
    //         if (toStop) {
    //             toLog && logger.info("To stop: ", toStop.stopName)
    //             const toStopPassingTimes = toStop.getPassingTimes(r => r.routeNumber === transfer.toRouteNumber, transfer)
    //                 .filter(pt => {
    //                     toLog && logger.info(pt, transfer.fromPassingTime)
    //                     return !isFinite(transfer.fromPassingTime) || pt.passingTime >= transfer.fromPassingTime
    //                 }).filter(toStopPassingTime => toStopPassingTime.scheduleIds.some(sId => schedules[sId] && (!schedules[sId].isFuture() && !schedules[sId].isObsolete())));
    //             toLog && logger.info('TO PASSING TIME: ', toStopPassingTimes)
    //
    //             let toStopPassingTime = null
    //
    //             if (!toStopPassingTimes.length) {
    //                 logger.warn('NO TRIPS FOR Transfer toRoute %s @ %s.', transfer.toRouteNumber, toStop.stopName)
    //             } else {
    //                 toStopPassingTime = toStopPassingTimes[0]
    //             }
    //             if (toStopPassingTimes.length > 1) {
    //                 transfer.warning = `MULTIPLE TRIPS FOR Transfer to route ${transfer.toRouteNumber} @ ${fromStop.stopName}.`;
    //                 logger.warn('MULTIPLE TRIPS FOR Transfer toRoute %s @ %s. Using first one.', transfer.toRouteNumber, toStop.stopName)
    //             }
    //
    //             transfer.toTripId = toStopPassingTime?.tripId;
    //             transfer.toScheduleIds = toStopPassingTime?.scheduleIds;
    //             transfer.toRouteId = toStopPassingTime?.routeId
    //             transfer.toPassingTime = toStopPassingTime?.passingTime
    //             transfer.toStopPassingTimes = toStopPassingTimes
    //         } else {
    //             logger.info('Could not find to stop with id ', transfer.toStopId)
    //             transfer.warning = `To stop does not exist.`
    //         }
    //
    //         if (!transfer.fromTripId) {
    //             transfer.invalid = true;
    //             transfer.warning = `Transfer from ${transfer.fromRouteNumber} @ ${toTime(transfer.time, false)} cannot be mapped to a trip.`
    //         } else if (fromLog) {
    //             logger.info(transfer.fromTripId, transfer.fromRouteId)
    //         }
    //
    //         if (!transfer.toTripId) {
    //             transfer.warning = transfer.invalid ? `Routes ${transfer.fromRouteNumber} -> ${transfer.toRouteNumber} @ ${toTime(transfer.time, false, transfer.window)} do not map to any trips.` : `Transfer to ${transfer.toRouteNumber} @ ${toTime(transfer.time, false, transfer.window)} cannot be mapped to a trip.`
    //             transfer.invalid = true;
    //         } else if (fromLog) {
    //             logger.info(transfer.toTripId, transfer.toRouteId)
    //         }
    //
    //         if (transfer.fromScheduleIds?.length && transfer.toScheduleIds?.length) {
    //             transfer.scheduleIds = intersection(transfer.fromScheduleIds, transfer.toScheduleIds)
    //         }
    //         if (transfer.warning) {
    //             logger.warn(transfer.warning);
    //         }
    //
    //     });
    //
    //     transfers = transfers.map(t => new Transfer(t))
    //     transfers.filter(t => !t.invalid).forEach((leg1) => {
    //         leg1._trx = []
    //         transfers.filter(t => !t.invalid && t !== leg1).forEach(leg2 => {
    //             if (leg2.fromStopId !== leg1.fromStopId &&
    //                 leg2.fromRouteNumber === leg1.toRouteNumber &&
    //                 leg2.time >= leg1.time &&
    //                 intersection(leg2.scheduleIds, leg1.scheduleIds).length
    //             ) {
    //                 leg2 = cloneDeep(leg2);
    //                 delete leg2._trx;
    //                 leg1._trx.push(leg2);
    //             }
    //         })
    //     });
    // }

    // async fetchAll() {
    //     logger.debug(`Fetching Transfers for ${this.apiKey}...`);
    //
    //     let fetched = await getTransfers(this.apiKey, this.fetchOptions.allSchedules, this.fetchOptions.allStops);
    //     logger.debug(`Fetched ${fetched.length} ${this.name}'s.`)
    //
    //     await this.db.bulkPut(fetched)
    //
    //     fetched = fetched.map(s => {
    //         return this.create(s);
    //     });
    //     if (this.localCache) {
    //         this.data = keyBy(fetched.map(obj => this.create(obj)), this.idName)
    //     }
    //     return fetched;
    // }

    getId(obj) {
        if (obj) {
            return obj.transferId;
        }
    }

    setId(obj, id = ulid()) {
        obj.transferId = id;
    }

    async deserialise(transfers) {
        const time = Date.now();
        console.log('Deserialising Transfers...');
        transfers = await super.deserialise(transfers);
        const allStops = await getAllPromise(stopModelData);
        if (!allStops)
            throw new Error('Stops must be fetched before ' + this.name);
        const schedules = await getAllPromise(scheduleModelData);
        if (!schedules)
            throw new Error('Schedules must be fetched before ' + this.name);
        transfers = transfers.map((transfer) => {
            transfer.schedule = schedules[transfer.scheduleId];
            if (typeof transfer.waypoints === 'string') {
                transfer.waypoints = JSON.parse(transfer.waypoints);
                transfer.distance = parseInt(transfer.distance);
                transfer.duration = parseInt(transfer.duration);
                transfer.time = parseInt(transfer.time);
                transfer.window = parseInt(transfer.window);
            }


            const fromStop = allStops[transfer.fromStopId];
            const fromRoute = fromStop?.routes[fromStop.routes.findIndex(r => r.routeId === transfer.fromRouteId)];
            const fromTrip = fromRoute?.services[transfer.fromTripId];//find(fromRoute.passingTimes, ['tripId', transfer.fromTripId])
            const arriveStopTime = find(fromTrip?.passingTimes, ['stopTimeId', transfer.fromStopTimeId]);
            transfer.arriveSecs = arriveStopTime?.arriveSecs;
            transfer.time = arriveStopTime?.arriveSecs;
            transfer.fromRouteColour = fromRoute?.colour;

            const toStop = allStops[transfer.toStopId];
            const toRoute = toStop?.routes[toStop.routes.findIndex(r => r.routeId === transfer.toRouteId)];
            const toTrip = toRoute?.services[transfer.toTripId];// find(toRoute.passingTimes, ['tripId', transfer.toTripId])
            const departStopTime = find(toTrip?.passingTimes, ['stopTimeId', transfer.toStopTimeId]);
            transfer.departSecs = departStopTime?.departSecs;
            transfer.window = departStopTime?.departSecs - arriveStopTime?.arriveSecs + 60;
            transfer.toRouteColour = toRoute?.colour;

            transfer.warnings = [];
            if (!transfer.fromRouteId || !transfer.fromTripId || !transfer.fromStopTimeId) {
                const missing = [];
                !transfer.fromRouteId && missing.push('route');
                !transfer.fromTripId && missing.push('trip');
                !transfer.fromStopTimeId && missing.push('stop time');
                transfer.warnings.push(`The transfer cannot be mapped to a setdown service. Missing: ${missing.join(', ')} (REF-026)`);
            }
            if (!transfer.toRouteId || !transfer.toTripId || !transfer.toStopTimeId) {
                const missing = [];
                !transfer.toRouteId && missing.push('route');
                !transfer.toTripId && missing.push('trip');
                !transfer.toStopTimeId && missing.push('stop time');
                transfer.warnings.push(`The transfer cannot be mapped to a pickup service. Missing: ${missing.join(', ')} (REF-027)`);
            }
            if (transfer.departSecs < transfer.arriveSecs) {
                transfer.warnings.push(`Invalid transfer times. Setdown service  ${transfer.fromRouteNumber} arrives ${toHrsMinsSecs(transfer.arriveSecs - transfer.departSecs, false, true)} after pickup service ${transfer.toRouteNumber} (REF-028)`);
            }

            transfer.invalid = !!transfer.warnings.length;
            // transfer.invalid = !transfer.fromTripId || !transfer.fromStopTimeId || !transfer.toTripId || !transfer.toStopTimeId || transfer.departSecs < transfer.arriveSecs;

            // delete transfer.fromTripId;
            // delete transfer.warning;
            // //distance
            // //duration
            // //time
            // //window
            // const fromLog = transfer.fromRouteNumber === "571";
            //
            // fromLog && logger.info("The TRANSFER: ", transfer);
            //
            // const fromStop = allStops[transfer.fromStopId];
            // fromLog && logger.info("From STOP: ", fromStop);
            // if (fromStop) {
            //     fromLog && logger.info("From stop: ", fromStop.stopName);
            //     const fromStopPassingTimes = fromStop
            //         .getPassingTimes(
            //             (r) => r.routeNumber === transfer.fromRouteNumber,
            //             transfer
            //         )
            //         .filter((fromStopPassingTime) =>
            //             fromStopPassingTime.scheduleIds.some(
            //                 (sId) =>
            //                     schedules[sId] &&
            //                     !schedules[sId].isFuture() &&
            //                     !schedules[sId].isObsolete()
            //             )
            //         );
            //     fromLog &&
            //     logger.info("FROM PASSING TIME: ", fromStopPassingTimes);
            //
            //     let fromStopPassingTime = null;
            //     if (!fromStopPassingTimes.length) {
            //         logger.warn(
            //             "NO TRIPS FOR Transfer fromRoute %s @ %s.",
            //             transfer.fromRouteNumber,
            //             fromStop.stopName
            //         );
            //     } else {
            //         fromStopPassingTime = fromStopPassingTimes[0];
            //     }
            //     if (fromStopPassingTimes.length > 1) {
            //         transfer.warning = `MULTIPLE TRIPS FOR Transfer from route ${transfer.fromRouteNumber} @ ${fromStop.stopName}.`;
            //         logger.warn(
            //             "MULTIPLE TRIPS FOR Transfer fromRoute %s @ %s. Using first one.",
            //             transfer.fromRouteNumber,
            //             fromStop.stopName
            //         );
            //     }
            //     transfer.fromTripId = fromStopPassingTime?.tripId;
            //     transfer.fromScheduleIds = fromStopPassingTime?.scheduleIds;
            //     transfer.fromRouteId = fromStopPassingTime?.routeId;
            //     transfer.fromPassingTime = fromStopPassingTime?.passingTime;
            //     transfer.fromStopPassingTimes = fromStopPassingTimes;
            // } else {
            //     logger.warn(
            //         "Could not find from stop with id ",
            //         transfer.fromStopId
            //     );
            //     transfer.warning = `From stop does not exist.`;
            // }
            //
            // const toLog = false; //transfer.toRouteNumber === 'S266'
            // const toStop = allStops[transfer.toStopId];
            // toLog && logger.info("To STOP: ", toStop);
            // if (toStop) {
            //     toLog && logger.info("To stop: ", toStop.stopName);
            //     const toStopPassingTimes = toStop
            //         .getPassingTimes(
            //             (r) => r.routeNumber === transfer.toRouteNumber,
            //             transfer
            //         )
            //         .filter((pt) => {
            //             toLog && logger.info(pt, transfer.fromPassingTime);
            //             return (
            //                 !isFinite(transfer.fromPassingTime) ||
            //                 pt.passingTime >= transfer.fromPassingTime
            //             );
            //         })
            //         .filter((toStopPassingTime) =>
            //             toStopPassingTime.scheduleIds.some(
            //                 (sId) =>
            //                     schedules[sId] &&
            //                     !schedules[sId].isFuture() &&
            //                     !schedules[sId].isObsolete()
            //             )
            //         );
            //     toLog && logger.info("TO PASSING TIME: ", toStopPassingTimes);
            //
            //     let toStopPassingTime = null;
            //
            //     if (!toStopPassingTimes.length) {
            //         logger.warn(
            //             "NO TRIPS FOR Transfer toRoute %s @ %s.",
            //             transfer.toRouteNumber,
            //             toStop.stopName
            //         );
            //     } else {
            //         toStopPassingTime = toStopPassingTimes[0];
            //     }
            //     if (toStopPassingTimes.length > 1) {
            //         transfer.warning = `MULTIPLE TRIPS FOR Transfer to route ${transfer.toRouteNumber} @ ${fromStop.stopName}.`;
            //         logger.warn(
            //             "MULTIPLE TRIPS FOR Transfer toRoute %s @ %s. Using first one.",
            //             transfer.toRouteNumber,
            //             toStop.stopName
            //         );
            //     }
            //
            //     transfer.toTripId = toStopPassingTime?.tripId;
            //     transfer.toScheduleIds = toStopPassingTime?.scheduleIds;
            //     transfer.toRouteId = toStopPassingTime?.routeId;
            //     transfer.toPassingTime = toStopPassingTime?.passingTime;
            //     transfer.toStopPassingTimes = toStopPassingTimes;
            // } else {
            //     logger.info(
            //         "Could not find to stop with id ",
            //         transfer.toStopId
            //     );
            //     transfer.warning = `To stop does not exist.`;
            // }
            //
            // if (!transfer.fromTripId) {
            //     transfer.invalid = true;
            //     transfer.warning = `Transfer from ${
            //         transfer.fromRouteNumber
            //     } @ ${toTime(
            //         transfer.time,
            //         false
            //     )} cannot be mapped to a trip.`;
            // } else if (fromLog) {
            //     logger.info(transfer.fromTripId, transfer.fromRouteId);
            // }
            //
            // if (!transfer.toTripId) {
            //     transfer.warning = transfer.invalid
            //         ? `Routes ${transfer.fromRouteNumber} -> ${
            //             transfer.toRouteNumber
            //         } @ ${toTime(
            //             transfer.time,
            //             false,
            //             transfer.window
            //         )} do not map to any trips.`
            //         : `Transfer to ${transfer.toRouteNumber} @ ${toTime(
            //             transfer.time,
            //             false,
            //             transfer.window
            //         )} cannot be mapped to a trip.`;
            //     transfer.invalid = true;
            // } else if (fromLog) {
            //     logger.info(transfer.toTripId, transfer.toRouteId);
            // }
            //
            // if (
            //     transfer.fromScheduleIds?.length &&
            //     transfer.toScheduleIds?.length
            // ) {
            //     transfer.scheduleIds = intersection(
            //         transfer.fromScheduleIds,
            //         transfer.toScheduleIds
            //     );
            // }
            // if (transfer.warning) {
            //     logger.warn(transfer.warning);
            // }
            return transfer;
        });

        transfers.forEach((leg1) => {
            leg1._trx = [];
            transfers.filter(t => !t.invalid && t !== leg1).forEach(leg2 => {
                if (leg2.fromStopId !== leg1.fromStopId &&
                    leg2.fromRouteId === leg1.toRouteId &&
                    leg2.fromTripId === leg1.toTripId &&
                    leg2.time >= leg1.time
                ) {
                    leg2 = cloneDeep(leg2);
                    delete leg2._trx;
                    leg1._trx.push(leg2);
                }
            });
        });

        console.log('Deserialising Transfers complete in ' + (Date.now() - time) + 'ms');

        return transfers;
    }

    create(transfer) {
        return new Transfer(transfer);
    }
}

export class DeadrunModelData extends ModelData {
    static createId(obj) {
        return `${obj.startStopId}#${obj.endStopId}#${
            obj.routeName?.length ? obj.routeName : ''
        }`;
    }

    static getStartEndStopIds(id) {
        return id.split('#').slice(0, 2);
    }

    constructor() {
        super({
            name: 'Deadrun',
            api: 'deadrun',
            idName: 'routeId'
        });
    }

    async fetch({startStopId, endStopId}) {
        const deadrun = await this.fetchAllIdBeginningWith(
            `${startStopId}#${endStopId}#`
        );
        return this.deserialise(deadrun);
    }

    setId(obj) {
        obj.routeId = DeadrunModelData.createId(obj);
    }

    create(obj) {
        return new Deadrun(obj);
    }

    createSubKey(obj) {
        let [startStopId, endStopId] = obj.routeId.split('#');
        startStopId = startStopId || obj.startStopId;
        endStopId = endStopId || obj.endStopId;
        return `${startStopId}#${endStopId}`;
    }

    keyDataBy(objs) {
        objs = Array.isArray(objs) ? objs : [objs];
        // HACK: There is a data issue where some Deadruns at some point had a routeId that didn't match the route. See BUS-3211
        // objs = objs.map(obj => {
        //     if (typeof obj.routeId === 'string') {
        //         const parts = obj.routeId.split('#');
        //         if (parts.length === 3) {
        //             obj.routeName = parts[2];
        //         }
        //     }
        //     return obj;
        // })
        return objs.reduce((result, obj) => {
            const subKey = this.createSubKey(obj);
            if (!result[subKey]) {
                result[subKey] = [];
            }
            const index = result[subKey].findIndex(o => o.routeId === obj.routeId);
            if(index >= 0) {
                result[subKey][index] = obj;
            } else {
                result[subKey].push(obj);
            }
            result[obj.routeId] = obj;
            return result;
        }, {});
    }

    updateKeyDataBy(objs) {
        objs = Array.isArray(objs) ? objs : [objs];
        // HACK: There is a data issue where some Deadruns at some point had a routeId that didn't match the route. See BUS-3211
        // objs = objs.map(obj => {
        //     if (typeof obj.routeId === 'string') {
        //         const parts = obj.routeId.split('#');
        //         if (parts.length === 3) {
        //             obj.routeName = parts[2];
        //         }
        //     }
        //     return obj;
        // })
        objs.forEach((obj) => {
            const subKey = this.createSubKey(obj);
            if (!this.data[subKey]) {
                this.data[subKey] = [];
            }
            const index = this.data[subKey].findIndex(o => o.routeId === obj.routeId);
            if(index >= 0) {
                this.data[subKey][index] = obj;
            } else {
                this.data[subKey].push(obj);
            }
            this.data[obj.routeId] = obj;
        });
    }

    /**
     * DeadrunModelData getAll returns a map of Deadruns mapped by {[startStopId#endStopId]: [Deadrund], [routeId]: Deadrun}
     * See keyDataBy function
     */
    async getAll(opts) {
        let all = (await super.getAll(opts));
        const toSave = Object.values(all).filter(obj => !Array.isArray(obj) && typeof obj.routeDetails !== 'string');

        await Promise.all(toSave.map(async obj => {
            await obj.buildDirections();
            await deadrunModelData.save(obj, {silent: true});
        }));
        return all;
    }

    serialise(obj) {
        return obj.toJson ? obj.toJson() : new Deadrun(obj).toJson();
    }

    async deserialise(objs) {
        console.log('Deserialising Deadruns...');
        const time = Date.now();
        objs = await super.deserialise(objs);
        const stopsById = await getAllPromise(stopModelData);
        if (!stopsById)
            throw new Error('Stops must be fetched before ' + this.name);
        const results = objs.map((obj) => {
            if(!obj.startStopId || !obj.endStopId) {
                const [startStopId, endStopId] = DeadrunModelData.getStartEndStopIds(obj.routeId)
                obj.startStopId = startStopId;
                obj.endStopId = endStopId;
            }
            obj.start = stopsById[obj.startStopId];
            obj.end = stopsById[obj.endStopId];
            obj.createStops();
            obj.calculateDistances({setDuration: false, setDistance: true});
            return obj;
        });
        console.log(`Deadruns deserialised. Elapsed: ${Date.now() - time}ms`);
        return results; 
    }
}

class LogModelData extends ModelData {
    constructor({type, name = 'Log', api = 'log', idName = 'logId'}) {
        super({name, api, idName, ws: true});
        this.type = type;
    }

    async fetchAll({asArray = false, apiKey = this.apiKey} = {}) {
        // if (this.fetchedAll) {
        //     return keyBy(await this.db.toArray(), this.idName);
        // }
        const result = await this.fetchAllIdBeginningWith(`${this.type}#`, apiKey, true);
        this.fetchedAll = true;
        this.loaded = true;
        // this.lastUpdated = Date.now();
        // const result = await this.db.toArray();
        if (asArray) return result;
        return keyBy(result, this.idName);
    }

    async fetchBetween({startDate = dayjs().subtract(7, 'd'), endDate = dayjs(), asArray = false, apiKey = this.apiKey} = {}) {
        const result = await this.fetchAllIdBetween(this.getIdWithDate(startDate.format('YYYY-MM-DD')), this.getIdWithDate(endDate.add(1, 'day').format('YYYY-MM-DD')), apiKey);
        this.loaded = true;
        if (asArray) return result;
        return keyBy(result, this.idName);
    }

    async fetchAfter({startDate = dayjs(), asArray = false, apiKey = this.apiKey} = {}) {
        const result = await this.fetchAllIdBeginningWith(this.getIdWithDate(startDate.format('YYYY-MM-DD')), apiKey, true);
        this.loaded = true;
        if (asArray) return result;
        return keyBy(result, this.idName);
    }

    getIdWithDate(date) {
        return `${this.type}#${this.apiKey}#${typeof date === 'string' ? date : date.toISOString()}`;
    }
}

class PublishTfNSWLogModelData extends LogModelData {
    constructor() {
        super({type: 'integration#publish#tfnsw'});
    }

    async getCurrent() {
        return await this.fetchCurrentIdBeginningWith(this.type);
    }
}

class TptReportsLogModelData extends LogModelData {
    constructor() {
        super({type: `integration#reports#tfnsw#tpt`});
    }

    async getCurrent() {
        return await this.fetchCurrentIdBeginningWith(this.type);
    }
}

class EmployeeModelData extends ModelData {
    constructor() {
        super({
            name: 'Employee',
            api: 'third_party',
            path: '/employees',
            idName: 'sk',
            ws: false
        });
    }

    getId(obj) {
        if (obj) {
            return obj.sk;
        }
    }

    setId(obj, id = ulid()) {
        obj.id = id;
    }

    create(obj) {
        const type = obj?.sk?.split('#')[3];
        switch (type) {
            case 'DETAILS':
                return new Employee(obj);
            case 'EMPLOYMENT':
                return new Employment(obj);
            case 'ENTITLEMENT':
                return new Entitlement(obj);
            case 'LEAVE':
                return new Leave(obj);
            case 'LICENSE':
                return new License(obj);
            case 'QUALIFICATIONS':
                return new Qualification(obj);
            case 'TRAINING':
                return new Training(obj);
            case 'SUPERANNUATION':
                return new Superannuation(obj);
            case 'TAX':
                return new Tax(obj);
            case 'BANKING':
                return new Banking(obj);
            default:
                return obj;
        }
    }

    async fetch(id) {
        return await this.fetch(`#EMPLOYEE#${id}`);
    }

    async _fetchAllLeaves() {
        const leaves = await this.getLeaves();
        const leaveObjects = leaves.map(leave => ({
            ...leave,
            sk: `#EMPLOYEE#${leave.employeeID}#LEAVE#${leave.leaveApplicationID}`
        }));
        return leaveObjects;
    }

    async getAll(opts = {summary: true}, keyPrefix = '#EMPLOYEE#') {
        const allData = await super.getAll(opts);
        const filteredData = {};

        for (const key in allData) {
            if (key.startsWith(keyPrefix)) {
                filteredData[key] = allData[key];
            }
        }
        return filteredData;
    }

    async getAllStaffGroups(opts = {summary: true}) {
        const allData = await super.getAll(opts);
        const filteredData = {};

        for (const key in allData) {
            if (key.startsWith('#STAFFGROUP#')) {
                filteredData[key] = allData[key];
            }
        }
        return filteredData;
    }


    // async _create(obj) {
    //     return this.create(obj)
    // }


    async saveToXero(obj) {
        const keysToRemove = [
            'id',
            'createdAt',
            'totalEmployees',
            'updatedAt',
            'userId',
            '_new',
            'sk',
            'updatedDateUTC',
            'bankAccounts',
            'payTemplate',
            'openingBalances',
            'taxDeclaration',
            'leaveBalances',
            'superMemberships',
        ];
        await API.put(
            'third_party',
            `/employees/external/${obj.employeeID}${
                this.apiKey ? `?_k=${this.apiKey}` : ''
            }`,
            {
                body: omit(obj, keysToRemove),
            }
        );
        this.save(obj);
    }

    async createToXero(obj) {
        const res = await API.post(
            'third_party',
            `/employees/external${
                this.apiKey ? `?_k=${this.apiKey}` : ''
            }`,
            {
                body: obj,
            }
        );
        await super.save(res.employees);
        return res;
    }

    async getLeaves() {
        return API.get('third_party',
            `/employees/leaves${this.apiKey ? `?_k=${this.apiKey}` : ''}`);
    }

    async createLeaves(employeeId, leaves) {
        return API.post('third_party',
            `/employees/${employeeId}/leaves${this.apiKey ? `?_k=${this.apiKey}` : ''}`,
            {
                body: leaves
            });
    }

    async approveLeave(employeeId, leaveId) {
        return API.put('third_party',
            `/employees/${employeeId}/leaves/${leaveId}/approve${this.apiKey ? `?_k=${this.apiKey}` : ''}`);
    }

    async rejectLeave(employeeId, leaveId) {
        return API.put('third_party',
            `/employees/${employeeId}/leaves/${leaveId}/reject${this.apiKey ? `?_k=${this.apiKey}` : ''}`);
    }

    async saveDriverAuthorityCheck(body) {
        return API.post('third_party',
            `/employees/driverAuthority/save${
                this.apiKey ? `?_k=${this.apiKey}` : ''
            }`, {
                body
            });
    }

    async getAllDARecords() {
        return API.get('third_party',
            `/employees/driverAuthority${
                this.apiKey ? `?_k=${this.apiKey}` : ''
            }`);
    }

    async getDriverDARecord(employeeId) {
        return API.get('third_party',
            `/employees/driverAuthority/${employeeId}${
                this.apiKey ? `?_k=${this.apiKey}` : ''
            }`);
    }

    async getLatestDriverAuthorityCheck() {
        const driverAuthRecords = await this.getAllDARecords();
        console.log('driverAuthRecords', driverAuthRecords);
        if (driverAuthRecords.length === 0) return [];
        const sortedRecords = driverAuthRecords.sort((a, b) => new Date(b.checkedDate) - new Date(a.checkedDate));
        const latestDate = sortedRecords[0].checkedDate;
        const latestRecords = sortedRecords.filter(record => record.checkedDate === latestDate);
        return latestRecords;
    }

    convertToDate(dateString) {
        if (!dateString) return null;
        const [day, month, year] = dateString.split('/');
        return new Date(year, month - 1, day);
    }

    async processDriverAuthorityCheck(data) {
        const threshold = await this.getDAConfig();
        const errors = [];
        const warnings = [];
        data.forEach((obj, index) => {
            const currentDate = new Date();
            const errorDAExpiry = new Date(currentDate.getTime() + threshold.driverErrorThreshold * 24 * 60 * 60 * 1000);
            const errorMedicalExpiry = new Date(currentDate.getTime() + threshold.medicalErrorThreshold * 24 * 60 * 60 * 1000);
            const warningDAExpiry = new Date(currentDate.getTime() + threshold.driverWarningThreshold * 24 * 60 * 60 * 1000);
            const warningMedicalExpiry = new Date(currentDate.getTime() + threshold.medicalWarningThreshold * 24 * 60 * 60 * 1000);
            let error = {...obj};
            let warning = {...obj};
            if (obj.status !== 'CURRENT') {
                error = {...error, error: 'A driver DA is not CURRENT'};
            } else if (!obj.expiryDate || this.convertToDate(obj.expiryDate) <= errorDAExpiry) {
                error = {
                    ...error,
                    error: `A driver DA has expired or is due to expire in ${threshold.driverErrorThreshold} days`
                };
            } else if (!obj.medicalDueDate || this.convertToDate(obj.medicalDueDate) <= errorMedicalExpiry) {
                error = {
                    ...error,
                    error: `A driver medical check is overdue or is due in ${threshold.medicalErrorThreshold} days`
                };
            } else if (obj.expiryDate && this.convertToDate(obj.expiryDate) <= warningDAExpiry) {
                warning = {
                    ...warning,
                    warning: `A driver authority is due to expire in less than ${threshold.driverWarningThreshold} days`
                };
            } else if (obj.medicalDueDate && this.convertToDate(obj.medicalDueDate) <= warningMedicalExpiry) {
                warning = {
                    ...warning,
                    warning: `A medical check is due in less than ${threshold.medicalWarningThreshold} days`
                };
            }
            if (error.error) {
                errors.push(error);
            }
            if (warning.warning) {
                warnings.push(warning);
            }
        });

        return {errors, warnings};
    }

    async getDAConfig() {
        const driverConfig = await this.get(this.apiKey, '#CONFIG#DRIVERAUTHORITY#');
        if (!driverConfig) return {
            driverErrorThreshold: 1,
            medicalErrorThreshold: 1,
            driverWarningThreshold: 30,
            medicalWarningThreshold: 30,
        };
        return driverConfig;
    }

    async updateLocalDArecord(employee) {
        const daRecords = await this.getDriverDARecord(employee.details.driverId);
        if (daRecords.length === 0) return;
        // replace name in every record and save it
        const records = daRecords.map(record => {
            record.name = `${employee.external.firstName} ${employee.external.lastName}`;
            return record;
        });
        this.save(records);
    }
}

class StudentModelData extends ModelData {
    constructor() {
        super({
            name: 'Student',
            path: '/students',
            api: 'student',
            idName: 'studentId'
        });
    }

    create(obj, all) {
        return new Student(obj);
    }

    serialise(obj) {
        obj = super.serialise(obj);
        // const student = new Student(cloneDeep(obj))
        // delete student.schoolStop
        // delete student.homeStop
        // delete student.expired
        // delete student.modified
        // delete student.key
        // delete student.warnings
        // student.guardians = student.guardians.map(s => s.toJson ? s.toJson() : s)
        // student.travelPasses = student.travelPasses.map(s => s.toJson ? s.toJson() : s)
        // student.misbehaviours = student.misbehaviours.map(s => s.toJson ? s.toJson() : s)
        return (new Student(obj)).toJson();
    }

    async deserialise(objs) {
        console.log('Deserialising Students...');
        const time = Date.now();
        objs = await super.deserialise(objs);
        const stopsById = await getAllPromise(stopModelData);
        if (!stopsById)
            throw new Error('Stops must be fetched before ' + this.name);

        const schoolStops = values(stopsById).filter(s => s.stopType === 'school');

        objs.forEach(student => {
            student.fromJson(stopsById, schoolStops);
            // if (student.schoolStopId) {
            //     student.schoolStop = stopsById[student.schoolStopId]
            //     student.schoolStop?.setLinkedStops(stopsById)
            // } else if (student.schoolName?.length) {
            //     const schoolStop = findSchoolStop(schoolStops, student.schoolName, student.schoolSuburb);
            //     student.schoolStop = schoolStop
            //     student.schoolStopId = schoolStop?.stopId
            //     student.schoolSuburb = student.schoolSuburb || schoolStop?.suburb;
            //     student.schoolStop?.setLinkedStops(stopsById)
            // }
            // student.travelPasses.forEach(tp => {
            //     tp.stop = stopsById[tp.stopId]
            //     tp.homeStop = stopsById[tp.stopId]
            //     tp.schoolStop = stopsById[tp.schoolStopId]
            //     tp.schoolStop?.setLinkedStops(stopsById)
            //     tp.guardian = student.guardians.find(g => g.guardianId === tp.guardianId)
            //     tp.student = student;
            // })
            // student.guardians.forEach(g => {
            //     if (g.stopId) {
            //         g.amStop = stopsById[g.amStopId];
            //         g.amStop?.setLinkedStops(stopsById)
            //         g.pmStop = stopsById[g.pmStopId];
            //         g.pmStop?.setLinkedStops(stopsById)
            //     }
            //     g.student = student;
            // });
        });
        console.log(`Students deserialised. Elapsed: ${Date.now() - time}ms`);
        return objs;
    }

    save(objs) {
        objs = Array.isArray(objs) ? objs : [objs];
        const allStudents = (this.data ? values(this.data) : []).filter(s => s.status !== 'archived');
        objs.filter(o => o.status !== 'archived').forEach(obj => {
            obj.warnings = obj.validateStudent(allStudents);
        });
        return super.save(objs);
    }

}

class SchoolModelData extends ModelData {
    constructor() {
        super({name: 'School', api: 'school', idName: 'schoolId'});
    }
}

class SMSModelData extends ModelData {
    constructor() {
        super({name: 'Sms', api: 'sms', idName: 'smsId', 
            ws: false});
    }

    async sendSMS(body) {
        let mock = false;
        if (process.env.NODE_ENV === 'development') {
            mock = true;
        }
        return await API.post(this.api, `/send?mock=${mock}${
            this.apiKey ? `&_k=${this.apiKey}` : ''
        }`, {
            body
        });
    }
}

class RefModelData extends ModelData {
    constructor({type, subIdName, name = 'Ref'}) {
        super({name, api: 'ref', idName: 'refId', ws: false});
        this.type = type;
        this.subIdName = subIdName;
    }

    getSubModelData(data) {
        let id = data.refId;
        if (Array.isArray(data) && data.length) {
            id = data[0].refId;
        }
        if (id?.startsWith('comment#route')) {
            return routeCommentModelData;
        } else if (id?.startsWith('comment#stop')) {
            return stopCommentModelData;
        } else if (id?.startsWith('note')) {
            return noteRefModelData;
        }
        return this;
    }

    async getAll(opts = {summary: false}) {
        // Get all refs and filter by type
        const allRefs = await super.getAll(opts);
        const filteredRefs = {};
        
        Object.values(allRefs).forEach(ref => {
            if (ref.type === this.type) {
                filteredRefs[ref.refId] = ref;
            }
        });
        
        return filteredRefs;
    }

    async get(apiKey, id) {
        return (await super.fetchAllIdBeginningWith(id, apiKey))?.[0];
    }

    async fetchAll() {
        if (this.fetchedAll) {
            return keyBy(await this.db.toArray(), this.idName);
        }
        let fetched = await this.fetchAllIdBeginningWith(this.getBeginningId(), this.apiKey, true);
        await this.db.bulkPut(fetched);
        if (this.localCache) {
            this.data = this.data || {};
            fetched = await this.deserialise(fetched);
            const fetchedById = keyBy(
                fetched,
                this.idName
            );
            this.data = {...this.data, ...fetchedById};
        }
        this.fetchedAll = true;
        this.loaded = true;
        // this.lastUpdated = Date.now();
        return keyBy(await this.db.toArray(), this.idName);
    }

    getBeginningId() {
        return `${this.type}#`;
    }

    setId(obj) {
        // Validate that the required field exists and is not undefined
        const subId = obj[this.subIdName];
        if (!subId || subId === 'undefined') {
            throw new Error(`Missing or invalid required field '${this.subIdName}' for ${this.type} reference. Value: ${subId}`);
        }
        obj.refId = `${this.type}#${subId}`;
        
        // Additional validation to ensure refId doesn't contain undefined
        if (obj.refId.includes('undefined')) {
            throw new Error(`Invalid refId generated: ${obj.refId}. Check that ${this.subIdName} field is properly set.`);
        }
    }
    
    async save(obj) {
        // ThreadModelData now uses parentId directly as subIdName, no fallback needed
        // For RouteCommentModelData, use parentId as fallback for route field
        if (this.type === 'comment' && this.subIdName === 'parentId' && !obj[this.subIdName] && obj.parentId) {
            // Encode parentId to ensure it's safe for database keys
            obj[this.subIdName] = encodeURIComponent(obj.parentId);
        }
        
        // Validate required fields before saving
        if (!obj[this.subIdName]) {
            throw new Error(`Missing required field '${this.subIdName}' for ${this.type} reference`);
        }
        
        // Set the ID only if it doesn't exist (for new records)
        if (!obj.refId) {
            this.setId(obj);
        }
        
        // Final validation before saving
        if (obj.refId.includes('undefined') || !obj.refId) {
            throw new Error(`Invalid refId generated: ${obj.refId}. Cannot save reference data.`);
        }
        
        return super.save(obj);
    }

    async _delete(ids) {
        // For ref data, we need to delete from the ref API
        if (!Array.isArray(ids)) {
            ids = [ids];
        }
        
        // Use the ref API to delete the items
        const apiKey = this.apiKey || '';
        return API.del('ref', `/ref/${ids.join(',')}?_k=${apiKey}`);
    }
}

export class NoteRefModelData extends RefModelData {
    constructor() {
        super({type: 'note', subIdName: 'title'});
    }

    serialise(obj) {
        const {refId, title, description, priority} = obj;
        return {refId, title, description, priority};
    }

    async deserialise(objs) {
        objs = await super.deserialise(objs);
        return objs.map((obj) => {
            const {refId, title, description, priority} = obj;
            return {refId, title, description, priority};
        });
    }
}


class CommentModelData extends RefModelData {
    constructor(data) {
        super(data);
    }

    getBeginningId() {
        return `${this.type}#${this.subIdName}#`;
    }

    async save(objs) {
        if (!Array.isArray(objs)) {
            objs = [objs];
        }
        objs.forEach(obj => {
            if (!obj.refId) {
                this.setId(obj);
            }
        });
        objs = await this.serialise(objs);
        objs = await API.post('third_party', `/comments?_k=${this.apiKey}`, {body: {comments: objs}});
        await this.db.bulkPut(objs);

        objs = await this.deserialise(objs);
        if (this.localCache) {
            this.data = this.data || {};
            objs.forEach((obj) => {
                this.data[this.getId(obj)] = this.create({...this.data[this.getId(obj)], ...obj});
            });
        }
        this.notifyUpdate(objs, true);
        return objs?.length === 1 ? objs[0] : objs;
    }

    serialise(objs) {
        objs = Array.isArray(objs) ? objs : [objs];
        return objs.map(obj => {
            obj.routeId = obj.route?.routeId;
            obj.stopId = obj.stop?.stopId || obj.stopTime?.stopId;
            obj.stopTimeId = obj.stopTime?.stopTimeId;
            delete obj.route;
            delete obj.stop;
            delete obj.stopTime;
            return obj;
        });
    }

    async deserialise(objs) {
        objs = await super.deserialise(objs);
        const stopsById = await getAllPromise(stopModelData);
        if (!stopsById)
            throw new Error('Stops must be fetched before ' + this.name);

        const routesById = await getAllPromise(routeModelData);
        if (!routesById)
            throw new Error('Routes must be fetched before ' + this.name);

        objs.forEach(comment => {
            if (comment.routeId) {
                comment.route = routesById[comment.routeId];

                if (comment.stopTimeId && comment.tripId && comment.route) {
                    const trip = find(comment.route.services, {tripId: comment.tripId});
                    if (trip) {
                        comment.stopTime = find(trip.stopTimes, {stopTimeId: comment.stopTimeId});
                    }
                }
            }
            if (comment.stopId) {
                comment.stop = stopsById[comment.stopId];
            }
        });
        return objs;
    }
}

export class RouteCommentModelData extends RefModelData {
    constructor() {
        super({type: 'comment', subIdName: 'parentId', name: 'RouteComment'});
    }

    setId(obj) {
        // Set explicit type for comments
        obj.type = 'comment';
        
        // Use parentId for unified approach, fallback to id for backward compatibility
        const parentId = obj.parentId || obj.id || obj.routeId || obj.route?.routeId;
        if (!parentId) {
            throw new Error('Missing parentId, id, or routeId for route comment');
        }
        
        // Determine subType based on parentType or existing subType
        let subType = obj.subType || 'otr_trip';
        if (obj.parentType === 'otr_trip') {
            subType = 'otr_trip';
        } else if (obj.parentType === 'tracked_trip') {
            subType = 'tracked_trip';
        }
        obj.subType = subType;
        
        // Encode parentId to ensure it's safe for database keys
        const encodedParentId = encodeURIComponent(parentId);
        obj.refId = `comment#${subType}#${encodedParentId}#${Date.now()}`;
        
        // Ensure parentId and parentType are set for unified approach
        if (!obj.parentId) {
            obj.parentId = parentId;
        }
        if (!obj.parentType) {
            if (obj.subType === 'otr_trip') {
                obj.parentType = 'otr_trip';
            } else if (obj.subType === 'tracked_trip') {
                obj.parentType = 'tracked_trip';
            } else {
                obj.parentType = 'route';
            }
        }
    }
}

export class BugReportModelData extends CommentModelData {
    constructor() {
        super({type: 'support_ticket', subIdName: 'id', name: 'BugReport'});
    }

    setId(obj) {
        obj.refId = `support_ticket#${obj.id}`;
    }

    getBeginningId() {
        return `${this.type}#`;
    }

    // Override to enable WebSocket updates like other comment models
    async save(obj) {
        const result = await super.save(obj);
        // Trigger listeners to update UI immediately
        this.notifyUpdate(result, true);
        return result;
    }

    async fetchByCompanyId(companyId) {
        // Use the same pattern as other RefModelData - fetch all bug reports and filter by companyId
        try {
            const beginningId = this.getBeginningId();
            console.log('🔍 DEBUG fetchByCompanyId: beginningId =', beginningId);
            console.log('🔍 DEBUG fetchByCompanyId: this.type =', this.type);
            console.log('🔍 DEBUG fetchByCompanyId: companyId =', companyId);
            
            const allBugReports = await this.fetchAllIdBeginningWith(beginningId, this.apiKey, true);
            console.log('🔍 DEBUG fetchByCompanyId: allBugReports count =', allBugReports.length);
            console.log('🔍 DEBUG fetchByCompanyId: allBugReports data =', allBugReports);
            
            // Filter by companyId
            const companyReports = allBugReports.filter(report => 
                report.companyId === companyId &&
                !report.archived
            );
            
            console.log('🔍 DEBUG fetchByCompanyId: filtered count =', companyReports.length);
            console.log('🔍 DEBUG fetchByCompanyId: filtered data =', companyReports);
            return companyReports;
        } catch (error) {
            console.error('Error fetching bug reports by company ID:', error);
            return [];
        }
    }
}


class StopCommentModelData extends CommentModelData {
    constructor() {
        super({type: 'comment', subIdName: 'stop', name: 'StopComment'});
    }

    setId(obj) {
        // Set explicit type for comments
        obj.type = 'comment';
        obj.refId = `comment#stop#${obj.stop.stopId}#${Date.now()}`;
    }
}

export class ThreadModelData extends RefModelData {
    constructor() {
        super({type: 'thread', subIdName: 'parentId', name: 'Thread'});
    }

    setId(obj) {
        // Only generate new refId if one doesn't exist (for new records)
        if (obj.refId) {
            return; // Don't regenerate refId for existing records
        }
        
        // Use parentId for unified approach, fallback to legacy fields for backward compatibility
        const parentId = obj.parentId || obj.bugReportId || obj.routeId;
        if (!parentId) {
            throw new Error('Missing parentId, bugReportId, or routeId for thread');
        }
        
        // Set explicit type for threads
        obj.type = 'thread';
        
        // Determine subType based on parentType or subType
        let subType = obj.subType || 'support_ticket';
        if (obj.parentType === 'otr_trip' || obj.subType === 'otr_trip') {
            subType = 'otr_trip';
        } else if (obj.parentType === 'tracked_trip' || obj.subType === 'tracked_trip') {
            subType = 'tracked_trip';
        } else if (obj.parentType === 'bug_report' || obj.bugReportId) {
            subType = 'support_ticket';
        }
        obj.subType = subType;
        
        // Generate unique threadId if not provided
        if (!obj.threadId) {
            obj.threadId = `thread_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }
        
        // Use threadId as the refId (consistent with updateThreadStatus method)
        obj.refId = `thread#${obj.threadId}`;
        
        // Ensure parentId and parentType are set for unified approach
        if (!obj.parentId) {
            obj.parentId = parentId;
        }
        if (!obj.parentType) {
            if (obj.bugReportId) {
                obj.parentType = 'bug_report';
            } else if (obj.subType === 'otr_trip') {
                obj.parentType = 'otr_trip';
            } else if (obj.subType === 'tracked_trip') {
                obj.parentType = 'tracked_trip';
            } else {
                obj.parentType = 'support_ticket';
            }
        }
        
        // Generate unique threadId if not provided
        if (!obj.threadId) {
            obj.threadId = `thread_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }
        
        // Set id field to match parentId for consistency with comments
        obj.id = obj.parentId;
    }

    async createThread(threadData) {
        const thread = {
            ...threadData,
            status: 'open',
            createdAt: Date.now(),
            updatedAt: Date.now()
        };
        
        // parent field will be set automatically by RefModelData.save() using parentId as fallback
        
        this.setId(thread);
        return await this.save(thread);
    }

    async delete(threadId) {
        return await this._delete(threadId);
    }

    async archiveThread(threadId) {
        const thread = await this.get(this.apiKey, `thread#${threadId}`);
        if (!thread) {
            throw new Error(`Thread ${threadId} not found`);
        }
        thread.archived = true;
        return await this.save(thread);
    }

    async updateThreadStatus(threadId, status, resolutionCode = null, resolvedBy = null) {
        const thread = await this.get(this.apiKey, `thread#${threadId}`);
        if (!thread) {
            throw new Error(`Thread ${threadId} not found`);
        }
        
        thread.status = status;
        thread.updatedAt = Date.now();
        
        if (status === 'resolved') {
            thread.resolutionCode = resolutionCode;
            thread.resolvedAt = Date.now();
            thread.resolvedBy = resolvedBy;
        } else {
            thread.resolutionCode = null;
            thread.resolvedAt = null;
            thread.resolvedBy = null;
        }
        
        return await this.save(thread);
    }

    async getThreadsForParent(parentId, parentType) {
        const allThreads = await this.fetchAll();
        return Object.values(allThreads).filter(thread => 
            thread.parentId === parentId && 
            thread.parentType === parentType &&
            !thread.archived
        );
    }
}

class ConfigModelData extends RefModelData {
    constructor({subIdName, name}) {
        super({type: 'config', subIdName, name});
    }

    getBeginningId() {
        return `${this.type}#${this.subIdName}#`;
    }


    async get(apiKey, id) {
        return await super.get(apiKey, `config#${this.subIdName}`);
    }

    setId(obj) {
        obj.refId = `config#${this.subIdName}#${Date.now()}`;
    }
}

class StudentConfigModelData extends ConfigModelData {
    constructor() {
        super({subIdName: 'student', name: 'StudentConfig'});
    }
}

class BookingModelData extends ModelData {
    constructor() {
        super({name: 'Booking', api: 'booking', idName: 'bookingId'});
        const optibusIntegration = null;
    }

    create(obj) {
        return new Booking(obj);
    }

    // async _create(objs) {
    //     const bookings = await super._create(objs);
    //     bookings = Array.isArray(bookings) ? bookings : [bookings];
    //     bookings = await this.deserialise(bookings);

    //     // Create integration instance
    //     const integration = await this.getOptibusIntegration();
    //     await Promise.all(bookings.map(booking => integration.createBooking(booking)));

    //     return objs;
    // }

    // async _delete(objs) {
    //     let bookings = await super._delete(objs);
    //     bookings = Array.isArray(bookings) ? bookings : [bookings];
    //     const integration = await this.getOptibusIntegration();
    //     await Promise.all(bookings.map(booking => integration.deleteBooking(booking.bookingId)));
    //     return bookings;
    // }

}

class CharterModelData extends ModelData {
    constructor() {
        super({name: 'Charter', api: 'charter', idName: 'charterId'});
    }

    create(obj) {
        if (!obj) {
            return null;
        }
        switch (obj.type) {
            case 'charter#itinerary':
                return new ItineraryShift(obj);
            case 'charter#route':
                return new CharterRouteRun(obj);
            case 'express':
                return obj;
            default:
                return new Charter(obj);
        }
    }

    // async _create(obj) {
    //     let charters = await super._create(obj);
    //     charters = Array.isArray(charters) ? charters : [charters];
    //     charters = await this.deserialise(charters);

    //     // Create integration instance
    //     const integration = await this.getOptibusIntegration();
    //     await Promise.all(charters.map(async charter => await integration.updateCharter(charter)));

    //     return charters;
    // }

    // async _delete(ids) {
    //     ids = Array.isArray(ids) ? ids : [ids];
    //     const charters = ids.map(id => this.get(id));
    //     await super._delete(ids);
    //     const integration = await this.getOptibusIntegration();
    //     await Promise.all(charters.map(charter => integration.deleteCharter(charter.charterId)));
    // }

    async fetch({startStopId, endStopId}) {
        const charters = await getAllPromise(this) ?? {};
        const routes = values(charters).filter(c => c?.type === 'charter#route' && c?.routeId?.startsWith(`${startStopId}#${endStopId}#`)).map(r => new CharterRouteRun(r));
        return routes;
    }

    async getTemplate(templateId) {
        const charters = await getAllPromise(this) ?? {};
        const template = values(charters).find(c => c.charterId === templateId);
        return template;
    }

    async save(objs, opts = {indirect: false, generatePDFs: false}) {
        if (!Array.isArray(objs)) {
            objs = [objs];
        }

        // Get operator and vehicle types for quote generation
        const operator = await operatorModelData.get(this.apiKey);
        const allVehicleTypes = await getAllPromise(vehicleTypeModelData);
        const allSchedules = await getAllPromise(scheduleModelData);
        const _objs = [];
        // Auto-generate quotes for charters with itinerary legs
        for (const obj of objs) {
            if (obj.itinerary?.length > 0) {
                const charter = new Charter(obj);
                    // Generate quote
                    await charter.generateQuote({
                        operator,
                        allVehicleTypes,
                        allSchedules
                    });
                _objs.push(charter);
            } else {
                _objs.push(obj);
            }
        }

        // Call the parent class's save method
        const savedCharters = await super.save(_objs, opts);
        
        if(opts.generatePDFs){
            // Generate and store PDFs for charters that need them
            if (Array.isArray(savedCharters)) {
                for (const charter of savedCharters) {
                    await generateAndStoreCharterPDFs(charter, operator);
                }
            } else {
                await generateAndStoreCharterPDFs(savedCharters, operator);
            }
        }

        return savedCharters;
    }


    async deserialise(objs) {
        console.log('Deserialising Charters...');
        const time = Date.now();
        objs = await super.deserialise(objs);
        // Run all data fetches in parallel
        const [
            stopsById,
            allRoutes,
            allVehicles,
            allVehicleTypes,
            allCustomers,
            allDeadruns,
            allShiftBats
        ] = await Promise.all([
            getAllPromise(stopModelData),
            getAllPromise(routeModelData),
            getAllPromise(vehicleModelData),
            getAllPromise(vehicleTypeModelData),
            getAllPromise(customerModelData),
            getAllPromise(deadrunModelData),
            getAllPromise(shiftBatModelData)
        ]);

        // Then validate all results
        if (!stopsById) throw new Error('Stops must be fetched before ' + this.name);
        if (!allRoutes) throw new Error('Routes must be fetched before ' + this.name);
        if (!allVehicles) throw new Error('Vehicles must be fetched before ' + this.name);
        if (!allVehicleTypes) throw new Error('Vehicle Types must be fetched before ' + this.name);
        if (!allCustomers) throw new Error('Customers must be fetched before ' + this.name);
        if (!allDeadruns) throw new Error('Deadruns must be fetched before ' + this.name);
        if (!allShiftBats) throw new Error('ShiftBats must be fetched before ' + this.name);

        await Promise.all(objs.map(async obj => {
            if (obj.itinerary) {
                obj.itinerary = obj.itinerary.map?.(route => {

                    if (typeof route === 'string') {
                        // If route is a string, it is the routeId
                        route = allRoutes[route];
                    }
                    route = new BusRoute(route);
                    route.setBaseStops(stopsById);
                    return route;
                });
            }
            obj.vehicle = allVehicles[obj.vehicleId];
            obj.customer = allCustomers[obj.customerId];
            if (obj.duty && !obj.duties?.length) {
                obj.duties = [obj.duty];
                delete obj.duty;
            }
            obj.duties = obj.duties?.filter(d => !!d);
            if (obj.duties?.length) {
                obj.duties = obj.duties.map(duty => {
                    if (typeof duty === 'string') {
                        // If duty is a string, it is the shiftBatId
                        duty = allShiftBats[duty];
                    }
                    if (duty?.vehicleTypeId) {
                        duty.vehicleType = allVehicleTypes[duty?.vehicleTypeId];
                    }
                    if(duty?.vehicleTypeIds) {
                        duty.vehicleTypes = duty.vehicleTypeIds.map(id => allVehicleTypes[id]);
                    }
                    return new ShiftBat(duty);
                });
                // await Promise.all(obj.duties.map(async duty => {
                //     await duty.updateRows({allDeadruns, allStops: stopsById, allRoutes})
                //      // Find any newly created deadruns
                //      const dirtyDeadrunRows = duty.rows.filter(r => r.type === ShiftBatRowType.dead && r.route?.routeId === '_')
                //      // Save those deadruns
                //      await Promise.all(dirtyDeadrunRows.map(async r => {
                //          const deadrun = await deadrunModelData.save(r.route);
                //          r.routeId = deadrun.routeId
                //      }));
                //      // Save the charter duty with the new deadrun routeId
                //      if(dirtyDeadrunRows.length) {
                //         duty = await charterModelData.save(duty);
                //      }
                // }));

                if (obj.duties && (!Number.isFinite(obj.duration) || !obj.duration)) {
                    obj.duration = Math.ceil(obj.duties.reduce((prev, next) => prev + next.getShiftTime(), 0) / 3600);
                }
                if (obj.duties && (!Number.isFinite(obj.distance) || !obj.distance)) {
                    obj.distance = Math.ceil(obj.duties.reduce((prev, next) => prev + next.getShiftDistance(), 0) / 1000);
                }
            }
        }));
        console.log(`Charters deserialised. Elapsed: ${Date.now() - time}ms`);
        return objs;
    }

    async confirmBooking(decoded) {
        // decoded should contain charterId, userId, etc.
        return API.post('third_party', '/confirm-booking', {body: decoded});
    }

    async testReminders(charterId) {
        // Test reminders for a charter - admin only
        return API.post('third_party', '/charters/test-reminders', {
            body: { charterId }
        });
    }

    async generateBookingReference(maxAttempts = 5) {
        let response, attempts = 0;
        do {
            response = await API.get('charter', `/booking/ref?_k=${this.apiKey}`);
            attempts++;
        } while (response.status === 'failed' && response.code === 'BOOKING_REFERENCE_GENERATION_FAILED' && attempts < maxAttempts);
        if (!response?.bookingReference) {
            throw new Error("Failed to generate booking reference. Please try again.");
        }
        return response?.bookingReference;
    }

}

class CustomerModelData extends ModelData {
    constructor() {
        super({name: 'Customer', api: 'customer', idName: 'customerId', ws: false});
    }

    create(obj) {
        switch (obj.type) {
            case 'charter#itinerary':
                return new Itinerary(obj);
            default:
                return new Customer(obj);
        }
    }

}

class TrackingModelData extends ModelData {
    constructor() {
        super({
            name: 'Tracking',
            api: 'tracking',
            idName: 'trackingId',
            baseKeyExpression: `begins_with(trackingId, :date)`,
            baseKeyExpressionValue: `date|${dayjs().format('YYYYMMDD')}|s`,
            ws: false
        });
    }
}

class JobModelData extends ModelData {
    constructor() {
        super({
            name: 'Job',
            api: 'job',
            idName: 'jobId',
            baseKeyExpression: `jobId > :date`,
            baseKeyExpressionValue: `date|${dayjs().subtract(14, 'days').format('YYYYMMDD')}|s`
        });
    }

    create(obj) {
        return new Job(obj);
    }

    setId(obj) {
        obj.jobId = `${obj.date.format('YYYYMMDD')}#${obj.type || 'shiftbat'}#${obj.typeId}#${obj.allocationType || 'employee'}#${obj.allocationId}#${Date.now()}`;
        obj.recurrenceId = `${obj.recurrence || 'none'}#${obj.type || 'shiftbat'}#${obj.typeId}#${obj.allocationType || 'employee'}#${obj.allocationId}#${Date.now()}`;
    }

    serialise(obj) {
        if (Array.isArray(obj)) {
            return obj.map(o => (new Job(o)).toJson());
        }
        return (new Job(obj)).toJson();
    }

    async fetchForDate(date) {
        const jobs = await API.get('job', `/jobs?_k=${this.apiKey}&queryDate=${date.format(DATE_STRING)}`);
        return await this.deserialise(jobs);
    }

    // async fetchAll() {
    //     this.expired = false;
    //     const time = Date.now();
    //     // Fetch todays worth of jobs from 7 days ago
    //     // let date = dayjs().subtract(7, 'd');
    //     // let promises = []
    //     // Array.from(Array(28).keys()).map(async () => {
    //     //     promises.push(this.fetchForDate(date));
    //     //     date = date.add(1, 'day');
    //     // });
    //     let promises = [await this.fetchForDate(dayjs())];
    //     let jobs = uniqBy(flatten(await Promise.all(promises)), 'jobId');
    //     // let jobs = await API.get('job', `/jobs?_k=${this.apiKey}`);
    //
    //     await this.db.bulkPut(jobs);
    //     if (this.localCache) {
    //         this.data = this.data || {};
    //         jobs = await this.deserialise(jobs);
    //         const jobsById = keyBy(
    //             jobs,
    //             this.idName
    //         );
    //         this.data = {...this.data, ...jobsById};
    //     }
    //     this.fetchedAll = true;
    //     this.loaded = true;
    //
    //     console.log('Time taken to fetch all jobs', Date.now() - time, 'ms');
    // }

    async deserialise(objs) {
        if (!objs?.length) {
            return objs;
        }
        objs = await super.deserialise(objs);
        const shiftBatsById = await getAllPromise(shiftBatModelData);
        if (!shiftBatsById)
            throw new Error('ShiftBats must be fetched before ' + this.name);

        const chartersById = await getAllPromise(charterModelData);
        if (!chartersById)
            throw new Error('Charters must be fetched before ' + this.name);

        const employeesById = await getAllPromise(employeeModelData);
        if (!employeesById)
            throw new Error('Employees must be fetched before ' + this.name);

        const vehiclesById = await getAllPromise(vehicleModelData);
        if (!vehiclesById)
            throw new Error('Vehicles must be fetched before ' + this.name);

        objs.forEach(job => {
            job.fromJson(shiftBatsById, chartersById, employeesById, vehiclesById);
        });
        return objs;
    }

    async fetchJobsForDate(date) {
        return await API.get('job', `/jobs?_k=${this.apiKey}&queryDate=${date.format(DATE_STRING)}`);
    }
}

class RosterModelData extends ModelData {
    constructor() {
        super({
            name: 'Roster',
            api: 'roster',
            idName: 'rosterId',
            baseKeyExpression: `rosterId > :date`,
            baseKeyExpressionValue: `date|${dayjs().startOf('week').subtract(28, 'days').format('YYYYMMDD')}|s`
        });
    }

    setId(obj, id = ulid()) {
        obj.rosterId = obj.date.format('YYYYMMDD');
    }

    getId(obj) {
        return obj.rosterId || obj.date.unix();
    }

    create(obj) {
        obj.scenarios = obj.scenarios?.map(s => WeeklyScenario.from(s));
        return obj;
    }

    serialise(obj) {
        return {
            ...obj,
            date: obj.date.format?.('YYYYMMDD') || obj.date,
            scenarios: obj.scenarios?.map(s => s.toJson?.() || s)
        };
    }

    async deserialise(objs) {
        console.log('Deserialising Rosters...');
        const time = Date.now();
        objs = await super.deserialise(objs);
        const vehicles = await getAllPromise(vehicleModelData);
        if (!vehicles)
            throw new Error('Vehicle must be fetched before ' + this.name);

        const employees = await getAllPromise(employeeModelData);
        if (!employees)
            throw new Error('Employees must be fetched before ' + this.name);

        const shiftBats = await getAllPromise(shiftBatModelData);
        if (!shiftBats)
            throw new Error('ShiftBats must be fetched before ' + this.name);
        objs.forEach(roster => {
            roster.date = dayjs(roster.date, 'YYYYMMDD').startOf('day');
            roster.scenarios = roster.scenarios?.map(s => WeeklyScenario.from(s).fromJson(shiftBats, employees, vehicles));
        });
        console.log(`Rosters deserialised. Elapsed: ${Date.now() - time}ms`);
        return objs;
    }

}

class ReportsModelData extends ModelData {
    constructor() {
        super({
            name: 'Report',
            api: 'report',
            idName: 'reportId',
            path: '/reports'
        });
    }

    create(obj) {
        return new Report(obj);
    }

    syncOperator(operatorId) {
        return API.post('report', `/reports/sync/${operatorId}`);
    }
}

class BlocksModelData extends ModelData {
    constructor() {
        super({
            name: 'Block',
            api: 'block',
            idName: 'blockId',
            path: '/blocks'
        });
    }
}

const operatorModelData = new OperatorModelData();
routesModelExpiryService.addData(operatorModelData);

const stopModelData = new StopModelData();
routesModelExpiryService.addData(stopModelData);

// Update the restricted points when the stops are updated
stopModelData.addListener({
    id: 'appStopModelData', loaded: stops => {
        restrictedPoints.setPoints(values(stops)?.filter(s => s.stopType === 'restricted') || []);
    }, notifyUpdate: stop => {
        stop.stopType === 'restricted' && restrictedPoints.addPoints(stop);
    }, notifyDelete: stop => {
        restrictedPoints.removePoint(stop);
    }
});

const routeModelData = new RouteModelData({
    includeStops: false,
    includePath: false,
    publishedOnly: false,
    loadSchedules: false,
});
routesModelExpiryService.addData(routeModelData);

// const routeSummaryModelData = new RouteSummaryModelData();
// routesModelExpiryService.addData(routeSummaryModelData);

const publicScheduleModelData = new PublicScheduleModelData();
routesModelExpiryService.addData(publicScheduleModelData);

const scheduleModelData = new ScheduleModelData();
routesModelExpiryService.addData(scheduleModelData);

const vehicleModelData = new VehicleModelData();
routesModelExpiryService.addData(vehicleModelData);

const vehicleTypeModelData = new VehicleTypeModelData();
routesModelExpiryService.addData(vehicleTypeModelData);

const driverModelData = new DriverModelData();
routesModelExpiryService.addData(driverModelData);

const driverShiftModelData = new DriverShiftModelData();
routesModelExpiryService.addData(driverShiftModelData);

const vehicleShiftModelData = new VehicleShiftModelData();
routesModelExpiryService.addData(vehicleShiftModelData);

const transferModelData = new TransferModelData();
routesModelExpiryService.addData(transferModelData);

const deadrunModelData = new DeadrunModelData();
routesModelExpiryService.addData(deadrunModelData);

const shiftBatModelData = new ShiftBatModelData();
routesModelExpiryService.addData(shiftBatModelData);

const publishTfNSWModelData = new PublishTfNSWLogModelData();
routesModelExpiryService.addData(publishTfNSWModelData);

const tptReportsLogModelData = new TptReportsLogModelData();
routesModelExpiryService.addData(tptReportsLogModelData);

const employeeModelData = new EmployeeModelData();
routesModelExpiryService.addData(employeeModelData);

const studentModelData = new StudentModelData();
routesModelExpiryService.addData(studentModelData);

const schoolModelData = new SchoolModelData();
routesModelExpiryService.addData(schoolModelData);

const smsModelData = new SMSModelData();
routesModelExpiryService.addData(smsModelData);

const noteRefModelData = new NoteRefModelData();
routesModelExpiryService.addData(noteRefModelData);

const routeCommentModelData = new RouteCommentModelData();
routesModelExpiryService.addData(routeCommentModelData);

const stopCommentModelData = new StopCommentModelData();
routesModelExpiryService.addData(stopCommentModelData);

const bugReportModelData = new BugReportModelData();
routesModelExpiryService.addData(bugReportModelData);


const threadModelData = new ThreadModelData();
routesModelExpiryService.addData(threadModelData);

const studentConfigModelData = new StudentConfigModelData();
routesModelExpiryService.addData(studentConfigModelData);

const bookingModelData = new BookingModelData();
routesModelExpiryService.addData(bookingModelData);

const charterModelData = new CharterModelData();
routesModelExpiryService.addData(charterModelData);

const customerModelData = new CustomerModelData();
routesModelExpiryService.addData(customerModelData);

const jobModelData = new JobModelData();
routesModelExpiryService.addData(jobModelData);

const rosterModelData = new RosterModelData();
routesModelExpiryService.addData(rosterModelData);

const trackingModelData = new TrackingModelData();

const reportsModelData = new ReportsModelData();
routesModelExpiryService.addData(reportsModelData);

const blocksModelData = new BlocksModelData();
routesModelExpiryService.addData(blocksModelData);

export {
    routesModelExpiryService,
    stopModelData,
    routeModelData,
    publicScheduleModelData,
    scheduleModelData,
    vehicleModelData,
    vehicleTypeModelData,
    driverModelData,
    driverShiftModelData,
    transferModelData,
    shiftBatModelData,
    deadrunModelData,
    publishTfNSWModelData,
    operatorModelData,
    vehicleShiftModelData,
    employeeModelData,
    studentModelData,
    schoolModelData,
    smsModelData,
    stopCommentModelData,
    routeCommentModelData,
    bugReportModelData,
    threadModelData,
    noteRefModelData,
    studentConfigModelData,
    bookingModelData,
    charterModelData,
    customerModelData,
    jobModelData,
    rosterModelData,
    trackingModelData,
    reportsModelData,
    blocksModelData,
    tptReportsLogModelData
};
